import React, { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import ReactDOM from 'react-dom';
import AntLocalization from '../../utils/AntLocalization';
import AntAPI from '../../utils/AntAPI';
import AntBadgeStore from '../../utils/AntBadgeStore';
import { AntSidebar, AntSession } from '../../utils/AntContext';
import { useNavigate } from 'react-router-dom';
import AntFileViewer from '../AntFileViewer';
import OFDocumentPreview from '../OFDocumentPreview';
import FloatingCommentPanel from '../FloatingCommentPanel';
import FloatingWorkPanel from '../FloatingWorkPanel';
import AIJobsPanel from './AIJobsPanel';
import MEDExplorerPanel from './MEDExplorerPanel';
import AntCondition from '../../utils/AntCondition';
import ChatPanel from './panels/ChatPanel';
import AIFloating from './AIFloating';
import AntAIAssistant from '../AntAIAssistant';
import { getInitial, renderMarkdown, MessageBlock } from './shared';
import { convertEmojiShortcuts } from './ChatShared';
import HtmlEditor, { Toolbar, Item } from 'devextreme-react/html-editor';
import GlobalApprovalPanel from './panels/ApprovalPanel';
import { SidebarState, sidebarRelativeTime, sidebarDate, sidebarDayMonth, sidebarTime } from './panels/SidebarStandard';

import './index.scss';
import './panels/SidebarStandard.scss';
import './panels/SidebarUnify.scss';

// ─── Tooltip button (position: fixed, thoát overflow: hidden) ───
var tooltipStyle = {
    position: 'fixed',
    padding: '4px 10px',
    background: '#1a3561',
    color: '#fff',
    fontSize: '11px',
    whiteSpace: 'nowrap',
    borderRadius: '4px',
    zIndex: 99999,
    pointerEvents: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
};

function TooltipBtn(props) {
    var [tip, setTip] = useState(null);
    var btnRef = useRef(null);
    var onEnter = useCallback(
        function () {
            if (!props.tooltip || !btnRef.current) return;
            var r = btnRef.current.getBoundingClientRect();
            var left = props.side === 'left' ? r.left - 6 : r.right + 6;
            var transform = props.side === 'left' ? 'translateX(-100%) translateY(-50%)' : 'translateY(-50%)';
            setTip({ left: left, top: r.top + r.height / 2, transform: transform });
        },
        [props.tooltip, props.side]
    );
    var onLeave = useCallback(function () {
        setTip(null);
    }, []);
    return (
        <button ref={btnRef} className={props.className} onClick={props.onClick} onMouseEnter={onEnter} onMouseLeave={onLeave}>
            {props.children}
            {tip &&
                ReactDOM.createPortal(
                    <div style={Object.assign({}, tooltipStyle, { left: tip.left, top: tip.top, transform: tip.transform })}>{props.tooltip}</div>,
                    document.body
                )}
        </button>
    );
}

// ─── Confirm Dialog (thay thế browser confirm) ─────────────────
function ConfirmDialog(props) {
    if (!props.visible) return null;
    return ReactDOM.createPortal(
        <div
            className="sidebar-confirm-overlay"
            onMouseDown={function (e) {
                if (e.target === e.currentTarget) props.onCancel();
            }}
        >
            <div
                className="sidebar-confirm-box"
                onMouseDown={function (e) {
                    e.stopPropagation();
                }}
            >
                <div className="sidebar-confirm-msg">{props.message}</div>
                <div className="sidebar-confirm-actions">
                    <button
                        className="sidebar-confirm-ok"
                        onClick={function (e) {
                            e.stopPropagation();
                            props.onConfirm();
                        }}
                    >
                        {AntLocalization.translateText(props.okText || { vi: 'Xác nhận', en: 'Confirm', fr: 'Confirmer', es: 'Confirmar', id: 'Konfirmasi', ja: '確認', zh: '确认', ko: '확인', hi: 'पुष्टि करें', ar: 'تأكيد' })}
                    </button>
                    <button
                        className="sidebar-confirm-cancel"
                        onClick={function (e) {
                            e.stopPropagation();
                            props.onCancel();
                        }}
                    >
                        {AntLocalization.translateText({ vi: 'Huỷ', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', ja: 'キャンセル', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء' })}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

// Hook: dùng thay confirm() — trả về { show(msg, onOk), el }
function useConfirm() {
    var [state, setState] = useState({ visible: false, message: '', onOk: null });
    var show = useCallback(function (message, onOk) {
        setState({ visible: true, message: message, onOk: onOk });
    }, []);
    var handleConfirm = useCallback(
        function () {
            if (state.onOk) state.onOk();
            setState({ visible: false, message: '', onOk: null });
        },
        [state.onOk]
    );
    var handleCancel = useCallback(function () {
        setState({ visible: false, message: '', onOk: null });
    }, []);
    var el = <ConfirmDialog visible={state.visible} message={state.message} onConfirm={handleConfirm} onCancel={handleCancel} />;
    return { show: show, el: el };
}

// ─── Chat Toast ─────────────────────────────────────────────────
function ChatToastItem(props) {
    useEffect(
        function () {
            var t = setTimeout(function () {
                props.onDismiss(props.id);
            }, 4500);
            return function () {
                clearTimeout(t);
            };
        },
        [props.id]
    );
    var initial = props.userName ? props.userName.charAt(0).toUpperCase() : '?';
    var text = props.contentType === 'file' ? AntLocalization.translateText({ vi: 'Tệp đính kèm', en: 'Attachment', fr: 'Pièce jointe', es: 'Adjunto', id: 'Lampiran', ja: '添付ファイル', zh: '附件', ko: '첨부 파일', hi: 'संलग्नक', ar: 'مرفق' }) : props.content || '...';
    var displayName = props.userName || '...';
    // Nếu là nhóm (group) hoặc voucher_thread và có tên nhóm → hiện tên nhóm phía trên
    var showChatName = props.chatName && props.chatType && props.chatType !== 'direct';
    return ReactDOM.createPortal(
        <div className="chat-toast-item" onClick={props.onChatClick}>
            <span className="chat-toast-avatar">{initial}</span>
            <div className="chat-toast-body">
                {showChatName && <div className="chat-toast-group">{props.chatName}</div>}
                <div className="chat-toast-name">{displayName}</div>
                <div className="chat-toast-text">{text}</div>
            </div>
            <button
                className="chat-toast-close"
                onClick={function (e) {
                    e.stopPropagation();
                    props.onDismiss(props.id);
                }}
            >
                ×
            </button>
        </div>,
        document.body
    );
}

function ChatToastContainer(props) {
    if (!props.toasts || props.toasts.length === 0) return null;
    return (
        <Fragment>
            {props.toasts.map(function (t) {
                return (
                    <ChatToastItem
                        key={t.id}
                        id={t.id}
                        userName={t.user_name}
                        chatName={t.chat_name}
                        chatType={t.chat_type}
                        content={t.content}
                        contentType={t.content_type}
                        onDismiss={props.onDismiss}
                        onChatClick={function () {
                            if (props.onChatClick) props.onChatClick(t.chat_id);
                        }}
                    />
                );
            })}
        </Fragment>
    );
}

// ─── Notify Toast (cho Comment/Notification) ───────────────────
function NotifyToastItem(props) {
    useEffect(
        function () {
            var t = setTimeout(function () {
                props.onDismiss(props.id);
            }, 5000);
            return function () {
                clearTimeout(t);
            };
        },
        [props.id]
    );
    var icon = <SvgIcon d={props.event === 'NewComment' ? GLYPH.comment : GLYPH.bell} size={16} />;
    var title = props.title ? AntLocalization.translateText(props.title) : AntLocalization.translateText({ vi: 'Thông báo mới', en: 'New notification', fr: 'Notification', es: 'Nueva notificación', id: 'Notifikasi baru', ja: '新しい通知', zh: '新通知', ko: '새 알림', hi: 'नई सूचना', ar: 'إشعار جديد' });
    var text = props.content ? AntLocalization.translateText(props.content) : '';
    return ReactDOM.createPortal(
        <div className="chat-toast-item notify-toast-item" onClick={props.onClick}>
            <span className="chat-toast-avatar notify-toast-icon">{icon}</span>
            <div className="chat-toast-body">
                <div className="chat-toast-name">{title}</div>
                {text && <div className="chat-toast-text">{text}</div>}
            </div>
            <button
                className="chat-toast-close"
                onClick={function (e) {
                    e.stopPropagation();
                    props.onDismiss(props.id);
                }}
            >
                ×
            </button>
        </div>,
        document.body
    );
}

function NotifyToastContainer(props) {
    if (!props.toasts || props.toasts.length === 0) return null;
    return (
        <Fragment>
            {props.toasts.map(function (t) {
                return (
                    <NotifyToastItem
                        key={t.id}
                        id={t.id}
                        event={t.event}
                        title={t.title}
                        content={t.content}
                        onDismiss={props.onDismiss}
                        onClick={function () {
                            if (props.onClick) props.onClick(t);
                        }}
                    />
                );
            })}
        </Fragment>
    );
}

// ─── Config ─────────────────────────────────────────────────────
var MOCK_MODE = false;

var STORAGE_KEY = 'ant_right_sidebar';
var MIN_WIDTH = 280;
var MAX_WIDTH = 520;
var DEFAULT_WIDTH = 280;
var COLLAPSED_WIDTH = 36;
var COMPACT_SCREEN_BREAKPOINT = 1279;
var WIDE_SCREEN_BREAKPOINT = 1600;

// ─── Panel definitions (GLOBAL - always visible) ────────────────
var GLOBAL_PANELS = [
    {
        key: 'notification',
        label: { vi: 'Thông báo', en: 'Notifications', fr: 'Notifications', es: 'Notificaciones', id: 'Notifikasi', ja: '通知', zh: '通知', ko: '알림', hi: 'सूचनाएँ', ar: 'الإشعارات' },
        iconPath:
            'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
    },
    {
        key: 'chat',
        label: { vi: 'Trao đổi', en: 'Chat', fr: 'Discussion', es: 'Chat', id: 'Obrolan', ja: 'チャット', zh: '聊天', ko: '채팅', hi: 'चैट', ar: 'الدردشة' },
        iconPath:
            'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
        visible: {
            whenUserScreen: 'Comments'
        }
    },
    {
        key: 'calendar',
        label: { vi: 'Lịch', en: 'Calendar', fr: 'Calendrier', es: 'Calendario', id: 'Kalender', ja: 'カレンダー', zh: '日历', ko: '캘린더', hi: 'कैलेंडर', ar: 'التقويم' },
        iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        // TEMP HIDDEN: tab Lịch tạm ẩn. Khi bật lại, đổi `false` về condition gốc:
        // visible: { when: { gr1: { when: { whenUserScreen: 'ARAppointments', whenSys: { use_Module_Task: 1 } }, whenType: 'OR' } } }
        visible: false
    },
    // {
    //     key: 'system',
    //     label: { vi: 'Hệ thống', en: 'System', fr: 'Système', es: 'Sistema', id: 'Sistem', ja: 'システム', zh: '系统', ko: '시스템', hi: 'सिस्टम', ar: 'النظام' },
    //     iconPath:
    //         'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
    // },
    {
        key: 'assistant',
        label: { vi: 'Trợ lý AI', en: 'AI Assistant', fr: 'Assistant IA', es: 'Asistente IA', id: 'Asisten AI', ja: 'AIアシスタント', zh: 'AI助手', ko: 'AI 어시스턴트', hi: 'AI सहायक', ar: 'مساعد AI' },
        iconPath: 'M13 10V3L4 14h7v7l9-11h-7z',
        // TEMP DISABLED: Trợ Lý AI tạm thời ẩn. Khi nào release, đổi `false` thành condition đúng.
        // Gate qua aiAvailable.ai_assistant (đã set false trong aiAvailable.js).
        visible: false
    },
    {
        key: 'documents',
        label: {
            vi: 'Công văn',
            en: 'Documents',
            fr: 'Documents',
            es: 'Documentos',
            id: 'Dokumen',
            ja: '社内文書',
            zh: '内部公文',
            ko: '내부 문서',
            hi: 'दस्तावेज़',
            ar: 'المستندات'
        },
        iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
        // Gate 2-layer (preview cho mọi user có module; manage gate ở maximize button bằng OFDocument_can_manage):
        //   Layer 1: sys_use_Service_ERP=1   — service core
        //   Layer 2: use_Module_OFDocument=1 — module flag admin tunable
        visible: {
            when: {
                whenSys: { sys_use_Service_ERP: 1, use_Module_OFDocument: 1 }
            }
        }
    },
    {
        key: 'file_explorer',
        label: {
            vi: 'Quản lý tệp tin',
            en: 'File Manager',
            fr: 'Fichiers',
            es: 'Gestor de archivos',
            id: 'Manajer File',
            ja: 'ファイル管理',
            zh: '文件管理',
            ko: '파일 관리',
            hi: 'फ़ाइल प्रबंधक',
            ar: 'إدارة الملفات'
        },
        iconPath: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z',
        // 3-layer visibility gate (theo feedback user 2026-06-11):
        //   Layer 1: sys_use_Service_ERP=1   — service core gate (auto từ BUOrder)
        //   Layer 2: use_Module_Attachment=1 — module flag, đủ để hiển thị mặc định
        //   Layer 3: whenUserScreen MEDExplorer — admin tắt per role nếu muốn
        visible: {
            when: {
                whenSys: { sys_use_Service_ERP: 1, use_Module_Attachment: 1 },
                whenUserScreen: 'MEDExplorer'
            }
        }
    },
    {
        key: 'task',
        label: { vi: 'Công việc', en: 'Tasks', fr: 'Tâches', es: 'Tareas', id: 'Tugas', ja: 'タスク', zh: '任务', ko: '작업', hi: 'कार्य', ar: 'المهام' },
        iconPath:
            'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
        visible: {
            when: {
                whenSys: { use_Module_Task: 1 }
            }
        }
    },
    {
        key: 'approval',
        label: { vi: 'Duyệt', en: 'Approval', fr: 'Approbation', es: 'Aprobación', id: 'Persetujuan', ja: '承認', zh: '审批', ko: '승인', hi: 'अनुमोदन', ar: 'الموافقة' },
        iconPath:
            'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
        visible: [
            { whenSys: { use_Module_Approval: 1 }, whenUser: { Approval_request_approval: 1 } },
            { whenSys: { use_Module_Approval: 1 }, whenUser: { Approval_can_approve: 1 } },
            { whenSys: { use_Module_Approval: 1 }, whenUser: { WFApprovalPending_can_approve: 1 } }
        ]
    },
    // Tạm ẩn AI Jobs khỏi right sidebar (bỏ comment để hiện lại)
    // {
    //     key: 'aijobs',
    //     label: { vi: 'AI Jobs', en: 'AI Jobs', fr: 'Tâches IA', es: 'Tareas IA', id: 'Tugas AI', ja: 'AIジョブ', zh: 'AI任务', ko: 'AI 작업', hi: 'AI कार्य', ar: 'مهام AI' },
    //     iconPath:
    //         'M13 10V3L4 14h7v7l9-11h-7z'
    // },
    {
        key: 'notes',
        label: { vi: 'Sổ tay', en: 'Notebooks', fr: 'Carnets', es: 'Cuadernos', id: 'Buku catatan', ja: 'ノート', zh: '笔记本', ko: '노트', hi: 'नोटबुक', ar: 'الدفاتر' },
        iconPath:
            'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
    },
    {
        key: 'reminder',
        label: { vi: 'Ghi nhớ', en: 'Stickies', fr: 'Pense-bêtes', es: 'Notas rápidas', id: 'Catatan tempel', ja: '付箋', zh: '便签', ko: '메모', hi: 'स्टिकी नोट', ar: 'ملاحظات لاصقة' },
        iconPath: 'M16 3H5a2 2 0 00-2 2v14a2 2 0 002 2h9l7-7V5a2 2 0 00-2-2h-3zM14 21v-5a2 2 0 012-2h5M7 8h8M7 12h5'
    }
    // {
    //     key: 'contacts',
    //     label: { vi: 'Liên hệ', en: 'Contacts', fr: 'Contacts', es: 'Contactos', id: 'Kontak', ja: '連絡先', zh: '联系人', ko: '연락처', hi: 'संपर्क', ar: 'جهات اتصال' },
    //     iconPath:
    //         'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z'
    // }
];

// ─── Saved state helpers ────────────────────────────────────────
function loadState() {
    try {
        var raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw);
    } catch (e) {
        /* silent */
    }
    return null;
}

function saveState(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
        /* silent */
    }
}

// ─── SVG Icon ───────────────────────────────────────────────────
function SvgIcon(props) {
    return (
        <svg
            width={props.size || 18}
            height={props.size || 18}
            viewBox="0 0 24 24"
            fill="none"
            stroke={props.color || 'currentColor'}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d={props.d} />
        </svg>
    );
}

var chevronRightPath = 'M9 5l7 7-7 7';
// Vector glyphs used where emoji used to stand in as icons.
var GLYPH = {
    clip: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
    bell: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
    comment: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
    pin: 'M16 3l5 5-3 1-4 4 1 5-2 2-4-4-5 5-1-1 5-5-4-4 2-2 5 1 4-4 1-3z',
    image: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
    doc: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    video: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
    archive: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4'
};
var chevronLeftPath = 'M15 19l-7-7 7-7';

// ═══════════════════════════════════════════════════════════════════
// ─── COMMENT PANEL ──────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var COMMENT_PAGE_SIZE = 10;

function commentFormatTime(ts) {
    return sidebarRelativeTime(ts);
}

// ─── getInitial imported from ./shared ──────────────────────────

function CommentPanel(props) {
    var context = props.context;
    var onSwitchPanel = props.onSwitchPanel;

    var commentAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserComment');
    }, []);

    var [allComments, setAllComments] = useState([]);
    var [inputText, setInputText] = useState('');
    var [loading, setLoading] = useState(false);
    var [sending, setSending] = useState(false);
    var [showAll, setShowAll] = useState(false);
    var listRef = useRef(null);
    var didScrollRef = useRef(false);

    // Sort: oldest first (ascending by created_at) for chat-like display
    var sortedComments = useMemo(
        function () {
            return allComments.slice().sort(function (a, b) {
                return (a.created_at || 0) - (b.created_at || 0);
            });
        },
        [allComments]
    );

    // Pagination: show last COMMENT_PAGE_SIZE items, or all if expanded
    var hasMore = sortedComments.length > COMMENT_PAGE_SIZE && !showAll;
    var visibleComments = hasMore ? sortedComments.slice(sortedComments.length - COMMENT_PAGE_SIZE) : sortedComments;

    // Load comments from API
    var loadComments = useCallback(
        function () {
            if (!context || !context.voucherType) return;
            setLoading(true);
            commentAPI.post(
                '/' + commentAPI.routeURL + '/list',
                {
                    voucher_type: context.voucherType,
                    voucher_id: context.voucherId,
                    voucher_code: context.voucherCode,
                    pageSize: 50,
                    pageIndex: 0
                },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || [];
                    setAllComments(rows);
                    setLoading(false);
                }
            );
        },
        [context]
    );

    useEffect(
        function () {
            loadComments();
            setShowAll(false);
        },
        [context ? context.voucherId : null]
    );

    // Auto-scroll to bottom on new comments
    useEffect(
        function () {
            if (listRef.current) {
                if (!didScrollRef.current || !showAll) {
                    listRef.current.scrollTop = listRef.current.scrollHeight;
                    didScrollRef.current = true;
                }
            }
        },
        [visibleComments.length]
    );

    // Send comment
    var handleSend = useCallback(
        function () {
            var text = convertEmojiShortcuts(inputText.trim());
            if (!text || !context || !context.voucherType) return;

            setSending(true);
            commentAPI.post(
                '/' + commentAPI.routeURL + '/create',
                {
                    voucher_type: context.voucherType,
                    voucher_id: context.voucherId,
                    voucher_code: context.voucherCode,
                    description: text
                },
                function () {
                    setSending(false);
                    setInputText('');
                    didScrollRef.current = false;
                    loadComments();
                }
            );
        },
        [inputText, context]
    );

    var handleKeyDown = useCallback(
        function (e) {
            if (e.key === 'Enter') {
                if (e.shiftKey || e.metaKey || e.ctrlKey) {
                    // Insert newline manually (Cmd/Ctrl/Shift + Enter)
                    e.preventDefault();
                    var ta = e.target;
                    var start = ta.selectionStart;
                    var end = ta.selectionEnd;
                    var val = ta.value;
                    var newVal = val.substring(0, start) + '\n' + val.substring(end);
                    setInputText(newVal);
                    // Set cursor position after React re-render
                    setTimeout(function () {
                        ta.selectionStart = ta.selectionEnd = start + 1;
                    }, 0);
                } else {
                    // Enter alone → send
                    e.preventDefault();
                    handleSend();
                }
            }
        },
        [handleSend]
    );

    // Attachment click: switch to attachment panel
    var handleAttachmentClick = useCallback(
        function (att) {
            if (onSwitchPanel) onSwitchPanel('attachment', att.id);
        },
        [onSwitchPanel]
    );

    // Empty state
    if (!context || !context.voucherType) {
        return (
            <div className="sidebar-panel-empty">
                <div className="empty-text">
                    {AntLocalization.translateText({ vi: 'Mở chứng từ để xem bình luận', en: 'Open a document to see comments', fr: 'Ouvrez un document pour commenter', es: 'Abra un documento para ver comentarios', id: 'Buka dokumen untuk melihat komentar', ja: '伝票を開くとコメントを表示', zh: '打开单据以查看评论', ko: '문서를 열면 댓글이 표시됩니다', hi: 'टिप्पणियाँ देखने हेतु दस्तावेज़ खोलें', ar: 'افتح مستندًا لعرض التعليقات' })}
                </div>
            </div>
        );
    }

    return (
        <div className="comment-panel">
            {/* Comment list */}
            <div className="comment-list" ref={listRef}>
                {loading && <div className="comment-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読込中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोडिंग...', ar: 'تحميل...' })}</div>}
                {!loading && sortedComments.length === 0 && (
                    <div className="comment-empty">{AntLocalization.translateText({ vi: 'Chưa có bình luận', en: 'No comments yet', fr: 'Aucun commentaire', es: 'Sin comentarios', id: 'Belum ada komentar', ja: 'コメントなし', zh: '暂无评论', ko: '아직 댓글이 없습니다', hi: 'अभी कोई टिप्पणी नहीं', ar: 'لا توجد تعليقات بعد' })}</div>
                )}
                {hasMore && (
                    <button
                        className="comment-load-more"
                        onClick={function () {
                            setShowAll(true);
                        }}
                    >
                        {AntLocalization.translateText({
                            vi: 'Xem thêm ' + (sortedComments.length - COMMENT_PAGE_SIZE) + ' bình luận cũ hơn',
                            en: 'Show ' + (sortedComments.length - COMMENT_PAGE_SIZE) + ' older comments'
                        })}
                    </button>
                )}
                {visibleComments.map(function (c) {
                    var hasMeddoc = c.meddoc_id && c.meddoc_name;
                    return (
                        <div key={c.id} className="comment-item">
                            {c.avatar_full_path ? (
                                <img className="comment-avatar" src={c.avatar_full_path} alt="" />
                            ) : (
                                <div className="comment-avatar">{getInitial(c.entity_full_name || c.entity_name || c.user_name)}</div>
                            )}
                            <div className="comment-body">
                                <div className="comment-meta">
                                    <span className="comment-author">{c.entity_full_name || c.entity_name || c.user_name}</span>
                                    <span className="comment-time">{commentFormatTime(c.created_at)}</span>
                                </div>
                                <div className="comment-text">{c.description}</div>
                                {hasMeddoc && (
                                    <div className="comment-attachments">
                                        <button
                                            className="comment-att-chip"
                                            onClick={function () {
                                                handleAttachmentClick({ id: c.meddoc_id, name: c.meddoc_name });
                                            }}
                                        >
                                            <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                            </svg>
                                            <span>{c.meddoc_name}</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Comment input - bottom */}
            <div className="comment-input-area">
                {allComments.length === 0 && !inputText && (
                    <div className="chat-ai-suggestions">
                        <span className="chat-ai-label">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            AI
                        </span>
                        {[
                            { vi: 'Tóm tắt phiếu này', en: 'Summarize this document', fr: 'Résumer ce document', es: 'Resumir este documento', id: 'Ringkas dokumen ini', ja: 'この伝票を要約', zh: '总结此单据', ko: '이 문서 요약', hi: 'इस दस्तावेज़ का सार', ar: 'لخّص هذا المستند' },
                            { vi: 'Có gì cần lưu ý?', en: 'Anything to note?', fr: 'Points à noter ?', es: '¿Algo que notar?', id: 'Ada catatan penting?', ja: '注意点はある？', zh: '有什么需要注意？', ko: '주의할 점이 있나요?', hi: 'कुछ ध्यान देने योग्य?', ar: 'هل من ملاحظات؟' }
                        ].map(function (s, i) {
                            return (
                                <button
                                    key={i}
                                    className="chat-ai-chip"
                                    onClick={function () {
                                        setInputText('@AI ' + AntLocalization.translateText(s));
                                    }}
                                >
                                    {AntLocalization.translateText(s)}
                                </button>
                            );
                        })}
                    </div>
                )}
                <textarea
                    className="comment-input"
                    value={inputText}
                    onChange={function (e) {
                        setInputText(e.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={AntLocalization.translateText({ vi: 'Nhập bình luận... (Enter để gửi)', en: 'Write a comment... (Enter to send)', fr: 'Votre commentaire... (Entrée : envoyer)', es: 'Escriba un comentario... (Enter: enviar)', id: 'Tulis komentar... (Enter untuk kirim)', ja: 'コメントを入力... (Enterで送信)', zh: '输入评论... (Enter 发送)', ko: '댓글 입력... (Enter로 전송)', hi: 'टिप्पणी लिखें... (भेजने हेतु Enter)', ar: 'اكتب تعليقًا... (Enter للإرسال)' })}
                    rows={2}
                    disabled={sending}
                />
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── ATTACHMENT PANEL ───────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var MOCK_FILES = [
    {
        id: 1,
        name: 'Hợp đồng mua bán - HĐ2024-042.pdf',
        size: 2457600,
        type: 'application/pdf',
        created_at: Date.now() / 1000 - 3600,
        user_name: 'Trần Minh'
    },
    {
        id: 2,
        name: 'Báo giá chi tiết.xlsx',
        size: 184320,
        type: 'application/vnd.ms-excel',
        created_at: Date.now() / 1000 - 7200,
        user_name: 'Nguyễn Hoa'
    },
    { id: 3, name: 'Ảnh sản phẩm mẫu.png', size: 1048576, type: 'image/png', created_at: Date.now() / 1000 - 86400, user_name: 'Lê Quang' },
    { id: 4, name: 'Chứng từ thanh toán.pdf', size: 512000, type: 'application/pdf', created_at: Date.now() / 1000 - 172800, user_name: 'Phạm Dũng' }
];

function formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
}

function getFileExt(name) {
    if (!name) return '';
    var parts = name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function getFileTypeLabel(ext) {
    var map = {
        pdf: 'PDF',
        xlsx: 'XLSX',
        xls: 'XLS',
        doc: 'DOC',
        docx: 'DOCX',
        png: 'PNG',
        jpg: 'JPG',
        jpeg: 'JPG',
        gif: 'GIF',
        csv: 'CSV',
        txt: 'TXT',
        zip: 'ZIP'
    };
    return map[ext] || ext.toUpperCase() || 'FILE';
}

function attachmentFormatTime(ts) {
    return sidebarDate(ts, false);
}

function AttachmentPanel(props) {
    var context = props.context;
    var mockMode = props.mockMode;

    var attachmentAPI = useMemo(function () {
        return new AntAPI('media', 'MEDDocument');
    }, []);

    var [files, setFiles] = useState(mockMode ? MOCK_FILES : []);
    var [loading, setLoading] = useState(false);
    var [uploading, setUploading] = useState(false);
    var fileInputRef = useRef(null);

    // Load attachments
    var loadFiles = useCallback(
        function () {
            if (mockMode || !context || !context.voucherType) return;
            setLoading(true);
            attachmentAPI.post(
                '/' + attachmentAPI.routeURL + '/list',
                {
                    voucher_type: context.voucherType,
                    voucher_id: context.voucherId,
                    voucher_code: context.voucherCode
                },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || [];
                    setFiles(rows);
                    setLoading(false);
                }
            );
        },
        [context, mockMode]
    );

    useEffect(
        function () {
            loadFiles();
        },
        [context ? context.voucherId : null]
    );

    // Upload handler
    var handleUploadClick = useCallback(function () {
        if (fileInputRef.current) fileInputRef.current.click();
    }, []);

    var handleFileChange = useCallback(
        function (e) {
            var fileList = e.target.files;
            if (!fileList || fileList.length === 0) return;

            if (mockMode) {
                var newFiles = [];
                for (var i = 0; i < fileList.length; i++) {
                    var f = fileList[i];
                    newFiles.push({ id: Date.now() + i, name: f.name, size: f.size, type: f.type, created_at: Date.now() / 1000, user_name: 'Bạn' });
                }
                setFiles(function (prev) {
                    return newFiles.concat(prev);
                });
                e.target.value = '';
                return;
            }

            // Real upload
            if (!context || !context.voucherType) return;
            setUploading(true);
            var formData = new FormData();
            for (var j = 0; j < fileList.length; j++) {
                formData.append('files', fileList[j]);
            }
            formData.append('voucher_type', context.voucherType);
            formData.append('voucher_id', context.voucherId);
            formData.append('voucher_code', context.voucherCode);

            attachmentAPI.post('/' + attachmentAPI.routeURL + '/upload', formData, function () {
                setUploading(false);
                loadFiles();
            });
            e.target.value = '';
        },
        [context, mockMode]
    );

    // Delete handler
    var handleDelete = useCallback(
        function (fileId) {
            if (mockMode) {
                setFiles(function (prev) {
                    return prev.filter(function (f) {
                        return f.id !== fileId;
                    });
                });
                return;
            }
            attachmentAPI.post('/' + attachmentAPI.routeURL + '/delete', { id: fileId }, function () {
                loadFiles();
            });
        },
        [mockMode]
    );

    // Empty state
    if (!mockMode && (!context || !context.voucherType)) {
        return (
            <div className="sidebar-panel-empty">
                <div className="empty-text">
                    {AntLocalization.translateText({ vi: 'Mở chứng từ để xem tài liệu đính kèm', en: 'Open a document to see attachments', fr: 'Ouvrez un document pour ses pièces jointes', es: 'Abra un documento para ver los adjuntos', id: 'Buka dokumen untuk melihat lampiran', ja: '伝票を開くと添付を表示', zh: '打开单据以查看附件', ko: '문서를 열면 첨부가 표시됩니다', hi: 'अटैचमेंट देखने हेतु दस्तावेज़ खोलें', ar: 'افتح مستندًا لعرض المرفقات' })}
                </div>
            </div>
        );
    }

    return (
        <div className="attachment-panel">
            {/* Upload area */}
            <div className="attachment-upload-area" onClick={handleUploadClick}>
                <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileChange} />
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M12 5v14M5 12h14" />
                </svg>
                <span>
                    {uploading
                        ? AntLocalization.translateText({ vi: 'Đang tải lên...', en: 'Uploading...', fr: 'Envoi en cours...', es: 'Subiendo...', id: 'Mengunggah...', ja: 'アップロード中...', zh: '上传中...', ko: '업로드 중...', hi: 'अपलोड जारी...', ar: 'جارٍ الرفع...' })
                        : AntLocalization.translateText({ vi: 'Thêm tài liệu', en: 'Add attachment', fr: 'Ajouter fichier', es: 'Añadir adjunto', id: 'Tambah lampiran', ja: '添付を追加', zh: '添加附件', ko: '첨부 추가', hi: 'अटैचमेंट जोड़ें', ar: 'إضافة مرفق' })}
                </span>
            </div>

            {/* File list */}
            <div className="attachment-list">
                {loading && <div className="attachment-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読込中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोडिंग...', ar: 'تحميل...' })}</div>}
                {!loading && files.length === 0 && (
                    <div className="attachment-empty">{AntLocalization.translateText({ vi: 'Chưa có tài liệu', en: 'No attachments', fr: 'Aucun fichier', es: 'Sin archivos', id: 'Belum ada file', ja: 'ファイルなし', zh: '暂无文件', ko: '파일 없음', hi: 'कोई फ़ाइल नहीं', ar: 'لا توجد ملفات' })}</div>
                )}
                {files.map(function (f) {
                    var ext = getFileExt(f.name);
                    return (
                        <div key={f.id} className="attachment-item">
                            <div className="attachment-icon">
                                <span className="attachment-ext">{getFileTypeLabel(ext)}</span>
                            </div>
                            <div className="attachment-info">
                                <div className="attachment-name" title={f.name}>
                                    {f.name}
                                </div>
                                <div className="attachment-meta">
                                    <span>{formatFileSize(f.size)}</span>
                                    {f.user_name && <span>{f.user_name}</span>}
                                    <span>{attachmentFormatTime(f.created_at)}</span>
                                </div>
                            </div>
                            <button
                                className="attachment-action-btn"
                                onClick={function () {
                                    handleDelete(f.id);
                                }}
                                title={AntLocalization.translateText({ vi: 'Xóa', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएं', ar: 'حذف' })}
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── TASK PANEL ─────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var MOCK_TASKS = [
    {
        id: 1,
        code: 'TSK-0051',
        title: 'Xác nhận thông tin giao hàng',
        task_status: 'WIP',
        user_name: 'Trần Minh',
        due_date: Date.now() / 1000 + 86400
    },
    {
        id: 2,
        code: 'TSK-0052',
        title: 'Kiểm tra tồn kho sản phẩm A',
        task_status: 'Done',
        user_name: 'Nguyễn Hoa',
        due_date: Date.now() / 1000 - 3600
    },
    {
        id: 3,
        code: 'TSK-0053',
        title: 'Liên hệ khách hàng xác nhận đơn',
        task_status: 'ToDo',
        user_name: 'Lê Quang',
        due_date: Date.now() / 1000 + 172800
    }
];

var TASK_STATUS_MAP = {
    New: { label: { vi: 'Mới', en: 'New', es: 'Nuevo', id: 'Baru', ja: '新規', zh: '新建', ko: '신규', hi: 'नया', ar: 'جديد', fr: 'Créé' }, color: '#94a3b8' },
    ToDo: { label: { vi: 'Cần làm', en: 'To Do', fr: 'À faire', es: 'Por hacer', id: 'Tertunda', ja: '未着手', zh: '待办', ko: '할 일', hi: 'करना है', ar: 'للتنفيذ' }, color: '#3b82f6' },
    WIP: { label: { vi: 'Đang làm', en: 'In Progress', fr: 'En cours', es: 'En curso', id: 'Dikerjakan', ja: '進行中', zh: '进行中', ko: '진행 중', hi: 'प्रगति में', ar: 'قيد التنفيذ' }, color: '#f59e0b' },
    Done: { label: { vi: 'Hoàn thành', en: 'Done', fr: 'Terminé', es: 'Completado', id: 'Selesai', ja: '完了', zh: '已完成', ko: '완료', hi: 'पूर्ण', ar: 'مكتمل' }, color: '#22c55e' },
    Failed: { label: { vi: 'Thất bại', en: 'Failed', fr: 'Échec', es: 'Error', id: 'Gagal', ja: '失敗', zh: '失败', ko: '실패', hi: 'विफल', ar: 'فشل' }, color: '#ef4444' },
    Close: { label: { vi: 'Đóng', en: 'Closed', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' }, color: '#64748b' },
    Cancel: { label: { vi: 'Hủy', en: 'Cancelled', fr: 'Annuler', es: 'Cancelar', id: 'Batal', ja: 'キャンセル', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء' }, color: '#94a3b8' }
};

function taskFormatDate(ts) {
    return sidebarDayMonth(ts);
}

function TaskPanel(props) {
    var context = props.context;
    var mockMode = props.mockMode;

    var taskAPI = useMemo(function () {
        return new AntAPI('of', 'OFTask');
    }, []);

    var [tasks, setTasks] = useState(mockMode ? MOCK_TASKS : []);
    var [loading, setLoading] = useState(false);
    var [showCreate, setShowCreate] = useState(false);
    var [newTitle, setNewTitle] = useState('');
    var [creating, setCreating] = useState(false);

    // Load tasks
    var loadTasks = useCallback(
        function () {
            if (mockMode || !context || !context.voucherType) return;
            setLoading(true);
            taskAPI.post(
                '/' + taskAPI.routeURL + '/list',
                {
                    ref_voucher_type: context.voucherType,
                    ref_voucher_id: context.voucherId,
                    ref_voucher_code: context.voucherCode,
                    pageSize: 20
                },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || [];
                    setTasks(rows);
                    setLoading(false);
                }
            );
        },
        [context, mockMode]
    );

    useEffect(
        function () {
            loadTasks();
        },
        [context ? context.voucherId : null]
    );

    // Create task
    var handleCreate = useCallback(
        function () {
            var title = newTitle.trim();
            if (!title) return;

            if (mockMode) {
                setTasks(function (prev) {
                    return [
                        {
                            id: Date.now(),
                            code: 'TSK-' + String(Math.floor(Math.random() * 9000) + 1000),
                            title: title,
                            task_status: 'New',
                            user_name: 'Bạn',
                            due_date: null
                        }
                    ].concat(prev);
                });
                setNewTitle('');
                setShowCreate(false);
                return;
            }

            if (!context || !context.voucherType) return;
            setCreating(true);
            taskAPI.post(
                '/' + taskAPI.routeURL + '/create',
                {
                    title: title,
                    ref_voucher_type: context.voucherType,
                    ref_voucher_id: context.voucherId,
                    ref_voucher_code: context.voucherCode
                },
                function () {
                    setCreating(false);
                    setNewTitle('');
                    setShowCreate(false);
                    loadTasks();
                }
            );
        },
        [newTitle, context, mockMode]
    );

    var handleKeyDown = useCallback(
        function (e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleCreate();
            }
            if (e.key === 'Escape') {
                setShowCreate(false);
                setNewTitle('');
            }
        },
        [handleCreate]
    );

    // Empty state
    if (!mockMode && (!context || !context.voucherType)) {
        return (
            <div className="sidebar-panel-empty">
                <div className="empty-text">
                    {AntLocalization.translateText({ vi: 'Mở chứng từ để xem công việc liên quan', en: 'Open a document to see related tasks', fr: 'Ouvrez un document pour voir les tâches liées', es: 'Abra un documento para ver tareas relacionadas', id: 'Buka dokumen untuk melihat tugas terkait', ja: '伝票を開くと関連タスクを表示', zh: '打开单据以查看相关任务', ko: '문서를 열면 관련 작업이 표시됩니다', hi: 'संबंधित कार्य देखने हेतु दस्तावेज़ खोलें', ar: 'افتح مستندًا لعرض المهام المرتبطة' })}
                </div>
            </div>
        );
    }

    return (
        <div className="task-panel">
            {/* Create area */}
            {!showCreate && (
                <button
                    className="task-add-btn"
                    onClick={function () {
                        setShowCreate(true);
                    }}
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                    <span>{AntLocalization.translateText({ vi: 'Tạo công việc', en: 'Create task', fr: 'Créer une tâche', es: 'Crear tarea', id: 'Buat tugas', ja: 'タスク作成', zh: '创建任务', ko: '작업 생성', hi: 'कार्य बनाएँ', ar: 'إنشاء مهمة' })}</span>
                </button>
            )}

            {showCreate && (
                <div className="task-create-form">
                    <input
                        className="task-create-input"
                        value={newTitle}
                        onChange={function (e) {
                            setNewTitle(e.target.value);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder={AntLocalization.translateText({ vi: 'Tiêu đề công việc...', en: 'Task title...', fr: 'Titre de la tâche...', es: 'Título de tarea...', id: 'Judul tugas...', ja: 'タスクのタイトル...', zh: '任务标题...', ko: '작업 제목...', hi: 'कार्य शीर्षक...', ar: 'عنوان المهمة...' })}
                        autoFocus
                        disabled={creating}
                    />
                    <div className="task-create-actions">
                        <button className="task-create-submit" onClick={handleCreate} disabled={!newTitle.trim() || creating}>
                            {AntLocalization.translateText({ vi: 'Tạo', en: 'Create', fr: 'Créer', es: 'Crear', id: 'Buat', ja: '作成', zh: '创建', ko: '생성', hi: 'बनाएँ', ar: 'إنشاء' })}
                        </button>
                        <button
                            className="task-create-cancel"
                            onClick={function () {
                                setShowCreate(false);
                                setNewTitle('');
                            }}
                        >
                            {AntLocalization.translateText({ vi: 'Hủy', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء', ja: '取消' })}
                        </button>
                    </div>
                </div>
            )}

            {/* Task list */}
            <div className="task-list">
                {loading && <div className="task-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読込中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोडिंग...', ar: 'تحميل...' })}</div>}
                {!loading && tasks.length === 0 && (
                    <div className="task-empty">{AntLocalization.translateText({ vi: 'Chưa có công việc', en: 'No tasks', fr: 'Aucune tâche', es: 'Sin tareas', id: 'Belum ada tugas', ja: 'タスクなし', zh: '暂无任务', ko: '작업 없음', hi: 'कोई कार्य नहीं', ar: 'لا توجد مهام' })}</div>
                )}
                {tasks.map(function (t) {
                    var statusDef = TASK_STATUS_MAP[t.task_status] || TASK_STATUS_MAP.New;
                    return (
                        <div key={t.id} className="task-item">
                            <div className="task-status-dot" style={{ backgroundColor: statusDef.color }} />
                            <div className="task-content">
                                <div className="task-title">{t.title}</div>
                                <div className="task-meta">
                                    <span className="task-status-label" style={{ color: statusDef.color }}>
                                        {AntLocalization.translateText(statusDef.label)}
                                    </span>
                                    {t.user_name && <span className="task-assignee">{t.user_name}</span>}
                                    {t.due_date && <span className="task-due">{taskFormatDate(t.due_date)}</span>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── AI PANEL ───────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

function getMockMessages(context) {
    var voucherCode = (context && context.voucherCode) || 'ARO-2024-0042';
    var voucherLabel = (context && context.voucherTypeLabel) || 'Đơn hàng';

    if (context && context.variant === 'search') {
        return [
            {
                role: 'assistant',
                blocks: [
                    { type: 'text', text: 'Tôi có thể giúp bạn phân tích danh sách dữ liệu hiện tại. Dưới đây là tổng hợp nhanh:' },
                    {
                        type: 'table',
                        headers: ['Chỉ số', 'Giá trị'],
                        rows: [
                            ['Tổng số bản ghi', '142'],
                            ['Trạng thái Release', '38 (26.8%)'],
                            ['Trạng thái Open', '71 (50.0%)'],
                            ['Trạng thái Close', '33 (23.2%)'],
                            ['Tổng giá trị', '2,847,500,000 đ']
                        ]
                    },
                    { type: 'text', text: 'Bạn cần xem thêm chi tiết hoặc lọc theo tiêu chí cụ thể nào?' }
                ]
            }
        ];
    }

    if (!context || !context.voucherType) {
        return [
            {
                role: 'assistant',
                blocks: [
                    { type: 'text', text: 'Chào buổi sáng! Tôi có thể giúp bạn nhanh các việc sau:' },
                    {
                        type: 'text',
                        text: '• Tra cứu đơn hàng, công nợ, tồn kho\n• Tóm tắt doanh thu, chi phí\n• Hướng dẫn quy trình nghiệp vụ\n• Thực hiện thao tác trên phiếu'
                    }
                ]
            },
            { role: 'user', blocks: [{ type: 'text', text: 'Doanh thu hôm nay thế nào?' }] },
            {
                role: 'assistant',
                blocks: [
                    {
                        type: 'table',
                        headers: ['Kênh bán', 'Số đơn', 'Doanh thu'],
                        rows: [
                            ['POS tại quầy', '23', '45,800,000 đ'],
                            ['Online', '12', '18,650,000 đ'],
                            ['Đại lý', '5', '32,100,000 đ']
                        ]
                    },
                    { type: 'text', text: 'Tổng doanh thu hôm nay: 96,550,000 đ (+12% so với cùng kỳ tuần trước).' },
                    { type: 'status', variant: 'info', text: '3 đơn hàng đang chờ duyệt, 2 đơn quá hạn thanh toán.' }
                ]
            }
        ];
    }

    return [
        {
            role: 'assistant',
            blocks: [
                { type: 'text', text: 'Đây là thông tin ' + voucherLabel + ' ' + voucherCode + ':' },
                {
                    type: 'table',
                    headers: ['Thông tin', 'Giá trị'],
                    rows: [
                        ['Mã đơn', voucherCode],
                        ['Khách hàng', 'Công ty TNHH Minh Phát'],
                        ['Trạng thái', 'Release'],
                        ['Tổng tiền', '15,250,000 đ'],
                        ['Số dòng', '5 sản phẩm'],
                        ['Ngày tạo', '20/03/2026'],
                        ['Hạn thanh toán', '04/04/2026']
                    ]
                },
                { type: 'text', text: 'Đơn hàng đã Release, sẵn sàng xuất kho. Công nợ phải thu: 15,250,000 đ.' }
            ]
        },
        { role: 'user', blocks: [{ type: 'text', text: 'Kiểm tra tồn kho cho đơn này' }] },
        {
            role: 'assistant',
            blocks: [
                {
                    type: 'table',
                    headers: ['Sản phẩm', 'Yêu cầu', 'Tồn kho', 'Trạng thái'],
                    rows: [
                        ['Bàn phím cơ K65', '10', '25', '✓ Đủ'],
                        ['Chuột không dây M300', '20', '18', '⚠ Thiếu 2'],
                        ['Tai nghe BT H50', '5', '12', '✓ Đủ'],
                        ['Bao da laptop 15"', '15', '15', '✓ Vừa đủ'],
                        ['Cáp sạc USB-C', '30', '45', '✓ Đủ']
                    ]
                },
                { type: 'status', variant: 'info', text: 'Chuột M300 thiếu 2 cái. Có thể chuyển kho từ Chi nhánh 2 (còn 30 cái).' },
                {
                    type: 'action',
                    actions: [
                        { key: 'transfer', label: 'Tạo phiếu chuyển kho', variant: 'primary' },
                        { key: 'cancel', label: 'Bỏ qua', variant: 'default' }
                    ]
                }
            ]
        }
    ];
}

// ─── renderMarkdown imported from ./shared ──────────────────────

function MarkdownText(props) {
    var html = renderMarkdown(props.text || '');
    return <div className={props.className || 'chat-msg-text'} dangerouslySetInnerHTML={{ __html: html }} />;
}

// ─── MessageBlock imported from ./shared ────────────────────────

export function AIPanel(props) {
    var context = props.context;
    var mockMode = props.mockMode;
    var aiNavigate = useNavigate();
    var isGlobalMode = !context || (!context.voucherType && context.variant !== 'search');

    var [messages, setMessages] = useState(function () {
        return mockMode ? getMockMessages(context) : [];
    });
    var [inputText, setInputText] = useState('');
    var [isTyping, setIsTyping] = useState(false);
    var [conversationId, setConversationId] = useState(null);
    var [streamingText, setStreamingText] = useState('');
    var [isStreaming, setIsStreaming] = useState(false);
    var [view, setView] = useState(isGlobalMode ? 'list' : 'chat'); // list | chat | settings
    var [conversations, setConversations] = useState([]);
    var [convLoading, setConvLoading] = useState(false);
    var [chatSearchText, setChatSearchText] = useState('');
    var [promptList, setPromptList] = useState([]);
    var [activePromptCode, setActivePromptCode] = useState(null);
    var [isSysConv, setIsSysConv] = useState(false);
    // Settings state
    var [editConvName, setEditConvName] = useState('');
    var [savingName, setSavingName] = useState(false);
    var [aiDocs, setAiDocs] = useState([]);
    var [aiDocsLoading, setAiDocsLoading] = useState(false);
    var [aiViewerState, setAiViewerState] = useState(null);
    // Attach state
    var [pendingFiles, setPendingFiles] = useState([]);
    var [sending, setSending] = useState(false);

    var listRef = useRef(null);
    var fileInputRef = useRef(null);
    var settingsFileRef = useRef(null);
    var aiAPI = useMemo(function () {
        return new AntAPI('ai', 'AISession');
    }, []);
    var msgAPI = useMemo(function () {
        return new AntAPI('ai', 'AIMessage');
    }, []);
    var docAPI = useMemo(function () {
        return new AntAPI('media', 'MEDDocument');
    }, []);
    var session = AntSession();
    var streamConvRef = useRef(null);
    var confirm = useConfirm();
    var backIcon = (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 19l-7-7 7-7" />
        </svg>
    );
    var handleNavigate = useCallback(
        function (url) {
            if (url && aiNavigate) aiNavigate(url);
        },
        [aiNavigate]
    );

    var [dragOver, setDragOver] = useState(false);
    var atBottomRef = useRef(true);
    var prevMsgLenRef = useRef(0);
    var textareaRef = useRef(null);

    // ─── Load messages (giống ChatPanel.loadMessages) ────────────
    var loadMessages = useCallback(
        function (convId, opts) {
            var cid = convId || conversationId;
            console.log('[FE-AI] loadMessages: cid=' + cid + ', conversationId=' + conversationId + ', opts=' + JSON.stringify(opts || {}));
            if (!cid) {
                console.log('[FE-AI] loadMessages: NO CID → skip');
                return;
            }
            msgAPI.post(
                '/' + msgAPI.routeURL + '/list',
                {
                    session_id: cid,
                    pageSize: 50,
                    ordering: [{ name: 'id', type: 'desc' }]
                },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                    if (!(rows instanceof Array)) rows = [];
                    rows.sort(function (a, b) {
                        return String(a.id || '0').padStart(18, '0').localeCompare(String(b.id || '0').padStart(18, '0'));
                    });
                    console.log(
                        '[FE-AI] loadMessages callback: ' +
                            rows.length +
                            ' rows, first content=' +
                            (rows.length > 0 ? (rows[0].content || '').substring(0, 30) : 'EMPTY')
                    );
                    setMessages(rows);
                    if (opts && opts.clearStreaming) {
                        setIsStreaming(false);
                        setIsTyping(false);
                        setStreamingText('');
                    }
                    if (!(opts && opts.skipScroll)) {
                        setTimeout(function () {
                            if (listRef.current) {
                                listRef.current.scrollTop = listRef.current.scrollHeight;
                                atBottomRef.current = true;
                            }
                        }, 50);
                    }
                }
            );
        },
        [conversationId, msgAPI]
    );

    // Ref để socket callback luôn dùng loadMessages mới nhất
    var loadMessagesRef = useRef(loadMessages);
    loadMessagesRef.current = loadMessages;

    // ─── Socket streaming ───────────────────────────────────────
    useEffect(
        function () {
            if (!session || !session.registerSocketListener) return;
            var listenerKey = 'AISessionStream';
            var onSocketReceived = function (data) {
                if (!data || data.event !== 'AISessionStream') return false;
                console.log(
                    '[FE-AI-Socket] received:',
                    data.done ? 'DONE' : data.status ? 'status=' + data.status : 'chunk=' + (data.chunk || '').substring(0, 30),
                    'convId=' + data.session_id,
                    'streamRef=' + streamConvRef.current
                );
                if (streamConvRef.current && data.session_id && String(data.session_id) !== String(streamConvRef.current)) {
                    console.log('[FE-AI-Socket] REJECTED: conv mismatch');
                    return false;
                }
                if (data.session_id && !streamConvRef.current) {
                    streamConvRef.current = data.session_id;
                    console.log('[FE-AI-Socket] set streamConvRef=' + data.session_id);
                }
                if (data.status === 'searching') {
                    setStreamingText(AntLocalization.translateText({ vi: 'Đang tra cứu dữ liệu...', en: 'Searching data...', fr: 'Recherche de données...', es: 'Buscando datos...', id: 'Mencari data...', ja: 'データを検索中...', zh: '正在查询数据...', ko: '데이터 조회 중...', hi: 'डेटा खोजा जा रहा है...', ar: 'جارٍ البحث في البيانات...' }));
                    setIsStreaming(true);
                    return true;
                }
                if (data.done) {
                    console.log('[FE-AI-Socket] DONE → loadMessages');
                    loadMessagesRef.current(data.session_id || streamConvRef.current, { clearStreaming: true });
                    return true;
                }
                if (data.chunk) {
                    setIsStreaming(true);
                    setStreamingText(function (prev) {
                        return (prev || '') + data.chunk;
                    });
                }
                return true;
            };
            session.registerSocketListener(onSocketReceived, listenerKey);
            return function () {
                session.clearSocketListener(listenerKey);
            };
        },
        [session]
    ); // Không phụ thuộc loadMessages — dùng ref

    // ─── Smart scroll (giống ChatPanel) ─────────────────────────
    var handleMsgScroll = useCallback(function () {
        if (!listRef.current) return;
        var el = listRef.current;
        atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    }, []);

    useEffect(
        function () {
            if (messages.length > prevMsgLenRef.current && atBottomRef.current && listRef.current) {
                listRef.current.scrollTop = listRef.current.scrollHeight;
            }
            prevMsgLenRef.current = messages.length;
        },
        [messages.length]
    );

    // Scroll xuống khi bắt đầu typing/streaming
    useEffect(
        function () {
            if ((isTyping || isStreaming) && atBottomRef.current && listRef.current) {
                listRef.current.scrollTop = listRef.current.scrollHeight;
            }
        },
        [isTyping, isStreaming, streamingText]
    );

    useEffect(
        function () {
            setMessages([]);
            setConversationId(null);
            setView(isGlobalMode ? 'list' : 'chat');
        },
        [context ? context.voucherId : null]
    );

    // ─── Drag-drop (giống ChatPanel) ────────────────────────────
    var handleDragOver = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    }, []);
    var handleDragLeave = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    }, []);
    var handleDrop = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setPendingFiles(function (prev) {
                return prev.concat(Array.prototype.slice.call(e.dataTransfer.files));
            });
        }
    }, []);

    // ─── Load prompt list (danh sách phiên có sẵn) ────────────
    var loadPromptList = useCallback(
        function () {
            aiAPI.post('/' + aiAPI.routeURL + '/getPromptList', {}, function (result) {
                var data = (result && result.data) || result || {};
                var prompts = data.prompts || [];
                setPromptList(prompts instanceof Array ? prompts : []);
            });
        },
        [aiAPI]
    );

    useEffect(function () {
        loadPromptList();
    }, []);

    // ─── Load conversations (ensure defaults first) ──────────
    var defaultsEnsured = useRef(false);
    var loadConversations = useCallback(
        function () {
            setConvLoading(true);
            var doLoad = function () {
                aiAPI.post(
                    '/' + aiAPI.routeURL + '/list',
                    { session_status: 'Active', pageSize: 50, orderBy: 'is_sys', orderType: 'desc' },
                    function (result) {
                        setConvLoading(false);
                        var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                        setConversations(rows instanceof Array ? rows : []);
                    }
                );
            };
            if (!defaultsEnsured.current) {
                defaultsEnsured.current = true;
                aiAPI.post('/' + aiAPI.routeURL + '/ensureDefaults', {}, function () {
                    doLoad();
                });
            } else {
                doLoad();
            }
        },
        [aiAPI]
    );

    useEffect(
        function () {
            if (isGlobalMode && view === 'list') loadConversations();
        },
        [isGlobalMode, view]
    );

    var openConversation = useCallback(
        function (conv) {
            setConversationId(conv.id);
            setEditConvName(conv.title || '');
            setActivePromptCode(conv.prompt_code || null);
            setIsSysConv(conv.is_sys === 1);
            setView('chat');
            setMessages([]);
            loadMessages(conv.id);
        },
        [loadMessages]
    );

    var newConversation = useCallback(function (promptCode) {
        setConversationId(null);
        setMessages([]);
        setEditConvName('');
        setActivePromptCode(promptCode || null);
        setView('chat');
    }, []);
    var backToList = useCallback(
        function () {
            setView('list');
            loadConversations();
        },
        [loadConversations]
    );

    // ─── Settings: save name ────────────────────────────────────
    var handleSaveName = useCallback(
        function () {
            if (!conversationId || !editConvName.trim()) return;
            if (mockMode) {
                setSavingName(false);
                return;
            }
            setSavingName(true);
            aiAPI.post('/' + aiAPI.routeURL + '/update', { where: { id: conversationId }, update: { title: editConvName.trim() } }, function () {
                setSavingName(false);
                loadConversations();
            });
        },
        [conversationId, editConvName, mockMode, aiAPI]
    );

    // ─── Settings: clear messages (giữ phiên, dọn nội dung) ────
    var handleClearMessages = useCallback(
        function () {
            if (!conversationId) return;
            confirm.show(AntLocalization.translateText({ vi: 'Dọn toàn bộ nội dung trao đổi?', en: 'Clear all messages?', fr: 'Effacer tous les messages ?', es: '¿Borrar todos los mensajes?', id: 'Hapus semua pesan?', ja: 'すべてのメッセージを消去しますか？', zh: '清空所有消息？', ko: '모든 메시지를 지울까요?', hi: 'सभी संदेश साफ़ करें?', ar: 'مسح كل الرسائل؟' }), function () {
                aiAPI.post('/' + aiAPI.routeURL + '/clearMessages', { id: conversationId }, function () {
                    setMessages([]);
                });
            });
        },
        [conversationId, confirm, aiAPI]
    );

    // ─── Settings: delete conversation ──────────────────────────
    var handleDeleteConv = useCallback(
        function () {
            confirm.show(AntLocalization.translateText({ vi: 'Xóa hội thoại AI này?', en: 'Delete this AI conversation?', fr: 'Supprimer cette discussion IA ?', es: '¿Eliminar esta conversación IA?', id: 'Hapus percakapan AI ini?', ja: 'このAI会話を削除しますか？', zh: '删除此AI对话？', ko: '이 AI 대화를 삭제할까요?', hi: 'यह AI वार्तालाप हटाएँ?', ar: 'حذف محادثة AI هذه؟' }), function () {
                if (mockMode) {
                    setView('list');
                    setMessages([]);
                    setConversationId(null);
                    return;
                }
                aiAPI.post('/' + aiAPI.routeURL + '/delete', { id: conversationId }, function () {
                    setView('list');
                    setMessages([]);
                    setConversationId(null);
                    loadConversations();
                });
            });
        },
        [conversationId, confirm, mockMode, aiAPI]
    );

    // ─── Settings: load docs ────────────────────────────────────
    var loadAiDocs = useCallback(
        function () {
            if (!conversationId || mockMode) {
                setAiDocs([]);
                return;
            }
            setAiDocsLoading(true);
            msgAPI.post(
                '/' + msgAPI.routeURL + '/list',
                { session_id: conversationId, content_type: 'file', pageSize: 50, ordering: [{ name: 'id', type: 'desc' }] },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                    var docs = [];
                    if (rows instanceof Array) {
                        rows.forEach(function (m) {
                            if (m.meddoc_id && m.meddoc_name && (m.file_full_path || m.file_short_path)) {
                                docs.push({
                                    id: m.meddoc_id,
                                    name: m.meddoc_name,
                                    full_path: m.file_full_path,
                                    short_path: m.file_short_path,
                                    type: m.content_type || '',
                                    sub_type: '',
                                    user_name: m.user_name || m.created_user_name,
                                    created_at: m.created_at,
                                    message_id: m.id
                                });
                            }
                        });
                    }
                    setAiDocs(docs);
                    setAiDocsLoading(false);
                }
            );
        },
        [conversationId, mockMode, msgAPI]
    );

    // ─── Settings: upload doc ───────────────────────────────────
    var handleSettingsFileUpload = useCallback(
        function (e) {
            if (!e.target.files || !e.target.files[0] || !conversationId) return;
            var file = e.target.files[0];
            e.target.value = '';
            if (mockMode) return;
            var fd = new FormData();
            fd.append('files', file);
            fd.append('session_id', conversationId);
            msgAPI.upload('/' + msgAPI.routeURL + '/createWithAttachment', fd, function () {
                loadAiDocs();
            });
        },
        [conversationId, mockMode, msgAPI, loadAiDocs]
    );

    // ─── Settings: delete doc ───────────────────────────────────
    var handleDeleteDoc = useCallback(
        function (doc) {
            docAPI.post('/' + docAPI.routeURL + '/delete', { id: doc.id }, function () {
                if (doc.message_id) {
                    msgAPI.post(
                        '/' + msgAPI.routeURL + '/update',
                        { where: { id: doc.message_id }, update: { file_full_path: null, file_short_path: null } },
                        function () {
                            loadAiDocs();
                        }
                    );
                } else {
                    loadAiDocs();
                }
            });
        },
        [docAPI, msgAPI, loadAiDocs]
    );

    // ─── Context params ─────────────────────────────────────────
    var getContextParams = useCallback(
        function () {
            if (!context) return { context_mode: 'global' };
            if (context.voucherType && context.voucherCode)
                return { context_mode: 'voucher', context_type: context.voucherType, context_code: context.voucherCode };
            if (context.variant === 'search' && context.serviceName) {
                var ctxData = {};
                if (context.searchConditions) ctxData.searchConditions = context.searchConditions;
                if (context.searchText) ctxData.searchText = context.searchText;
                if (context.totalCount != null) ctxData.totalCount = context.totalCount;
                if (context.searchGroup) ctxData.searchGroup = context.searchGroup;
                return { context_mode: 'search', context_type: context.serviceName, context_data: ctxData };
            }
            return { context_mode: 'global' };
        },
        [context]
    );

    // ─── Send text ──────────────────────────────────────────────
    var sendText = useCallback(
        function (text) {
            if (!text || isTyping) return;
            setIsTyping(true);
            // Mở khóa streamConvRef để nhận chunks từ BẤT KỲ conversation nào
            // (sẽ set đúng ID khi HTTP response về)
            streamConvRef.current = null;
            var body = Object.assign({ content: text }, getContextParams());
            if (conversationId) body.session_id = conversationId;
            if (activePromptCode) body.prompt_code = activePromptCode;
            var canStream = session && session.registerSocketListener;
            if (canStream) body.streaming = true;
            aiAPI.post('/' + aiAPI.routeURL + '/actionSendMessage', body, function (result) {
                var data = (result && result.data) || result || {};
                if (data.conversation && data.conversation.id) {
                    var newConvId = data.conversation.id;
                    setConversationId(newConvId);
                    streamConvRef.current = newConvId;
                    if (!editConvName) setEditConvName((text || '').substring(0, 60));
                }
                if (data.streaming) {
                    loadMessages(data.conversation ? data.conversation.id : conversationId);
                    return;
                }
                setIsTyping(false);
                loadMessages(data.conversation ? data.conversation.id : conversationId);
            });
        },
        [isTyping, conversationId, getContextParams, aiAPI, session, loadMessages, editConvName]
    );

    // ─── Send (text + files) ────────────────────────────────────
    // Luôn upload file TRƯỚC → rồi mới gọi AI (sendText)
    // Để khi Gemini nhận history, file đã có trong DB
    var handleSend = useCallback(
        function () {
            var text = inputText.trim();
            if (!text && pendingFiles.length === 0) return;
            if (isTyping || sending) return;
            setInputText('');

            if (pendingFiles.length > 0 && conversationId) {
                setSending(true);
                var filesToSend = pendingFiles.slice();
                setPendingFiles([]);
                var sendNextFile = function (idx) {
                    if (idx >= filesToSend.length) {
                        setSending(false);
                        // File upload xong → gọi AI nếu có text
                        if (text) {
                            sendText(text);
                        } else {
                            loadMessages(conversationId);
                        }
                        if (textareaRef.current) textareaRef.current.focus();
                        return;
                    }
                    var fd = new FormData();
                    fd.append('files', filesToSend[idx]);
                    fd.append('session_id', conversationId);
                    msgAPI.upload('/' + msgAPI.routeURL + '/createWithAttachment', fd, function () {
                        sendNextFile(idx + 1);
                    });
                };
                sendNextFile(0);
            } else {
                if (text) {
                    setPendingFiles([]);
                    sendText(text);
                }
            }
        },
        [inputText, pendingFiles, isTyping, sending, conversationId, sendText, msgAPI, loadMessages]
    );

    var handleKeyDown = useCallback(
        function (e) {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
            }
        },
        [handleSend]
    );

    // ─── File selection ─────────────────────────────────────────
    var handleFileSelect = useCallback(function (e) {
        if (!e.target.files || e.target.files.length === 0) return;
        var newFiles = Array.prototype.slice.call(e.target.files);
        setPendingFiles(function (prev) {
            return prev.concat(newFiles);
        });
        e.target.value = '';
    }, []);

    var removePendingFile = useCallback(function (idx) {
        setPendingFiles(function (prev) {
            return prev.filter(function (_, i) {
                return i !== idx;
            });
        });
    }, []);

    // ─── Action handler ─────────────────────────────────────────
    var handleAction = useCallback(
        function (actionKey) {
            if (actionKey === 'cancel') {
                setMessages(function (prev) {
                    return prev.concat([
                        { role: 'user', blocks: [{ type: 'text', text: 'Hủy' }] },
                        { role: 'assistant', blocks: [{ type: 'text', text: 'Đã hủy thao tác.' }] }
                    ]);
                });
                return;
            }
            if (mockMode) {
                setMessages(function (prev) {
                    return prev.concat([{ role: 'user', blocks: [{ type: 'text', text: 'Xác nhận' }] }]);
                });
                setIsTyping(true);
                setTimeout(function () {
                    setMessages(function (prev) {
                        return prev.concat([
                            { role: 'assistant', blocks: [{ type: 'status', variant: 'success', text: 'Thao tác đã được thực hiện thành công.' }] }
                        ]);
                    });
                    setIsTyping(false);
                }, 600);
            }
        },
        [mockMode]
    );

    // ─── Suggested prompts ──────────────────────────────────────
    var suggestedPrompts = useMemo(
        function () {
            if (context && context.voucherType) {
                var svc = context.voucherType;
                if (svc === 'AROrder') return ['Tóm tắt đơn hàng này', 'Giải thích trạng thái hiện tại', 'Đơn này có gì bất thường?'];
                if (svc === 'APOrder') return ['Tóm tắt đơn mua hàng', 'Công nợ phải trả bao nhiêu?', 'Tiến độ nhận hàng'];
                if (svc === 'ARCredit' || svc === 'APCredit') return ['Giải thích công nợ này', 'Trạng thái thanh toán', 'Lịch sử giao dịch'];
                if (svc.indexOf('IC') === 0) return ['Tồn kho hiện tại', 'Lịch sử xuất nhập', 'Giải thích các trường'];
                return ['Tóm tắt phiếu này', 'Giải thích trạng thái', 'Có gì cần lưu ý?'];
            }
            if (context && context.variant === 'search') return ['Tóm tắt dữ liệu hiện tại', 'Xu hướng đáng chú ý?', 'Đề xuất bộ lọc khác'];
            return ['Hướng dẫn quy trình bán hàng', 'Tổng doanh thu hôm nay?', 'Đơn hàng nào đang chờ duyệt?', 'Sản phẩm sắp hết hàng?'];
        },
        [context]
    );

    // AI avatar inline style (dùng chung)
    var aiAvatarStyle = {
        width: 28,
        height: 28,
        borderRadius: '50%',
        // background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 10,
        fontWeight: 700
    };
    var aiAvatarListStyle = Object.assign({}, aiAvatarStyle, { width: 32, height: 32, fontSize: 11 });

    // ─── File viewer ──────────────────────────────────────────────
    var openFileViewer = useCallback(
        function (m) {
            var allFiles = messages
                .filter(function (x) {
                    return x.meddoc_id && (x.file_full_path || x.file_short_path);
                })
                .map(function (x) {
                    return { name: x.meddoc_name || x.content, url: x.file_full_path || x.file_short_path, type: '' };
                });
            var idx = 0;
            var targetUrl = m.file_full_path || m.file_short_path;
            for (var i = 0; i < allFiles.length; i++) {
                if (allFiles[i].url === targetUrl) {
                    idx = i;
                    break;
                }
            }
            if (allFiles.length > 0) setAiViewerState({ files: allFiles, index: idx });
        },
        [messages]
    );

    // ═══ SETTINGS VIEW (giống Chat settings) ════════════════════
    if (view === 'settings') {
        return (
            <div className="chat-panel">
                {confirm.el}
                <div className="chat-subview-header">
                    <button
                        className="chat-back-btn"
                        onClick={function () {
                            setView('chat');
                            setTimeout(function () {
                                if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
                            }, 50);
                        }}
                    >
                        {backIcon}
                    </button>
                    <span className="chat-subview-title">{AntLocalization.translateText({ vi: 'Cấu hình', en: 'Settings', fr: 'Paramètres', id: 'Pengaturan', ja: '設定', zh: '设置', ko: '설정', hi: 'सेटिंग्स', ar: 'الإعدادات', es: 'Ajustes' })}</span>
                </div>
                <div className="chat-settings-body">
                    {/* Rename */}
                    <div className="chat-settings-section">
                        <span className="chat-settings-label">{AntLocalization.translateText({ vi: 'Tên hội thoại', en: 'Conversation name', fr: 'Titre de discussion', es: 'Nombre del chat', id: 'Nama percakapan', ja: '会話名', zh: '对话名称', ko: '대화 이름', hi: 'वार्तालाप का नाम', ar: 'اسم المحادثة' })}</span>
                        <div className="chat-settings-rename-row">
                            <input
                                className="chat-settings-name-input"
                                value={editConvName}
                                onChange={function (e) {
                                    setEditConvName(e.target.value);
                                }}
                                onKeyDown={function (e) {
                                    if (e.key === 'Enter') handleSaveName();
                                }}
                                placeholder={AntLocalization.translateText({ vi: 'Nhập tên...', en: 'Enter name...', fr: 'Saisir le nom...', es: 'Nombre...', id: 'Masukkan nama...', ja: '名前を入力...', zh: '输入名称...', ko: '이름 입력...', hi: 'नाम लिखें...', ar: 'أدخل الاسم...' })}
                            />
                            <button
                                className="chat-settings-save-btn"
                                onClick={handleSaveName}
                                disabled={savingName || !editConvName.trim()}
                                title={AntLocalization.translateText({ vi: 'Lưu', en: 'Save', es: 'Guardar', id: 'Simpan', ja: '保存', zh: '保存', ko: '저장', hi: 'सहेजें', ar: 'حفظ', fr: 'Enreg.' })}
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                                    <polyline points="17 21 17 13 7 13 7 21" />
                                    <polyline points="7 3 7 8 15 8" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    {/* Documents */}
                    <div className="chat-settings-section">
                        <div className="chat-settings-label-row">
                            <span className="chat-settings-label">
                                {AntLocalization.translateText({ vi: 'Tài liệu đính kèm', en: 'Attachments', fr: 'Pièces jointes', es: 'Adjuntos', id: 'Lampiran', ja: '添付ファイル', zh: '附件', ko: '첨부 파일', hi: 'अटैचमेंट', ar: 'المرفقات' })}
                            </span>
                            <button
                                className="chat-settings-link-btn"
                                onClick={function () {
                                    if (settingsFileRef.current) settingsFileRef.current.click();
                                }}
                            >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 5v14M5 12h14" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Thêm', en: 'Add', fr: 'Ajouter', es: 'Añadir', id: 'Tambah', ja: '追加', zh: '添加', ko: '추가', hi: 'जोड़ें', ar: 'إضافة' })}
                            </button>
                            <input ref={settingsFileRef} type="file" style={{ display: 'none' }} onChange={handleSettingsFileUpload} />
                        </div>
                        <div className="chat-settings-doc-list">
                            {aiDocsLoading && (
                                <div className="chat-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読込中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोडिंग...', ar: 'تحميل...' })}</div>
                            )}
                            {!aiDocsLoading && aiDocs.length === 0 && (
                                <div className="chat-settings-empty">
                                    {AntLocalization.translateText({ vi: 'Chưa có tài liệu', en: 'No documents', fr: 'Aucun fichier', es: 'Sin archivos', id: 'Belum ada file', ja: 'ファイルなし', zh: '暂无文件', ko: '파일 없음', hi: 'कोई फ़ाइल नहीं', ar: 'لا توجد ملفات' })}
                                </div>
                            )}
                            {aiDocs.map(function (d) {
                                return (
                                    <FileListItem
                                        key={d.id}
                                        file={d}
                                        onPreview={function () {
                                            var viewFiles = aiDocs
                                                .filter(function (x) {
                                                    return x.full_path;
                                                })
                                                .map(function (x) {
                                                    return { name: x.name, url: x.full_path, type: x.sub_type || x.type || '' };
                                                });
                                            var viewIdx = 0;
                                            for (var vi = 0; vi < viewFiles.length; vi++) {
                                                if (viewFiles[vi].url === d.full_path) {
                                                    viewIdx = vi;
                                                    break;
                                                }
                                            }
                                            if (viewFiles.length > 0) setAiViewerState({ files: viewFiles, index: viewIdx });
                                        }}
                                        onDelete={function () {
                                            handleDeleteDoc(d);
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                    {/* Actions */}
                    <div className="chat-settings-danger">
                        <button className="chat-delete-btn" onClick={handleClearMessages}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {AntLocalization.translateText({ vi: 'Dọn nội dung trao đổi', en: 'Clear all messages', fr: 'Effacer les messages', es: 'Borrar mensajes', id: 'Hapus semua pesan', ja: 'メッセージを消去', zh: '清空消息', ko: '메시지 지우기', hi: 'सभी संदेश साफ़ करें', ar: 'مسح كل الرسائل' })}
                        </button>
                        {!isSysConv && (
                            <button className="chat-delete-btn" style={{ marginTop: 8 }} onClick={handleDeleteConv}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Xóa hội thoại', en: 'Delete conversation', fr: 'Supprimer la discussion', es: 'Eliminar conversación', id: 'Hapus percakapan', ja: '会話を削除', zh: '删除对话', ko: '대화 삭제', hi: 'वार्तालाप हटाएँ', ar: 'حذف المحادثة' })}
                            </button>
                        )}
                    </div>
                </div>
                {aiViewerState && (
                    <AntFileViewer
                        visible
                        files={aiViewerState.files}
                        initialIndex={aiViewerState.index}
                        onClose={function () {
                            setAiViewerState(null);
                        }}
                    />
                )}
            </div>
        );
    }

    // Active prompt info (cho chat detail header)
    var activePromptDef = activePromptCode
        ? promptList.find(function (p) {
              return p.code === activePromptCode;
          })
        : null;

    // ═══ LIST VIEW ══════════════════════════════════════════════
    if (isGlobalMode && view === 'list') {
        var sysConvs = conversations.filter(function (c) {
            return c.is_sys === 1;
        });
        var userConvs = conversations.filter(function (c) {
            return c.is_sys !== 1;
        });

        if (chatSearchText.trim()) {
            var sq = chatSearchText.trim().toLowerCase();
            sysConvs = sysConvs.filter(function (c) {
                return (c.title || '').toLowerCase().indexOf(sq) >= 0;
            });
            userConvs = userConvs.filter(function (c) {
                return (c.title || '').toLowerCase().indexOf(sq) >= 0;
            });
        }

        return (
            <div className="chat-panel">
                <div className="chat-search-bar">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input
                        className="chat-search-input"
                        value={chatSearchText}
                        onChange={function (e) {
                            setChatSearchText(e.target.value);
                        }}
                        placeholder={AntLocalization.translateText({ vi: 'Tìm...', en: 'Search...', fr: 'Chercher...', es: 'Buscar...', id: 'Cari...', ja: '検索...', zh: '搜索...', ko: '검색...', hi: 'खोजें...', ar: 'بحث...' })}
                    />
                    {chatSearchText && (
                        <button
                            className="chat-search-clear"
                            onClick={function () {
                                setChatSearchText('');
                            }}
                        >
                            ×
                        </button>
                    )}
                </div>
                <div className="chat-list">
                    {convLoading && <div className="chat-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読込中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोडिंग...', ar: 'تحميل...' })}</div>}

                    {/* Phiên mặc định (is_sys) */}
                    {sysConvs.length > 0 &&
                        sysConvs.map(function (conv) {
                            var pDef = promptList.find(function (p) {
                                return p.code === conv.prompt_code;
                            });
                            var icon = pDef ? pDef.icon : '🤖';
                            var subtitle =
                                pDef && pDef.suggestions && pDef.suggestions.length > 0 ? AntLocalization.translateText(pDef.suggestions[0]) : '';
                            return (
                                <div
                                    key={conv.id}
                                    className="chat-item"
                                    onClick={function () {
                                        openConversation(conv);
                                    }}
                                >
                                    <div className="chat-item-avatar">
                                        <span style={aiAvatarListStyle}>{icon}</span>
                                    </div>
                                    <div className="chat-item-body">
                                        <div className="chat-item-header">
                                            <span className="chat-item-name">{conv.title}</span>
                                            {conv.last_message_at ? (
                                                <span className="chat-item-time">{commentFormatTime(conv.last_message_at)}</span>
                                            ) : null}
                                        </div>
                                        <div className="chat-item-preview">{conv.last_message_content || subtitle}</div>
                                    </div>
                                </div>
                            );
                        })}

                    {/* Phiên do user tạo */}
                    {userConvs.length > 0 && (
                        <Fragment>
                            <div className="reminder-group-label">{AntLocalization.translateText({ vi: 'Khác', en: 'Other', fr: 'Autre', es: 'Otros', id: 'Lainnya', ja: 'その他', zh: '其他', ko: '기타', hi: 'अन्य', ar: 'أخرى' })}</div>
                            {userConvs.map(function (conv) {
                                return (
                                    <div
                                        key={conv.id}
                                        className="chat-item"
                                        onClick={function () {
                                            openConversation(conv);
                                        }}
                                    >
                                        <div className="chat-item-avatar">
                                            <span style={aiAvatarListStyle}>💬</span>
                                        </div>
                                        <div className="chat-item-body">
                                            <div className="chat-item-header">
                                                <span className="chat-item-name">
                                                    {conv.title || AntLocalization.translateText({ vi: 'Hội thoại', en: 'Chat', fr: 'Discussion', es: 'Chat', id: 'Percakapan', ja: '会話', zh: '对话', ko: '대화', hi: 'वार्तालाप', ar: 'محادثة' })}
                                                </span>
                                                {conv.last_message_at ? (
                                                    <span className="chat-item-time">{commentFormatTime(conv.last_message_at)}</span>
                                                ) : null}
                                            </div>
                                            <div className="chat-item-preview">{conv.last_message_content || ''}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </Fragment>
                    )}
                </div>
            </div>
        );
    }

    // ═══ CHAT DETAIL VIEW (giống Chat detail) ═══════════════════
    return (
        <div className={'chat-panel' + (dragOver ? ' drag-over' : '')} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            {confirm.el}
            {dragOver && (
                <div className="chat-drop-overlay">{AntLocalization.translateText({ vi: 'Thả file để đính kèm', en: 'Drop file to attach', fr: 'Déposez pour joindre', es: 'Suelte para adjuntar', id: 'Lepas untuk melampirkan', ja: 'ドロップして添付', zh: '拖放文件以附加', ko: '파일을 놓아 첨부', hi: 'संलग्न हेतु फ़ाइल छोड़ें', ar: 'أفلت الملف للإرفاق' })}</div>
            )}

            {/* Header */}
            <div className="chat-detail-header">
                {isGlobalMode && (
                    <button className="chat-back-btn" onClick={backToList}>
                        {backIcon}
                    </button>
                )}
                <div className="chat-detail-info">
                    <div className="chat-detail-name">
                        {editConvName ||
                            (activePromptDef
                                ? AntLocalization.translateText(activePromptDef.title)
                                : AntLocalization.translateText({ vi: 'Trợ lý AI', en: 'AI Assistant', fr: 'Assistant IA', es: 'Asistente IA', id: 'Asisten AI', ja: 'AIアシスタント', zh: 'AI助手', ko: 'AI 어시스턴트', hi: 'AI सहायक', ar: 'مساعد AI' }))}
                    </div>
                    <div className="chat-detail-members">
                        {activePromptDef
                            ? activePromptDef.icon + ' ' + activePromptDef.module
                            : AntLocalization.translateText({ vi: 'Trợ lý AI', en: 'AI Assistant', fr: 'Assistant IA', es: 'Asistente IA', id: 'Asisten AI', ja: 'AIアシスタント', zh: 'AI助手', ko: 'AI 어시스턴트', hi: 'AI सहायक', ar: 'مساعد AI' })}
                    </div>
                </div>
                <button
                    className="chat-settings-icon-btn"
                    onClick={function () {
                        setView('settings');
                        loadAiDocs();
                    }}
                    title={AntLocalization.translateText({ vi: 'Cấu hình', en: 'Settings', fr: 'Paramètres', id: 'Pengaturan', ja: '設定', zh: '设置', ko: '설정', hi: 'सेटिंग्स', ar: 'الإعدادات', es: 'Ajustes' })}
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <circle cx="12" cy="12" r="3" />
                    </svg>
                </button>
            </div>

            {/* Messages (giống ChatPanel message rendering) */}
            <div className="chat-messages" ref={listRef} onScroll={handleMsgScroll}>
                {messages.length === 0 && !isTyping && (
                    <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                            {context && context.voucherCode
                                ? AntLocalization.translateText({ vi: 'Hỏi bất kỳ điều gì về phiếu này.', en: 'Ask anything about this document.', fr: 'Posez vos questions sur ce document.', es: 'Pregunte sobre este documento.', id: 'Tanyakan apa saja tentang dokumen ini.', ja: 'この伝票について何でも質問できます。', zh: '关于此单据，尽管提问。', ko: '이 문서에 대해 무엇이든 물어보세요.', hi: 'इस दस्तावेज़ के बारे में कुछ भी पूछें।', ar: 'اسأل أي شيء عن هذا المستند.' })
                                : AntLocalization.translateText({
                                      vi: 'Hỏi về quy trình, dữ liệu, hoặc hướng dẫn sử dụng.',
                                      en: 'Ask about workflows, data, or how to use the system.',
                                      fr: 'Questions sur les processus, les données ou l\'utilisation.',
                                      es: 'Pregunte sobre procesos, datos o uso del sistema.',
                                      id: 'Tanyakan tentang alur kerja, data, atau cara penggunaan.',
                                      ja: '業務フロー、データ、使い方について質問できます。',
                                      zh: '可询问流程、数据或使用方法。',
                                      ko: '업무 절차, 데이터, 사용법을 물어보세요.',
                                      hi: 'प्रक्रिया, डेटा या उपयोग के बारे में पूछें।',
                                      ar: 'اسأل عن الإجراءات أو البيانات أو طريقة الاستخدام.'
                                  })}
                        </div>
                    </div>
                )}
                {(function () {
                    if (messages.length > 0)
                        console.log(
                            '[FE-AI] render: ' +
                                messages.length +
                                ' msgs, streaming=' +
                                isStreaming +
                                ', typing=' +
                                isTyping +
                                ', streamText=' +
                                (streamingText || '').substring(0, 20)
                        );
                })()}
                {messages.map(function (m, mi) {
                    var isUser = m.role === 'user';
                    var prev = mi > 0 ? messages[mi - 1] : null;
                    var sameRoleAsPrev = prev && prev.role === m.role;
                    var showHeader = !sameRoleAsPrev;
                    var userName = isUser
                        ? m.created_user_name || m.user_name || AntLocalization.translateText({ vi: 'Bạn', en: 'You', fr: 'Vous', es: 'Usted', id: 'Anda', ja: '自分', zh: '您', ko: '나', hi: 'आप', ar: 'أنت' })
                        : AntLocalization.translateText({ vi: 'Trợ lý AI', en: 'AI Assistant', fr: 'Assistant IA', es: 'Asistente IA', id: 'Asisten AI', ja: 'AIアシスタント', zh: 'AI助手', ko: 'AI 어시스턴트', hi: 'AI सहायक', ar: 'مساعد AI' });

                    // File display (giống ChatPanel)
                    var hasMeddoc = m.meddoc_id && m.meddoc_name;
                    var fileUrl = m.file_full_path || m.file_short_path;
                    var isImage = hasMeddoc && fileUrl && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(m.meddoc_name);
                    var fileExt = hasMeddoc ? (m.meddoc_name || '').split('.').pop().toLowerCase() : '';
                    var fileLabel =
                        fileExt === 'pdf'
                            ? 'PDF'
                            : fileExt === 'doc' || fileExt === 'docx'
                            ? 'DOC'
                            : fileExt === 'xls' || fileExt === 'xlsx'
                            ? 'XLS'
                            : fileExt.toUpperCase();

                    return (
                        <div key={m.id || mi} className={'chat-msg' + (sameRoleAsPrev ? ' chat-msg-grouped' : '')}>
                            {showHeader && !isUser ? (
                                <div className="chat-msg-avatar">
                                    <span style={aiAvatarStyle}>AI</span>
                                </div>
                            ) : showHeader && isUser ? (
                                <div className="chat-msg-avatar">{getInitial(userName)}</div>
                            ) : (
                                <div className="chat-msg-avatar-spacer" />
                            )}
                            <div className="chat-msg-body">
                                {showHeader && (
                                    <div className="chat-msg-meta">
                                        <span className="chat-msg-author">{userName}</span>
                                        {m.created_at && <span className="chat-msg-time">{commentFormatTime(m.created_at)}</span>}
                                    </div>
                                )}
                                {m.content_type !== 'file' && m.content && <MarkdownText text={m.content} />}
                                {m.content_data &&
                                    (function () {
                                        try {
                                            var blocks = typeof m.content_data === 'string' ? JSON.parse(m.content_data) : m.content_data;
                                            if (!blocks) return null;
                                            if (!(blocks instanceof Array)) blocks = [blocks];
                                            return blocks.map(function (block, bi) {
                                                return <MessageBlock key={bi} block={block} onAction={handleAction} onNavigate={handleNavigate} />;
                                            });
                                        } catch (e) {
                                            return null;
                                        }
                                    })()}
                                {hasMeddoc && fileUrl && (
                                    <div className="chat-msg-attachments">
                                        {isImage ? (
                                            <img
                                                className="chat-att-image"
                                                src={fileUrl}
                                                alt={m.meddoc_name || ''}
                                                onClick={function () {
                                                    openFileViewer(m);
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="att-file-card"
                                                onClick={function () {
                                                    openFileViewer(m);
                                                }}
                                                title={m.meddoc_name}
                                            >
                                                <div className="att-file-card-icon">
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="1.2"
                                                    >
                                                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                                        <polyline points="14 2 14 8 20 8" />
                                                    </svg>
                                                    <span className="att-file-card-ext">{fileLabel}</span>
                                                </div>
                                                <div className="att-file-card-name">{m.meddoc_name}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {hasMeddoc && !fileUrl && (
                                    <div className="chat-msg-attachments">
                                        <span className="chat-att-deleted">
                                            📎 {m.meddoc_name} <em>({AntLocalization.translateText({ vi: 'đã xoá', en: 'deleted', fr: 'supprimé', es: 'eliminado', id: 'dihapus', ja: '削除済み', zh: '已删除', ko: '삭제됨', hi: 'हटाया गया', ar: 'محذوف' })})</em>
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                {isStreaming && streamingText && (
                    <div className="chat-msg">
                        <div className="chat-msg-avatar">
                            <span style={aiAvatarStyle}>AI</span>
                        </div>
                        <div className="chat-msg-body">
                            <MarkdownText text={streamingText} />
                        </div>
                    </div>
                )}
                {isTyping && !isStreaming && (
                    <div className="chat-typing-indicator">
                        <span className="chat-typing-dot" />
                        <span className="chat-typing-dot" />
                        <span className="chat-typing-dot" />
                        <span className="chat-typing-label">
                            {AntLocalization.translateText({ vi: 'AI đang trả lời...', en: 'AI is typing...', fr: 'L\'IA répond...', es: 'La IA responde...', id: 'AI sedang menjawab...', ja: 'AIが回答中...', zh: 'AI正在回复...', ko: 'AI가 답변 중...', hi: 'AI उत्तर दे रहा है...', ar: 'AI يكتب الرد...' })}
                        </span>
                    </div>
                )}
            </div>

            {/* Input area */}
            <div className="chat-input-area">
                {messages.length === 0 &&
                    !inputText &&
                    pendingFiles.length === 0 &&
                    (function () {
                        var tips =
                            activePromptDef && activePromptDef.suggestions
                                ? activePromptDef.suggestions
                                : suggestedPrompts.map(function (s) {
                                      return typeof s === 'string' ? { vi: s, en: s } : s;
                                  });
                        return (
                            <div className="chat-ai-suggestions">
                                <span className="chat-ai-label">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                    {AntLocalization.translateText({ vi: 'Gợi ý', en: 'Suggestions', fr: 'Suggestions', es: 'Sugerencias', id: 'Saran', ja: '提案', zh: '建议', ko: '제안', hi: 'सुझाव', ar: 'اقتراحات' })}
                                </span>
                                {tips.map(function (tip, ti) {
                                    var text = typeof tip === 'string' ? tip : AntLocalization.translateText(tip);
                                    return (
                                        <button
                                            key={ti}
                                            className="chat-ai-chip"
                                            onClick={function () {
                                                sendText(text);
                                            }}
                                        >
                                            {text}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })()}
                {/* File preview */}
                {pendingFiles.length > 0 && (
                    <div className="chat-file-preview-list">
                        {pendingFiles.map(function (f, fi) {
                            return (
                                <div key={fi} className="chat-file-preview">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                    <span className="file-preview-name">{f.name}</span>
                                    <button
                                        className="file-preview-close"
                                        onClick={function () {
                                            removePendingFile(fi);
                                        }}
                                    >
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="chat-input-toolbar">
                    <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
                    <button
                        className="chat-tool-btn"
                        onClick={function () {
                            if (fileInputRef.current) fileInputRef.current.click();
                        }}
                        title={AntLocalization.translateText({ vi: 'Đính kèm', en: 'Attach', fr: 'Joindre', es: 'Adjuntar', id: 'Lampirkan', ja: '添付', zh: '附加', ko: '첨부', hi: 'संलग्न करें', ar: 'إرفاق' })}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                    </button>
                    <span style={{ flex: 1 }} />
                    <button
                        className={'chat-tool-btn chat-send-btn' + (inputText.trim() || pendingFiles.length > 0 ? ' active' : '')}
                        onClick={handleSend}
                        disabled={(!inputText.trim() && pendingFiles.length === 0) || isTyping || sending}
                        title={AntLocalization.translateText({ vi: 'Gửi', en: 'Send', fr: 'Envoyer', es: 'Enviar', id: 'Kirim', ja: '送信', zh: '发送', ko: '보내기', hi: 'भेजें', ar: 'إرسال' })}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                        </svg>
                    </button>
                </div>
                <div className="chat-input-row">
                    <textarea
                        ref={textareaRef}
                        className="chat-input"
                        value={inputText}
                        onChange={function (e) {
                            setInputText(e.target.value);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder={AntLocalization.translateText({ vi: 'Nhập tin nhắn...', en: 'Type a message...', fr: 'Entrez un message...', es: 'Ingrese mensaje...', id: 'Masukkan pesan...', ja: 'メッセージを入力...', zh: '输入消息...', ko: '메시지 입력...', hi: 'संदेश दर्ज करें...', ar: 'أدخل رسالة...' })}
                        rows={2}
                        disabled={isTyping || sending}
                    />
                </div>
            </div>
            {aiViewerState && (
                <AntFileViewer
                    visible
                    files={aiViewerState.files}
                    initialIndex={aiViewerState.index}
                    onClose={function () {
                        setAiViewerState(null);
                    }}
                />
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── NOTIFICATION PANEL ─────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var NOTIFY_PAGE_SIZE = 20;

var NOTIFY_TYPE_CONFIG = {
    Comment: { icon: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z', color: '#2563eb' },
    APCredit_OverdueAlert: {
        icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
        color: '#dc2626'
    },
    // Việc ghi sổ nền của Kế toán (TQ-05): có phiếu lỗi → cảnh báo đỏ; xong không lỗi → biểu tượng mặc định.
    GLJournal_JobAlert: {
        icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
        color: '#dc2626'
    },
    GLJournal_JobDone: {
        icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
        color: '#64748b'
    },
    OFTask_Alert: {
        icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
        color: '#3182ce'
    },
    default: {
        icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
        color: '#64748b'
    }
};

function notifyFormatTime(ts) {
    return sidebarRelativeTime(ts);
}

var NOTIFY_FILTERS = [
    { key: 'all', label: { vi: 'Tất cả', en: 'All', fr: 'Tous', es: 'Todos', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' } },
    { key: 'Comment', label: { vi: 'Bình luận', en: 'Comments', fr: 'Commentaires', es: 'Comentarios', id: 'Komentar', ja: 'コメント', zh: '评论', ko: '댓글', hi: 'टिप्पणियाँ', ar: 'التعليقات' } },
    { key: 'Approval', visible: { whenSys: { use_Module_Approval: 1 } }, label: { vi: 'Phê duyệt', en: 'Approval', fr: 'Approbation', es: 'Aprobación', id: 'Persetujuan', ja: '承認', zh: '审批', ko: '승인', hi: 'अनुमोदन', ar: 'الموافقة' } },
    { key: 'Alert', label: { vi: 'Cảnh báo', en: 'Alert', fr: 'Alerte', es: 'Alerta', id: 'Peringatan', ja: 'アラート', zh: '警报', ko: '경고', hi: 'चेतावनी', ar: 'تنبيه' } }
];

function groupNotificationsByTime(notifications) {
    var now = Date.now();
    var todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    var yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    var weekStart = new Date(todayStart);
    weekStart.setDate(weekStart.getDate() - 7);

    var groups = { today: [], yesterday: [], week: [], older: [] };
    notifications.forEach(function (n) {
        var ts = n.created_at < 1e12 ? n.created_at * 1000 : n.created_at;
        if (ts >= todayStart.getTime()) groups.today.push(n);
        else if (ts >= yesterdayStart.getTime()) groups.yesterday.push(n);
        else if (ts >= weekStart.getTime()) groups.week.push(n);
        else groups.older.push(n);
    });
    return groups;
}

function NotificationPanel(props) {
    var onNavigate = props.onNavigate;
    var refreshKey = props.refreshKey || 0;

    var [notifications, setNotifications] = useState([]);
    var [loading, setLoading] = useState(false);
    var [filter, setFilter] = useState('all');

    var notifyAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserNotification');
    }, []);

    var loadNotifications = useCallback(function () {
        setLoading(true);
        notifyAPI.post(
            '/' + notifyAPI.routeURL + '/list',
            {
                pageSize: 50
            },
            function (result) {
                var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                setNotifications(rows instanceof Array ? rows : []);
                setLoading(false);
            }
        );
    }, []);

    // Reload khi mount + khi refreshKey thay đổi (socket NewComment/notification)
    useEffect(
        function () {
            loadNotifications();
        },
        [refreshKey]
    );

    var handleMarkViewed = useCallback(function (id) {
        notifyAPI.post('/' + notifyAPI.routeURL + '/view', { id: id }, function () {
            setNotifications(function (prev) {
                return prev.map(function (n) {
                    return n.id === id ? Object.assign({}, n, { viewed: 1 }) : n;
                });
            });
        });
    }, []);

    var handleMarkAllViewed = useCallback(
        function () {
            notifications
                .filter(function (n) {
                    return !n.viewed;
                })
                .forEach(function (n) {
                    notifyAPI.post('/' + notifyAPI.routeURL + '/view', { id: n.id }, function () {});
                });
            setNotifications(function (prev) {
                return prev.map(function (n) {
                    return Object.assign({}, n, { viewed: 1 });
                });
            });
        },
        [notifications]
    );

    var handleDelete = useCallback(function (n, e) {
        e.stopPropagation();
        notifyAPI.post('/' + notifyAPI.routeURL + '/delete', { id: n.id }, function () {
            setNotifications(function (prev) {
                return prev.filter(function (x) {
                    return x.id !== n.id;
                });
            });
        });
    }, []);

    var handleClearRead = useCallback(
        function () {
            var readItems = notifications.filter(function (n) {
                return n.viewed;
            });
            readItems.forEach(function (n) {
                notifyAPI.post('/' + notifyAPI.routeURL + '/delete', { id: n.id }, function () {});
            });
            setNotifications(function (prev) {
                return prev.filter(function (n) {
                    return !n.viewed;
                });
            });
        },
        [notifications]
    );

    var handleClick = useCallback(
        function (n) {
            if (!n.viewed) handleMarkViewed(n.id);
            if (n.object_type && n.object_value && onNavigate) {
                onNavigate(n.object_type, n.object_value, n.notify_type);
            }
        },
        [onNavigate, handleMarkViewed]
    );

    // Filter
    var filtered = useMemo(
        function () {
            if (filter === 'all') return notifications;
            return notifications.filter(function (n) {
                return n.notify_type && n.notify_type.indexOf(filter) >= 0;
            });
        },
        [notifications, filter]
    );

    var groups = useMemo(
        function () {
            return groupNotificationsByTime(filtered);
        },
        [filtered]
    );
    var unreadCount = notifications.filter(function (n) {
        return !n.viewed;
    }).length;

    // Render group
    var renderGroup = function (label, items) {
        if (!items || items.length === 0) return null;
        return (
            <>
                <div className="notification-group-label">{label}</div>
                {items.map(function (n) {
                    var typeCfg = NOTIFY_TYPE_CONFIG[n.notify_type] || NOTIFY_TYPE_CONFIG.default;
                    var isUnread = !n.viewed;
                    return (
                        <div
                            key={n.id}
                            className={'notification-item' + (isUnread ? ' unread' : '')}
                            onClick={function () {
                                handleClick(n);
                            }}
                        >
                            <div className="notification-icon" style={{ color: typeCfg.color }}>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d={typeCfg.icon} />
                                </svg>
                            </div>
                            <div className="notification-body">
                                <div className="notification-title">{AntLocalization.translateText(n.notify_title)}</div>
                                {n.notify_content && <div className="notification-content">{AntLocalization.translateText(n.notify_content)}</div>}
                                <div className="notification-time">{notifyFormatTime(n.created_at)}</div>
                            </div>
                            <div className="notification-item-actions">
                                {isUnread && <div className="notification-dot" />}
                                <button
                                    className="notification-delete-btn"
                                    onClick={function (e) {
                                        handleDelete(n, e);
                                    }}
                                    title={AntLocalization.translateText({ vi: 'Xóa', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएँ', ar: 'حذف' })}
                                >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </>
        );
    };

    return (
        <div className="notification-panel">
            {/* Header */}
            <div className="notification-header">
                <div className="notification-header-left">
                    {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
                    <span className="notification-header-text">
                        {unreadCount > 0
                            ? AntLocalization.translateText({ vi: 'chưa đọc', en: 'unread', fr: 'non lues', es: 'sin leer', id: 'belum dibaca', ja: '未読', zh: '未读', ko: '읽지 않음', hi: 'अपठित', ar: 'غير مقروءة' })
                            : AntLocalization.translateText({ vi: 'Tất cả đã đọc', en: 'All read', fr: 'Tout est lu', es: 'Todo leído', id: 'Semua dibaca', ja: 'すべて既読', zh: '全部已读', ko: '모두 읽음', hi: 'सभी पढ़े गए', ar: 'تمت قراءة الكل' })}
                    </span>
                </div>
                <div className="notification-header-actions">
                    {unreadCount > 0 && (
                        <button
                            className="notification-action-btn"
                            onClick={handleMarkAllViewed}
                            title={AntLocalization.translateText({ vi: 'Đánh dấu tất cả đã đọc', en: 'Mark all read', fr: 'Tout marquer lu', es: 'Marcar todo leído', id: 'Tandai semua dibaca', ja: 'すべて既読にする', zh: '全部标为已读', ko: '모두 읽음으로 표시', hi: 'सभी पढ़ा चिह्नित करें', ar: 'تعليم الكل كمقروء' })}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                        </button>
                    )}
                    {notifications.some(function (n) {
                        return n.viewed;
                    }) && (
                        <button
                            className="notification-action-btn"
                            onClick={handleClearRead}
                            title={AntLocalization.translateText({ vi: 'Xóa đã đọc', en: 'Clear read', fr: 'Effacer lues', es: 'Borrar leídas', id: 'Hapus yang dibaca', ja: '既読を削除', zh: '清除已读', ko: '읽은 항목 삭제', hi: 'पढ़े गए हटाएँ', ar: 'حذف المقروءة' })}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    )}
                    <button
                        className="notification-action-btn"
                        onClick={loadNotifications}
                        title={AntLocalization.translateText({ vi: 'Làm mới', en: 'Refresh', fr: 'Actualiser', es: 'Actualizar', id: 'Muat ulang', ja: '更新', zh: '刷新', ko: '새로고침', hi: 'रीफ़्रेश करें', ar: 'تحديث' })}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Filter chips */}
            <div className="rs-chips notification-filters" role="tablist">
                {NOTIFY_FILTERS.filter(function (f) {
                    return !f.visible || AntCondition.isVisible({}, f);
                }).map(function (f) {
                    return (
                        <button
                            key={f.key}
                            type="button"
                            role="tab"
                            aria-selected={filter === f.key}
                            className={'rs-chip' + (filter === f.key ? ' active' : '')}
                            onClick={function () {
                                setFilter(f.key);
                            }}
                        >
                            {AntLocalization.translateText(f.label)}
                        </button>
                    );
                })}
            </div>

            {/* Grouped list */}
            <div className="notification-list">
                {loading && <SidebarState type="loading" />}
                {!loading && filtered.length === 0 && (
                    <SidebarState type="empty" icon="notification" text={{ vi: 'Không có thông báo', en: 'No notifications', fr: 'Aucune notification', es: 'Sin notificaciones', id: 'Tidak ada notifikasi', ja: '通知はありません', zh: '暂无通知', ko: '알림이 없습니다', hi: 'कोई सूचना नहीं', ar: 'لا توجد إشعارات' }} />
                )}
                {renderGroup(AntLocalization.translateText({ vi: 'Hôm nay', en: 'Today', fr: 'Aujourd\'hui', es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم' }), groups.today)}
                {renderGroup(AntLocalization.translateText({ vi: 'Hôm qua', en: 'Yesterday', fr: 'Hier', es: 'Ayer', id: 'Kemarin', ja: '昨日', zh: '昨天', ko: '어제', hi: 'कल', ar: 'أمس' }), groups.yesterday)}
                {renderGroup(AntLocalization.translateText({ vi: 'Tuần này', en: 'This week', fr: 'Cette semaine', es: 'Esta semana', id: 'Minggu ini', ja: '今週', zh: '本周', ko: '이번 주', hi: 'इस सप्ताह', ar: 'هذا الأسبوع' }), groups.week)}
                {renderGroup(AntLocalization.translateText({ vi: 'Cũ hơn', en: 'Older', fr: 'Plus ancien', es: 'Anteriores', id: 'Lebih lama', ja: 'それ以前', zh: '更早', ko: '이전', hi: 'पुराने', ar: 'أقدم' }), groups.older)}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── INSIGHT PANEL (system notifications) ───────────────────────
// ═══════════════════════════════════════════════════════════════════

var MOCK_INSIGHTS = [
    {
        id: 1,
        severity: 'warning',
        title: { vi: 'Công nợ quá hạn', en: 'Overdue receivables' },
        description: {
            vi: '3 khách hàng có công nợ quá hạn trên 30 ngày, tổng 45,200,000 đ.',
            en: '3 customers with receivables overdue more than 30 days, total 45,200,000 VND.'
        },
        link: '/ar/ARCredits',
        linkText: { vi: 'Xem danh sách', en: 'View list' }
    },
    {
        id: 2,
        severity: 'alert',
        title: { vi: 'Tồn kho thấp', en: 'Low inventory' },
        description: { vi: '7 sản phẩm có tồn kho dưới mức tối thiểu tại kho chính.', en: '7 products below minimum stock level at main warehouse.' },
        link: '/inv/ICInventories',
        linkText: { vi: 'Kiểm tra kho', en: 'Check inventory' }
    },
    {
        id: 3,
        severity: 'suggestion',
        title: { vi: 'Đơn hàng chờ duyệt', en: 'Pending orders' },
        description: {
            vi: '12 đơn hàng ở trạng thái New quá 2 ngày chưa được duyệt.',
            en: '12 orders in New status for more than 2 days without approval.'
        },
        link: '/ar/AROrders',
        linkText: { vi: 'Xem đơn hàng', en: 'View orders' }
    },
    {
        id: 4,
        severity: 'info',
        title: { vi: 'Doanh thu tháng này', en: 'This month revenue' },
        description: {
            vi: 'Doanh thu tháng 3 đạt 78% so với mục tiêu. Cần tăng tốc trong 10 ngày còn lại.',
            en: 'March revenue reached 78% of target. Need to accelerate in remaining 10 days.'
        },
        link: null,
        linkText: null
    },
    {
        id: 5,
        severity: 'suggestion',
        title: { vi: 'Nhà cung cấp mới', en: 'New supplier' },
        description: {
            vi: 'Có 2 đề xuất mua hàng từ nhà cung cấp chưa được phê duyệt.',
            en: '2 purchase proposals from suppliers pending approval.'
        },
        link: '/ap/APOrders',
        linkText: { vi: 'Xem đề xuất', en: 'View proposals' }
    }
];

var SEVERITY_CONFIG = {
    alert: { label: { vi: 'Cảnh báo', en: 'Alert', fr: 'Alerte', es: 'Alerta', id: 'Peringatan', ja: 'アラート', zh: '警报', ko: '경고', hi: 'चेतावनी', ar: 'تنبيه' }, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
    warning: { label: { vi: 'Lưu ý', en: 'Warning', fr: 'Remarque', es: 'Nota', id: 'Catatan', ja: '注意', zh: '注意', ko: '참고', hi: 'ध्यान दें', ar: 'ملاحظة' }, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
    suggestion: { label: { vi: 'Đề xuất', en: 'Suggestion', fr: 'Suggestion', es: 'Sugerencia', id: 'Saran', ja: '提案', zh: '建议', ko: '제안', hi: 'सुझाव', ar: 'اقتراح' }, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
    info: { label: { vi: 'Thông tin', en: 'Info', fr: 'Info', es: 'Info', id: 'Info', ja: '情報', zh: '信息', ko: '정보', hi: 'जानकारी', ar: 'معلومة' }, color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' }
};

function InsightPanel(props) {
    var mockMode = props.mockMode;
    var onNavigate = props.onNavigate;

    var [insights, setInsights] = useState(mockMode ? MOCK_INSIGHTS : []);
    var [loading, setLoading] = useState(false);
    var [dismissedIds, setDismissedIds] = useState([]);

    var notifyAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserNotification');
    }, []);

    // Load real insights từ API
    useEffect(
        function () {
            if (mockMode) return;
            setLoading(true);
            notifyAPI.post('/' + notifyAPI.routeURL + '/getInsights', {}, function (result) {
                var rows = (result && result.rows) || [];
                setInsights(rows instanceof Array ? rows : []);
                setLoading(false);
            });
        },
        [mockMode]
    );

    var handleDismiss = useCallback(function (id) {
        setDismissedIds(function (prev) {
            return prev.concat([id]);
        });
    }, []);

    var handleNavigate = useCallback(
        function (link) {
            if (link && onNavigate) onNavigate(link);
        },
        [onNavigate]
    );

    var visibleInsights = insights.filter(function (item) {
        return dismissedIds.indexOf(item.id) < 0;
    });

    return (
        <div className="insight-panel">
            <div className="insight-header">
                <span className="insight-header-text">{AntLocalization.translateText({ vi: 'Hệ thống phát hiện', en: 'System detected', fr: 'Détections système', es: 'Detectado por sistema', id: 'Temuan sistem', ja: 'システム検知', zh: '系统发现', ko: '시스템 감지', hi: 'सिस्टम ने पाया', ar: 'رصد النظام' })}</span>
                <span className="insight-count">{visibleInsights.length}</span>
            </div>

            <div className="insight-list">
                {loading && <div className="insight-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読込中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोडिंग...', ar: 'تحميل...' })}</div>}
                {!loading && visibleInsights.length === 0 && (
                    <div className="insight-empty">{AntLocalization.translateText({ vi: 'Không có thông báo nào', en: 'No notifications', fr: 'Aucune notification', es: 'Sin notificaciones', id: 'Tidak ada notifikasi', ja: '通知はありません', zh: '暂无通知', ko: '알림 없음', hi: 'कोई सूचना नहीं', ar: 'لا توجد إشعارات' })}</div>
                )}
                {visibleInsights.map(function (item) {
                    var cfg = SEVERITY_CONFIG[item.severity] || SEVERITY_CONFIG.info;
                    var titleText = typeof item.title === 'object' ? AntLocalization.translateText(item.title) : item.title;
                    var descText = typeof item.description === 'object' ? AntLocalization.translateText(item.description) : item.description;
                    var linkLabel = item.linkText
                        ? typeof item.linkText === 'object'
                            ? AntLocalization.translateText(item.linkText)
                            : item.linkText
                        : null;

                    return (
                        <div key={item.id} className="insight-card" style={{ borderLeftColor: cfg.color }}>
                            <div className="insight-card-header">
                                <span className="insight-severity" style={{ color: cfg.color }}>
                                    {AntLocalization.translateText(cfg.label)}
                                </span>
                                <button
                                    className="insight-dismiss"
                                    onClick={function () {
                                        handleDismiss(item.id);
                                    }}
                                >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="insight-card-title">{titleText}</div>
                            <div className="insight-card-desc">{descText}</div>
                            {item.link && linkLabel && (
                                <button
                                    className="insight-card-link"
                                    onClick={function () {
                                        handleNavigate(item.link);
                                    }}
                                >
                                    {linkLabel}
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── NOTES PANEL ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var NOTE_COLORS = [
    { key: 'white', bg: '#fff', border: '#dce5f2' },
    { key: 'yellow', bg: '#fefce8', border: '#fde68a' },
    { key: 'blue', bg: '#eff6ff', border: '#bfdbfe' },
    { key: 'green', bg: '#f0fdf4', border: '#bbf7d0' },
    { key: 'pink', bg: '#fdf2f8', border: '#fbcfe8' },
    { key: 'purple', bg: '#faf5ff', border: '#e9d5ff' }
];

var VISIBILITY_OPTIONS = [
    { key: 'private', label: { vi: 'Riêng tư', en: 'Private', fr: 'Privé', es: 'Privado', id: 'Pribadi', ja: '非公開', zh: '私密', ko: '비공개', hi: 'निजी', ar: 'خاص' } },
    { key: 'store', label: { vi: 'Cửa hàng', en: 'Store', fr: 'Magasin', es: 'Tienda', id: 'Toko', ja: '店舗', zh: '门店', ko: '매장', hi: 'स्टोर', ar: 'المتجر' } },
    { key: 'department', label: { vi: 'Phòng ban', en: 'Department', fr: 'Service', es: 'Departamento', id: 'Departemen', ja: '部署', zh: '部门', ko: '부서', hi: 'विभाग', ar: 'القسم' } },
    { key: 'group', label: { vi: 'Nhóm', en: 'Group', fr: 'Équipe', es: 'Equipo', id: 'Tim', ja: 'チーム', zh: '团队', ko: '팀', hi: 'टीम', ar: 'الفريق' } },
    { key: 'public', label: { vi: 'Toàn công ty', en: 'Company', fr: 'Entreprise', es: 'Empresa', id: 'Perusahaan', ja: '全社', zh: '全公司', ko: '전사', hi: 'पूरी कंपनी', ar: 'الشركة كلها' } }
];

function NoteEditorPopup(props) {
    var [content, setContent] = useState(props.initialContent || '');
    var overlayRef = useRef(null);

    var handleOverlayClick = useCallback(
        function (e) {
            // Chỉ đóng khi click ĐÚNG overlay (không phải dropdown/popover bên ngoài modal)
            if (e.target === overlayRef.current) props.onClose();
        },
        [props.onClose]
    );

    return ReactDOM.createPortal(
        <div className="notes-editor-overlay" ref={overlayRef} onMouseDown={handleOverlayClick}>
            <div
                className="notes-editor-modal"
                onMouseDown={function (e) {
                    e.stopPropagation();
                }}
            >
                <div className="notes-editor-modal-header">
                    <span className="notes-editor-modal-title">
                        {props.title || AntLocalization.translateText({ vi: 'Chỉnh sửa nội dung', en: 'Edit content', fr: 'Modifier le contenu', es: 'Editar contenido', id: 'Ubah isi', ja: '内容を編集', zh: '编辑内容', ko: '내용 편집', hi: 'सामग्री संपादन', ar: 'تعديل المحتوى' })}
                    </span>
                    <button
                        className="chat-settings-save-btn"
                        onClick={function () {
                            props.onSave(content);
                        }}
                        title={AntLocalization.translateText({ vi: 'Lưu & Đóng', en: 'Save & Close', fr: 'Enreg. et fermer', es: 'Guardar y cerrar', id: 'Simpan & tutup', ja: '保存して閉じる', zh: '保存并关闭', ko: '저장 후 닫기', hi: 'सहेजें व बंद', ar: 'حفظ وإغلاق' })}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 13l4 4L19 7" />
                        </svg>
                    </button>
                    <button
                        className="notes-editor-modal-close"
                        onClick={function () {
                            props.onClose();
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <HtmlEditor
                    className="notes-html-editor-full"
                    defaultValue={content}
                    onValueChanged={function (e) {
                        setContent(e.value);
                    }}
                    placeholder={AntLocalization.translateText({ vi: 'Nội dung ghi chú...', en: 'Note content...', fr: 'Contenu de la note...', es: 'Contenido de la nota...', id: 'Isi catatan...', ja: 'メモの内容...', zh: '笔记内容...', ko: '노트 내용...', hi: 'नोट की सामग्री...', ar: 'محتوى الملاحظة...' })}
                    height="100%"
                >
                    <Toolbar>
                        <Item name="header" acceptedValues={[1, 2, 3, false]} />
                        <Item name="separator" />
                        <Item name="bold" />
                        <Item name="italic" />
                        <Item name="underline" />
                        <Item name="strike" />
                        <Item name="separator" />
                        <Item name="color" />
                        <Item name="background" />
                        <Item name="separator" />
                        <Item name="orderedList" />
                        <Item name="bulletList" />
                        <Item name="separator" />
                        <Item name="alignLeft" />
                        <Item name="alignCenter" />
                        <Item name="alignRight" />
                        <Item name="separator" />
                        <Item name="increaseIndent" />
                        <Item name="decreaseIndent" />
                        <Item name="separator" />
                        <Item name="clear" />
                    </Toolbar>
                </HtmlEditor>
            </div>
        </div>,
        document.body
    );
}

function NotesPanel() {
    var noteAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserNote');
    }, []);
    var docAPI = useMemo(function () {
        return new AntAPI('media', 'MEDDocument');
    }, []);
    var junctionAPI = useMemo(function () {
        return new AntAPI('media', 'MEDDocumentVoucher');
    }, []);

    var [noteView, setNoteView] = useState('list'); // 'list' | 'detail' | 'settings'
    var [notes, setNotes] = useState([]);
    var [loading, setLoading] = useState(false);
    var [editingNote, setEditingNote] = useState(null);
    var [isEditing, setIsEditing] = useState(false); // false = view mode, true = edit mode
    var [saving, setSaving] = useState(false);
    var [noteFilter, setNoteFilter] = useState('all'); // all | mine | shared
    // Attachment state
    var [noteDocs, setNoteDocs] = useState([]);
    var [noteViewerState, setNoteViewerState] = useState(null);
    var [showEditorPopup, setShowEditorPopup] = useState(false);
    var editorContentRef = useRef('');
    var [noteSearchText, setNoteSearchText] = useState('');
    var [docsLoading, setDocsLoading] = useState(false);
    var [showAttachments, setShowAttachments] = useState(true);
    var attFileRef = useRef(null);

    var currentUserCode = useMemo(function () {
        var session = AntSession ? AntSession() : null;
        if (session && session.user_code) return session.user_code;
        // Fallback: tìm trong localStorage
        try {
            var keys = Object.keys(localStorage);
            for (var i = 0; i < keys.length; i++) {
                if (keys[i].endsWith('-session-info')) {
                    var info = JSON.parse(localStorage.getItem(keys[i]));
                    if (info && info.user_code) return info.user_code;
                }
            }
        } catch (e) {
            /* silent */
        }
        return null;
    }, []);

    // Phân loại (hooks phải trước early return)
    var myNotes = useMemo(
        function () {
            return notes.filter(function (n) {
                return n.created_user_code === currentUserCode;
            });
        },
        [notes, currentUserCode]
    );
    var sharedNotes = useMemo(
        function () {
            return notes.filter(function (n) {
                return n.created_user_code !== currentUserCode;
            });
        },
        [notes, currentUserCode]
    );

    // ─── Load ───────────────────────────────────────────────────
    var loadNotes = useCallback(function () {
        setLoading(true);
        noteAPI.post(
            '/' + noteAPI.routeURL + '/list',
            {
                pageSize: 100,
                pageIndex: 0
            },
            function (result) {
                var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                if (!(rows instanceof Array)) rows = [];
                // Sort: pinned first, then by updated_at desc
                rows.sort(function (a, b) {
                    if (a.pinned && !b.pinned) return -1;
                    if (!a.pinned && b.pinned) return 1;
                    return (b.updated_at || 0) - (a.updated_at || 0);
                });
                setNotes(rows);
                setLoading(false);
            }
        );
    }, []);

    useEffect(function () {
        loadNotes();
    }, []);

    // ─── Create ─────────────────────────────────────────────────
    var handleCreate = useCallback(
        function () {
            noteAPI.post(
                '/' + noteAPI.routeURL + '/create',
                {
                    title: '',
                    content: '',
                    color: 'white',
                    visibility: 'private'
                },
                function (result) {
                    // AntAPI callback: result có thể là { data: {...} } hoặc {...} trực tiếp
                    var data = (result && result.data) || result;
                    var noteId = data && (data.id || data.code);
                    if (noteId) {
                        setEditingNote({
                            id: data.id,
                            code: data.code,
                            title: '',
                            content: '',
                            color: 'white',
                            visibility: 'private',
                            pinned: 0,
                            created_user_code: currentUserCode,
                            _isNew: true
                        });
                        setNoteView('settings');
                        setShowEditorPopup(true);
                    } else {
                        loadNotes();
                    }
                }
            );
        },
        [currentUserCode]
    );

    var [isDirty, setIsDirty] = useState(false);

    // ─── Save (chỉ khi user bấm nút Lưu) ─────────────────────
    var doSave = useCallback(
        function (note) {
            if (!note || !note.id) return;
            setSaving(true);
            noteAPI.post(
                '/' + noteAPI.routeURL + '/update',
                {
                    where: { id: note.id },
                    update: {
                        title: note.title,
                        content: note.content,
                        color: note.color,
                        pinned: note.pinned,
                        visibility: note.visibility,
                        share_store_code: note.share_store_code,
                        share_department: note.share_department,
                        share_user_codes: note.share_user_codes,
                        tags: note.tags
                    }
                },
                function () {
                    setSaving(false);
                    setIsDirty(false);
                    loadNotes();
                }
            );
        },
        [loadNotes]
    );

    var updateField = useCallback(function (field, value) {
        setEditingNote(function (prev) {
            if (!prev) return prev;
            var updated = Object.assign({}, prev);
            updated[field] = value;
            return updated;
        });
        setIsDirty(true);
    }, []);

    // ─── Confirm dialog ─────────────────────────────────────────
    var confirm = useConfirm();

    // ─── Delete ─────────────────────────────────────────────────
    var handleDelete = useCallback(
        function (note) {
            confirm.show(AntLocalization.translateText({ vi: 'Xóa ghi chú này?', en: 'Delete this note?', fr: 'Supprimer cette note ?', es: '¿Eliminar esta nota?', id: 'Hapus catatan ini?', ja: 'このノートを削除？', zh: '删除此笔记？', ko: '이 노트를 삭제할까요?', hi: 'यह नोट हटाएँ?', ar: 'حذف هذه الملاحظة؟' }), function () {
                noteAPI.post('/' + noteAPI.routeURL + '/delete', { id: note.id }, function () {
                    setEditingNote(null);
                    setNoteView('list');
                    loadNotes();
                });
            });
        },
        [confirm.show]
    );

    // ─── Toggle pin (from list) ──────────────────────────────────
    var handleTogglePin = useCallback(function (note) {
        var newPinned = note.pinned ? 0 : 1;
        noteAPI.post('/' + noteAPI.routeURL + '/update', { where: { id: note.id }, update: { pinned: newPinned } }, function () {
            loadNotes();
        });
    }, []);

    // ─── Back to list ───────────────────────────────────────────
    var doBackToList = useCallback(
        function () {
            setEditingNote(null);
            setNoteView('list');
            setNoteDocs([]);
            setIsEditing(false);
            setIsDirty(false);
            loadNotes();
        },
        [loadNotes]
    );

    var handleBack = useCallback(
        function () {
            if (isDirty) {
                confirm.show(
                    AntLocalization.translateText({ vi: 'Bạn có thay đổi chưa lưu. Bỏ thay đổi?', en: 'You have unsaved changes. Discard?', fr: 'Modifications non enregistrées. Les abandonner ?', es: 'Hay cambios sin guardar. ¿Descartarlos?', id: 'Ada perubahan yang belum disimpan. Buang?', ja: '未保存の変更があります。破棄しますか？', zh: '有未保存的更改。是否放弃？', ko: '저장하지 않은 변경 사항이 있습니다. 버릴까요?', hi: 'बिना सहेजे बदलाव हैं। छोड़ दें?', ar: 'لديك تغييرات غير محفوظة. تجاهلها؟' }),
                    function () {
                        doBackToList();
                    }
                );
            } else {
                doBackToList();
            }
        },
        [isDirty, confirm, doBackToList]
    );

    // ─── Load note docs ──────────────────────────────────────────
    // BUG FIX: trước đây gọi MEDDocument/list với voucher_id/voucher_type — 2 cột này
    // đã DROP khỏi MEDDocument (Phase 1.3) → framework strip filter → list trả về TOÀN BỘ
    // MEDDocument của business. Phải query qua junction MEDDocumentVoucher.
    var loadNoteDocs = useCallback(
        function (note) {
            var n = note || editingNote;
            if (!n || !n.id) return;
            setDocsLoading(true);
            junctionAPI.post('/' + junctionAPI.routeURL + '/list', {
                voucher_type: 'ACUserNote',
                voucher_id: n.id,
                pageSize: 50,
                pageIndex: 0
            }, function (result) {
                var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                if (!(rows instanceof Array)) rows = [];
                // Map junction fields → shape consumers expect (id, name/file_name, url/file_url/full_path, sub_type/type)
                var mapped = rows.map(function (x) {
                    return {
                        id: x.document_id,            // dùng cho key + onDelete (id MEDDocument)
                        junction_id: x.id,
                        name: x.document_name,
                        file_name: x.document_name,
                        full_path: x.document_full_path,
                        file_url: x.document_full_path,
                        url: x.document_full_path,
                        sub_type: x.document_sub_type,
                        type: x.document_type,
                        file_size: x.document_file_size || 0,
                        created_user_name: x.voucher_created_user_name,
                        created_at: x.attached_at
                    };
                });
                setNoteDocs(mapped);
                setDocsLoading(false);
            });
        },
        [editingNote, junctionAPI]
    );

    var handleAttUpload = useCallback(
        function (e) {
            if (!e.target.files || !e.target.files[0] || !editingNote || !editingNote.id) return;
            var file = e.target.files[0];
            e.target.value = '';
            // BUG FIX: trước đây gọi /user/ACUserNote/uploadDocuments — endpoint không
            // tồn tại. Phải dùng pattern chuẩn /media/MEDDocument/upload với voucher_type
            // + voucher_id để actionUploadDocuments tạo MEDDocument + junction tự động.
            var fd = new FormData();
            fd.append('files', file);
            fd.append('voucher_type', 'ACUserNote');
            fd.append('voucher_id', editingNote.id);
            if (editingNote.code) fd.append('voucher_code', editingNote.code);
            docAPI.upload('/' + docAPI.routeURL + '/upload', fd, function () {
                loadNoteDocs(editingNote);
            });
        },
        [editingNote, docAPI, loadNoteDocs]
    );

    var handleAttDelete = useCallback(
        function (doc) {
            if (!editingNote || !doc || !doc.id) return;
            // Detach junction — BE actionDetachFromVoucher tự refcount: nếu MEDDocument
            // hết junction sau detach → hard delete + cascade S3 + decrement quota.
            docAPI.post('/' + docAPI.routeURL + '/actionDetachFromVoucher', {
                document_id: doc.id,
                voucher_type: 'ACUserNote',
                voucher_id: editingNote.id,
                voucher_code: editingNote.code
            }, function () {
                loadNoteDocs(editingNote);
            });
        },
        [editingNote, docAPI, loadNoteDocs]
    );

    // ═══ DETAIL VIEW ════════════════════════════════════════════
    if (editingNote) {
        var colorDef =
            NOTE_COLORS.find(function (c) {
                return c.key === editingNote.color;
            }) || NOTE_COLORS[0];
        var isOwner = !editingNote.created_user_code || currentUserCode === editingNote.created_user_code || editingNote._isNew;
        var backIcon = (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 19l-7-7 7-7" />
            </svg>
        );

        var openNoteViewer = function (doc) {
            var viewFiles = noteDocs
                .filter(function (x) {
                    return x.url || x.file_url || x.full_path;
                })
                .map(function (x) {
                    return { name: x.file_name || x.name, url: x.url || x.file_url || x.full_path, type: x.sub_type || x.type || '' };
                });
            var viewIdx = 0;
            var targetUrl = doc.url || doc.file_url || doc.full_path;
            for (var vi = 0; vi < viewFiles.length; vi++) {
                if (viewFiles[vi].url === targetUrl) {
                    viewIdx = vi;
                    break;
                }
            }
            if (viewFiles.length > 0) setNoteViewerState({ files: viewFiles, index: viewIdx });
        };

        // ── Settings sub-view ────────────────────────────────────
        if (noteView === 'settings') {
            return (
                <div className="chat-panel">
                    {confirm.el}
                    <div className="chat-subview-header">
                        <button
                            className="chat-back-btn"
                            onClick={function () {
                                if (editingNote._isNew && !editingNote.title && !editingNote.content) {
                                    doBackToList();
                                    return;
                                }
                                if (isDirty) {
                                    confirm.show(
                                        AntLocalization.translateText({ vi: 'Bạn có thay đổi chưa lưu. Bỏ thay đổi?', en: 'You have unsaved changes. Discard?', fr: 'Modifications non enregistrées. Les abandonner ?', es: 'Hay cambios sin guardar. ¿Descartarlos?', id: 'Ada perubahan yang belum disimpan. Buang?', ja: '未保存の変更があります。破棄しますか？', zh: '有未保存的更改。是否放弃？', ko: '저장하지 않은 변경 사항이 있습니다. 버릴까요?', hi: 'बिना सहेजे बदलाव हैं। छोड़ दें?', ar: 'لديك تغييرات غير محفوظة. تجاهلها؟' }),
                                        function () {
                                            if (editingNote._isNew) {
                                                doBackToList();
                                            } else {
                                                setIsDirty(false);
                                                setNoteView('detail');
                                                loadNotes();
                                            }
                                        }
                                    );
                                } else {
                                    if (editingNote._isNew) {
                                        doBackToList();
                                    } else {
                                        setNoteView('detail');
                                    }
                                }
                            }}
                        >
                            {backIcon}
                        </button>
                        <span className="chat-subview-title">{AntLocalization.translateText({ vi: 'Cấu hình', en: 'Settings', fr: 'Paramètres', es: 'Configuración', id: 'Pengaturan', ja: '設定', zh: '设置', ko: '설정', hi: 'सेटिंग्स', ar: 'الإعدادات' })}</span>
                        {isDirty && (
                            <button
                                className="chat-settings-save-btn"
                                onClick={function () {
                                    doSave(editingNote);
                                }}
                                title={AntLocalization.translateText({ vi: 'Lưu', en: 'Save', fr: 'Enreg.', es: 'Guardar', id: 'Simpan', ja: '保存', zh: '保存', ko: '저장', hi: 'सहेजें', ar: 'حفظ' })}
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                                    <polyline points="17 21 17 13 7 13 7 21" />
                                    <polyline points="7 3 7 8 15 8" />
                                </svg>
                            </button>
                        )}
                        <span className="notes-save-indicator">
                            {saving ? AntLocalization.translateText({ vi: 'Đang lưu...', en: 'Saving...', fr: 'Sauvegarde...', es: 'Guardando...', id: 'Menyimpan...', ja: '保存中...', zh: '保存中...', ko: '저장 중...', hi: 'सेव हो रहा...', ar: 'جارٍ الحفظ...' }) : ''}
                        </span>
                    </div>
                    <div className="chat-settings-body">
                        {/* Title */}
                        <div className="chat-settings-section">
                            <span className="chat-settings-label">{AntLocalization.translateText({ vi: 'Tiêu đề', en: 'Title', fr: 'Titre', es: 'Título', id: 'Judul', ja: 'タイトル', zh: '标题', ko: '제목', hi: 'शीर्षक', ar: 'العنوان' })}</span>
                            <input
                                className="chat-settings-name-input"
                                value={editingNote.title || ''}
                                onChange={function (e) {
                                    updateField('title', e.target.value);
                                }}
                                placeholder={AntLocalization.translateText({ vi: 'Nhập tiêu đề...', en: 'Title...', fr: 'Titre...', es: 'Título...', id: 'Judul...', ja: 'タイトル...', zh: '标题...', ko: '제목...', hi: 'शीर्षक...', ar: 'العنوان...' })}
                            />
                        </div>
                        {/* Content — inline editor (ẩn toolbar) + nút maximize mở popup */}
                        <div className="chat-settings-section notes-editor-section">
                            <div className="chat-settings-label-row">
                                <span className="chat-settings-label">{AntLocalization.translateText({ vi: 'Nội dung', en: 'Content', fr: 'Contenu', es: 'Contenido', id: 'Isi', ja: '内容', zh: '内容', ko: '내용', hi: 'सामग्री', ar: 'المحتوى' })}</span>
                                <button
                                    className="chat-settings-link-btn"
                                    onClick={function () {
                                        setShowEditorPopup(true);
                                    }}
                                >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M15 3h6v6M14 10l6.1-6.1M9 21H3v-6M10 14l-6.1 6.1" />
                                    </svg>
                                    {AntLocalization.translateText({ vi: 'Mở rộng', en: 'Expand', fr: 'Agrandir', es: 'Expandir', id: 'Perluas', ja: '展開', zh: '展开', ko: '펼치기', hi: 'विस्तार करें', ar: 'توسيع' })}
                                </button>
                            </div>
                            <HtmlEditor
                                className="notes-html-editor-inline"
                                defaultValue={editingNote.content || ''}
                                onValueChanged={function (e) {
                                    updateField('content', e.value);
                                }}
                                placeholder={AntLocalization.translateText({ vi: 'Nội dung ghi chú...', en: 'Note content...', fr: 'Contenu de la note...', es: 'Contenido de la nota...', id: 'Isi catatan...', ja: 'メモの内容...', zh: '笔记内容...', ko: '노트 내용...', hi: 'नोट की सामग्री...', ar: 'محتوى الملاحظة...' })}
                            >
                                <Toolbar multiline={false} />
                            </HtmlEditor>
                        </div>
                        {/* Editor popup — dùng component riêng để tránh re-render parent */}
                        {showEditorPopup && (
                            <NoteEditorPopup
                                title={editingNote.title}
                                initialContent={editingNote.content || ''}
                                onSave={function (html) {
                                    updateField('content', html);
                                    setShowEditorPopup(false);
                                }}
                                onClose={function () {
                                    setShowEditorPopup(false);
                                }}
                            />
                        )}
                        {/* Attachments */}
                        <div className="chat-settings-section">
                            <div className="chat-settings-label-row">
                                <span className="chat-settings-label">
                                    {AntLocalization.translateText({ vi: 'Tài liệu đính kèm', en: 'Attachments', fr: 'Pièces jointes', es: 'Adjuntos', id: 'Lampiran', ja: '添付ファイル', zh: '附件', ko: '첨부 파일', hi: 'संलग्नक', ar: 'المرفقات' })}
                                </span>
                                <button
                                    className="chat-settings-link-btn"
                                    onClick={function () {
                                        if (attFileRef.current) attFileRef.current.click();
                                    }}
                                >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 5v14M5 12h14" />
                                    </svg>
                                    {AntLocalization.translateText({ vi: 'Thêm', en: 'Add', fr: 'Ajouter', es: 'Añadir', id: 'Tambah', ja: '追加', zh: '添加', ko: '추가', hi: 'जोड़ें', ar: 'إضافة' })}
                                </button>
                                <input ref={attFileRef} type="file" style={{ display: 'none' }} onChange={handleAttUpload} />
                            </div>
                            <div className="chat-settings-doc-list">
                                {docsLoading && (
                                    <div className="chat-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा है...', ar: 'جارٍ التحميل...' })}</div>
                                )}
                                {!docsLoading && noteDocs.length === 0 && (
                                    <div className="chat-settings-empty">
                                        {AntLocalization.translateText({ vi: 'Chưa có tài liệu', en: 'No documents', fr: 'Aucun document', es: 'Sin documentos', id: 'Belum ada dokumen', ja: '文書はありません', zh: '暂无文档', ko: '문서가 없습니다', hi: 'कोई दस्तावेज़ नहीं', ar: 'لا توجد مستندات' })}
                                    </div>
                                )}
                                {noteDocs.map(function (doc) {
                                    return (
                                        <FileListItem
                                            key={doc.id}
                                            file={{
                                                name: doc.file_name || doc.name,
                                                full_path: doc.url || doc.file_url || doc.full_path,
                                                sub_type: doc.sub_type || doc.type,
                                                user_name: doc.created_user_name,
                                                created_at: doc.created_at
                                            }}
                                            onPreview={function () {
                                                openNoteViewer(doc);
                                            }}
                                            onDelete={function () {
                                                handleAttDelete(doc);
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        {/* Color */}
                        <div className="chat-settings-section">
                            <span className="chat-settings-label">{AntLocalization.translateText({ vi: 'Màu sắc', en: 'Color', fr: 'Couleur', es: 'Color', id: 'Warna', ja: '色', zh: '颜色', ko: '색상', hi: 'रंग', ar: 'اللون' })}</span>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                                {NOTE_COLORS.map(function (c) {
                                    return (
                                        <button
                                            key={c.key}
                                            className={'notes-settings-color-dot' + (editingNote.color === c.key ? ' active' : '')}
                                            style={{ background: c.bg, borderColor: c.border }}
                                            onClick={function () {
                                                updateField('color', c.key);
                                            }}
                                            title={c.key}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        {/* Visibility */}
                        <div className="chat-settings-section">
                            <span className="chat-settings-label">{AntLocalization.translateText({ vi: 'Hiển thị', en: 'Visibility', fr: 'Visibilité', es: 'Visibilidad', id: 'Visibilitas', ja: '公開範囲', zh: '可见范围', ko: '공개 범위', hi: 'दृश्यता', ar: 'الظهور' })}</span>
                            <select
                                className="notes-settings-vis-select"
                                value={editingNote.visibility || 'private'}
                                onChange={function (e) {
                                    updateField('visibility', e.target.value);
                                }}
                            >
                                {VISIBILITY_OPTIONS.map(function (v) {
                                    return (
                                        <option key={v.key} value={v.key}>
                                            {AntLocalization.translateText(v.label)}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        {/* Pin */}
                        <div className="chat-settings-section">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span className="chat-settings-label">{AntLocalization.translateText({ vi: 'Ghim ghi chú', en: 'Pin note', fr: 'Épingler note', es: 'Fijar nota', id: 'Sematkan', ja: 'ノートを固定', zh: '置顶笔记', ko: '노트 고정', hi: 'नोट पिन करें', ar: 'تثبيت الملاحظة' })}</span>
                                <button
                                    className={'notes-settings-pin-btn' + (editingNote.pinned ? ' active' : '')}
                                    onClick={function () {
                                        updateField('pinned', editingNote.pinned ? 0 : 1);
                                    }}
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill={editingNote.pinned ? 'currentColor' : 'none'}
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                    >
                                        <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        {/* Danger */}
                        <div className="chat-settings-danger">
                            <button
                                className="chat-delete-btn"
                                onClick={function () {
                                    handleDelete(editingNote);
                                }}
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Xóa ghi chú này', en: 'Delete this note', fr: 'Supprimer cette note', es: 'Eliminar esta nota', id: 'Hapus catatan ini', ja: 'このノートを削除', zh: '删除此笔记', ko: '이 노트 삭제', hi: 'यह नोट हटाएँ', ar: 'حذف هذه الملاحظة' })}
                            </button>
                        </div>
                    </div>
                    {noteViewerState && (
                        <AntFileViewer
                            visible
                            files={noteViewerState.files}
                            initialIndex={noteViewerState.index}
                            onClose={function () {
                                setNoteViewerState(null);
                            }}
                        />
                    )}
                </div>
            );
        }

        // ── DETAIL VIEW (giống Chat detail: content + 1 nút gear mở edit) ──
        return (
            <div className="notes-panel">
                {confirm.el}
                <div className="notes-detail-header">
                    <button className="notes-back-btn" onClick={handleBack}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="notes-header-title">
                        {editingNote.title || AntLocalization.translateText({ vi: 'Không tiêu đề', en: 'Untitled', fr: 'Sans titre', es: 'Sin título', id: 'Tanpa judul', ja: '無題', zh: '无标题', ko: '제목 없음', hi: 'शीर्षकहीन', ar: 'بدون عنوان' })}
                    </span>
                    {isOwner && (
                        <button
                            className="notes-settings-icon-btn"
                            onClick={function () {
                                setNoteView('settings');
                            }}
                            title={AntLocalization.translateText({ vi: 'Cấu hình', en: 'Settings', fr: 'Paramètres', es: 'Configuración', id: 'Pengaturan', ja: '設定', zh: '设置', ko: '설정', hi: 'सेटिंग्स', ar: 'الإعدادات' })}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                        </button>
                    )}
                </div>
                <div className="notes-detail-body" style={{ background: colorDef.bg }}>
                    {editingNote.content ? (
                        <div className="notes-view-content" dangerouslySetInnerHTML={{ __html: editingNote.content }} />
                    ) : (
                        <div className="notes-view-content notes-view-empty">
                            {AntLocalization.translateText({ vi: 'Chưa có nội dung', en: 'No content', fr: 'Aucun contenu', es: 'Sin contenido', id: 'Belum ada isi', ja: '内容なし', zh: '暂无内容', ko: '내용 없음', hi: 'कोई सामग्री नहीं', ar: 'لا يوجد محتوى' })}
                        </div>
                    )}
                    {noteDocs.length > 0 && (
                        <div className="notes-att-inline">
                            {(function () {
                                var images = noteDocs.filter(function (d) {
                                    return (d.url || d.file_url || d.full_path) && (d.sub_type || d.type || '').indexOf('image') >= 0;
                                });
                                var others = noteDocs.filter(function (d) {
                                    return (d.url || d.file_url || d.full_path) && (d.sub_type || d.type || '').indexOf('image') < 0;
                                });
                                return (
                                    <Fragment>
                                        {images.length > 0 && (
                                            <div className="chat-att-grid">
                                                {images.map(function (img) {
                                                    return (
                                                        <img
                                                            key={img.id}
                                                            className="chat-att-grid-img"
                                                            src={img.url || img.file_url || img.full_path}
                                                            alt=""
                                                            onClick={function () {
                                                                openNoteViewer(img);
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {others.length > 0 && (
                                            <div className="chat-att-grid">
                                                {others.map(function (of) {
                                                    var name = of.file_name || of.name || 'File';
                                                    var ext = name.split('.').pop().toLowerCase();
                                                    var label =
                                                        ext === 'pdf'
                                                            ? 'PDF'
                                                            : ext === 'doc' || ext === 'docx'
                                                            ? 'DOC'
                                                            : ext === 'xls' || ext === 'xlsx'
                                                            ? 'XLS'
                                                            : ext === 'ppt' || ext === 'pptx'
                                                            ? 'PPT'
                                                            : ext.toUpperCase();
                                                    return (
                                                        <div
                                                            key={of.id}
                                                            className="att-file-card"
                                                            onClick={function () {
                                                                openNoteViewer(of);
                                                            }}
                                                            title={name}
                                                        >
                                                            <div className="att-file-card-icon">
                                                                <svg
                                                                    width="24"
                                                                    height="24"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.2"
                                                                >
                                                                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                                                    <polyline points="14 2 14 8 20 8" />
                                                                </svg>
                                                                <span className="att-file-card-ext">{label}</span>
                                                            </div>
                                                            <div className="att-file-card-name">{name}</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </Fragment>
                                );
                            })()}
                        </div>
                    )}
                </div>
                {noteViewerState && (
                    <AntFileViewer
                        visible
                        files={noteViewerState.files}
                        initialIndex={noteViewerState.index}
                        onClose={function () {
                            setNoteViewerState(null);
                        }}
                    />
                )}
            </div>
        );
    }

    // ═══ LIST VIEW ══════════════════════════════════════════════
    var filteredNotes = noteFilter === 'mine' ? myNotes : noteFilter === 'shared' ? sharedNotes : notes;
    if (noteSearchText.trim()) {
        var nq = noteSearchText.trim().toLowerCase();
        filteredNotes = filteredNotes.filter(function (n) {
            return (n.title || '').toLowerCase().indexOf(nq) >= 0 || (n.content || '').toLowerCase().indexOf(nq) >= 0;
        });
    }

    var renderNoteItem = function (note) {
        var colorDef =
            NOTE_COLORS.find(function (c) {
                return c.key === note.color;
            }) || NOTE_COLORS[0];
        var isOwner = currentUserCode === note.created_user_code;
        var isShared = !isOwner;
        return (
            <div
                key={note.id}
                className={'notes-item' + (note.pinned ? ' pinned' : '') + (isShared ? ' shared' : '')}
                style={{ borderLeftColor: colorDef.border, background: note.pinned ? '#fffde7' : colorDef.bg }}
                onClick={function () {
                    setEditingNote(note);
                    setNoteView('detail');
                    setIsEditing(false);
                    loadNoteDocs(note);
                }}
            >
                <div className="notes-item-header">
                    <span className="notes-item-title">{note.title || AntLocalization.translateText({ vi: 'Không tiêu đề', en: 'Untitled', fr: 'Sans titre', es: 'Sin título', id: 'Tanpa judul', ja: '無題', zh: '无标题', ko: '제목 없음', hi: 'शीर्षकहीन', ar: 'بدون عنوان' })}</span>
                    <span className="notes-item-badges">
                        {note.pinned ? (
                            <span className="notes-badge pinned" title={AntLocalization.translateText({ vi: 'Đã ghim', en: 'Pinned', fr: 'Épinglé', es: 'Fijado', id: 'Disematkan', ja: '固定済み', zh: '已置顶', ko: '고정됨', hi: 'पिन किया गया', ar: 'مثبت' })}>
                                <SvgIcon d={GLYPH.pin} size={12} />
                            </span>
                        ) : null}
                        {isShared ? (
                            <span className="notes-badge shared" title={AntLocalization.translateText({ vi: 'Được chia sẻ', en: 'Shared', fr: 'Partagées', es: 'Compartidas', id: 'Dibagikan', ja: '共有', zh: '共享', ko: '공유됨', hi: 'साझा', ar: 'مشتركة' })}>
                                ↗
                            </span>
                        ) : null}
                    </span>
                </div>
                {isShared && note.created_user_name && (
                    <div className="notes-item-meta">
                        <span className="notes-item-author">{note.created_user_name}</span>
                    </div>
                )}
                {isOwner && (
                    <button
                        className="notes-item-pin-btn"
                        onClick={function (e) {
                            e.stopPropagation();
                            handleTogglePin(note);
                        }}
                        title={
                            note.pinned
                                ? AntLocalization.translateText({ vi: 'Bỏ ghim', en: 'Unpin', fr: 'Désépingler', es: 'Desfijar', id: 'Lepas sematan', ja: '固定を解除', zh: '取消置顶', ko: '고정 해제', hi: 'अनपिन करें', ar: 'إلغاء التثبيت' })
                                : AntLocalization.translateText({ vi: 'Ghim', en: 'Pin', fr: 'Épingler', es: 'Fijar', id: 'Sematkan', ja: '固定', zh: '置顶', ko: '고정', hi: 'पिन करें', ar: 'تثبيت' })
                        }
                    >
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill={note.pinned ? 'currentColor' : 'none'}
                            stroke="currentColor"
                            strokeWidth="1.5"
                        >
                            <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className="notes-panel">
            <div className="chat-search-bar">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                    className="chat-search-input"
                    value={noteSearchText}
                    onChange={function (e) {
                        setNoteSearchText(e.target.value);
                    }}
                    placeholder={AntLocalization.translateText({ vi: 'Tìm ghi chú...', en: 'Search notes...', fr: 'Chercher notes...', es: 'Buscar notas...', id: 'Cari catatan...', ja: 'ノートを検索...', zh: '搜索笔记...', ko: '노트 검색...', hi: 'नोट खोजें...', ar: 'البحث في الملاحظات...' })}
                />
                {noteSearchText && (
                    <button
                        className="chat-search-clear"
                        onClick={function () {
                            setNoteSearchText('');
                        }}
                    >
                        ×
                    </button>
                )}
            </div>

            {sharedNotes.length > 0 && (
                <div className="rs-chips" role="tablist">
                    <button
                        className={'rs-chip' + (noteFilter === 'all' ? ' active' : '')}
                        onClick={function () {
                            setNoteFilter('all');
                        }}
                    >
                        {AntLocalization.translateText({ vi: 'Tất cả', en: 'All', fr: 'Tous', es: 'Todos', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' })} <span className="rs-chip-count">{notes.length}</span>
                    </button>
                    <button
                        className={'rs-chip' + (noteFilter === 'mine' ? ' active' : '')}
                        onClick={function () {
                            setNoteFilter('mine');
                        }}
                    >
                        {AntLocalization.translateText({ vi: 'Của tôi', en: 'Mine', fr: 'Les miennes', es: 'Mías', id: 'Milik saya', ja: '自分', zh: '我的', ko: '내 항목', hi: 'मेरे', ar: 'الخاصة بي' })} <span className="rs-chip-count">{myNotes.length}</span>
                    </button>
                    <button
                        className={'rs-chip' + (noteFilter === 'shared' ? ' active' : '')}
                        onClick={function () {
                            setNoteFilter('shared');
                        }}
                    >
                        {AntLocalization.translateText({ vi: 'Được chia sẻ', en: 'Shared', fr: 'Partagées', es: 'Compartidas', id: 'Dibagikan', ja: '共有', zh: '共享', ko: '공유됨', hi: 'साझा', ar: 'مشتركة' })}{' '}
                        <span className="rs-chip-count">{sharedNotes.length}</span>
                    </button>
                </div>
            )}

            <div className="notes-list">
                {loading && <SidebarState type="loading" />}
                {!loading && filteredNotes.length === 0 && (
                    <SidebarState type="empty" icon="note" text={{ vi: 'Chưa có ghi chú', en: 'No notes yet', fr: 'Aucune note', es: 'Sin notas', id: 'Belum ada catatan', ja: 'ノートはありません', zh: '暂无笔记', ko: '노트가 없습니다', hi: 'अभी कोई नोट नहीं', ar: 'لا توجد ملاحظات' }} />
                )}
                {filteredNotes.map(renderNoteItem)}
            </div>
            <button className="chat-fab" onClick={handleCreate} title={AntLocalization.translateText({ vi: 'Ghi chú mới', en: 'New note', fr: 'Nouvelle note', es: 'Nueva nota', id: 'Catatan baru', ja: '新しいノート', zh: '新建笔记', ko: '새 노트', hi: 'नया नोट', ar: 'ملاحظة جديدة' })}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                </svg>
            </button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── CONTACTS PANEL ─────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var ENTITY_TYPE_COLORS = {
    Customer: { label: { vi: 'Khách hàng', en: 'Customer', fr: 'Client', es: 'Cliente', id: 'Pelanggan', ja: '顧客', zh: '客户', ko: '고객', hi: 'ग्राहक', ar: 'العميل' }, color: '#22c55e', icon: '🟢' },
    Vendor: { label: { vi: 'Nhà cung cấp', en: 'Vendor', fr: 'Fournisseur', es: 'Proveedor', id: 'Pemasok', ja: '仕入先', zh: '供应商', ko: '공급업체', hi: 'आपूर्तिकर्ता', ar: 'المورّد' }, color: '#3b82f6', icon: '🔵' },
    Employee: { label: { vi: 'Nhân viên', en: 'Employee', fr: 'Employé', es: 'Empleado', id: 'Karyawan', ja: '従業員', zh: '员工', ko: '직원', hi: 'कर्मचारी', ar: 'الموظف' }, color: '#8b5cf6', icon: '🟣' }
};

function ContactsPanel() {
    var contactsNavigate = useNavigate();
    var entityAPI = useMemo(function () {
        return new AntAPI('entity', 'BusinessEntity');
    }, []);

    var [keyword, setKeyword] = useState('');
    var [entityFilter, setEntityFilter] = useState('all'); // all | Customer | Vendor | Employee
    var [results, setResults] = useState([]);
    var [loading, setLoading] = useState(false);
    var [viewEntity, setViewEntity] = useState(null); // null = list, object = detail
    var [viewData, setViewData] = useState(null);
    var [viewLoading, setViewLoading] = useState(false);
    var [recentCodes, setRecentCodes] = useState(function () {
        try {
            return JSON.parse(localStorage.getItem('ANTE-contacts-recent')) || [];
        } catch (e) {
            return [];
        }
    });
    var [starredCodes, setStarredCodes] = useState(function () {
        try {
            return JSON.parse(localStorage.getItem('ANTE-contacts-starred')) || [];
        } catch (e) {
            return [];
        }
    });
    var searchTimerRef = useRef(null);

    // ─── Search ─────────────────────────────────────────────────
    var doSearch = useCallback(function (text, typeFilter) {
        setLoading(true);
        var hasKeyword = text && text.length >= 1;
        if (hasKeyword) {
            // Có keyword → dùng quickSearch
            var params = { pageSize: 20, keyword: text };
            if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
            entityAPI.post('/' + entityAPI.routeURL + '/quickSearch', params, function (result) {
                var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                setResults(rows instanceof Array ? rows : []);
                setLoading(false);
            });
        } else {
            // Không keyword → dùng list để load danh sách mặc định
            var listParams = { pageSize: 30, pageIndex: 0 };
            if (typeFilter && typeFilter !== 'all') listParams.type = typeFilter;
            entityAPI.post('/' + entityAPI.routeURL + '/list', listParams, function (result) {
                var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                setResults(rows instanceof Array ? rows : []);
                setLoading(false);
            });
        }
    }, []);

    // Auto-load danh sách mặc định khi mở panel (Employee, Customer, Vendor đều hiện)
    useEffect(function () {
        doSearch('', entityFilter);
    }, []);

    var handleSearchChange = useCallback(
        function (e) {
            var val = e.target.value;
            setKeyword(val);
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
            searchTimerRef.current = setTimeout(function () {
                doSearch(val.trim(), entityFilter);
            }, 300);
        },
        [doSearch, entityFilter]
    );

    // ─── Quick View ─────────────────────────────────────────────
    var handleViewEntity = useCallback(function (entity) {
        setViewEntity(entity);
        setViewLoading(true);
        setViewData(null);

        // Add to recent
        var code = entity.code;
        setRecentCodes(function (prev) {
            var updated = [code]
                .concat(
                    prev.filter(function (c) {
                        return c !== code;
                    })
                )
                .slice(0, 10);
            localStorage.setItem('ANTE-contacts-recent', JSON.stringify(updated));
            return updated;
        });

        entityAPI.post(
            '/' + entityAPI.routeURL + '/quickView',
            {
                entity_code: code
            },
            function (result) {
                var data = (result && result.data) || result || {};
                setViewData(data);
                setViewLoading(false);
            }
        );
    }, []);

    // ─── Star toggle ────────────────────────────────────────────
    var handleToggleStar = useCallback(function (code) {
        setStarredCodes(function (prev) {
            var updated;
            if (prev.indexOf(code) >= 0) {
                updated = prev.filter(function (c) {
                    return c !== code;
                });
            } else {
                updated = [code].concat(prev);
            }
            localStorage.setItem('ANTE-contacts-starred', JSON.stringify(updated));
            return updated;
        });
    }, []);

    // ─── Navigate ───────────────────────────────────────────────
    var handleNavigate = useCallback(
        function (type, code) {
            var pathMap = {
                Customer: '/entity/Customers',
                Vendor: '/entity/Vendors',
                Employee: '/entity/Employees'
            };
            var basePath = pathMap[type] || '/entity/BusinessEntitys';
            contactsNavigate(basePath);
        },
        [contactsNavigate]
    );

    // ─── Format currency ────────────────────────────────────────
    var formatAmount = function (amt) {
        if (amt === null || amt === undefined) return '—';
        return AntLocalization.translateCurrency(amt);
    };

    // ═══ DETAIL VIEW ════════════════════════════════════════════
    if (viewEntity) {
        var ent = (viewData && viewData.entity) || viewEntity;
        var typeDef = ENTITY_TYPE_COLORS[ent.type || ent.entity_type] || ENTITY_TYPE_COLORS.Customer;
        var isStarred = starredCodes.indexOf(ent.code) >= 0;

        return (
            <div className="contacts-panel">
                <div className="contacts-detail-header">
                    <button
                        className="contacts-back-btn"
                        onClick={function () {
                            setViewEntity(null);
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="contacts-detail-name">{ent.name || ent.full_name || ent.code}</span>
                    <button
                        className={'contacts-star-btn' + (isStarred ? ' active' : '')}
                        onClick={function () {
                            handleToggleStar(ent.code);
                        }}
                    >
                        {isStarred ? '⭐' : '☆'}
                    </button>
                    <button
                        className="contacts-nav-btn"
                        onClick={function () {
                            handleNavigate(ent.entity_type, ent.code);
                        }}
                        title={AntLocalization.translateText({ vi: 'Xem chi tiết', en: 'View detail', fr: 'Voir le détail', es: 'Ver detalle', id: 'Lihat detail', ja: '詳細を見る', zh: '查看详情', ko: '상세 보기', hi: 'विवरण देखें', ar: 'عرض التفاصيل' })}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                        </svg>
                    </button>
                </div>

                <div className="contacts-detail-body">
                    {viewLoading && <div className="contacts-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読込中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोडिंग...', ar: 'تحميل...' })}</div>}

                    {!viewLoading && viewData && (
                        <>
                            {/* Info */}
                            <div className="contacts-info-section">
                                <span className="contacts-type-badge" style={{ color: typeDef.color }}>
                                    {typeDef.icon} {AntLocalization.translateText(typeDef.label)}
                                </span>
                                {ent.phone && (
                                    <div className="contacts-info-row">
                                        <span>📞</span>
                                        <a href={'tel:' + ent.phone}>{ent.phone}</a>
                                    </div>
                                )}
                                {ent.email && (
                                    <div className="contacts-info-row">
                                        <span>📧</span>
                                        <a href={'mailto:' + ent.email}>{ent.email}</a>
                                    </div>
                                )}
                                {ent.address && (
                                    <div className="contacts-info-row">
                                        <span>📍</span>
                                        <span>{ent.address}</span>
                                    </div>
                                )}
                            </div>

                            {/* Balances */}
                            {(viewData.ar_balance !== null || viewData.ap_balance !== null) && (
                                <div className="contacts-balance-section">
                                    {viewData.ar_balance !== null && viewData.ar_balance !== 0 && (
                                        <div className="contacts-balance-item">
                                            <span>{AntLocalization.translateText({ vi: 'Phải thu', en: 'Receivables', fr: 'Créances', es: 'Por cobrar', id: 'Piutang', ja: '売掛金', zh: '应收', ko: '미수금', hi: 'प्राप्य', ar: 'المدينون' })}</span>
                                            <span
                                                className="contacts-balance-amount"
                                                style={{ color: viewData.ar_balance > 0 ? '#dc2626' : '#22c55e' }}
                                            >
                                                {formatAmount(viewData.ar_balance)}
                                            </span>
                                        </div>
                                    )}
                                    {viewData.ap_balance !== null && viewData.ap_balance !== 0 && (
                                        <div className="contacts-balance-item">
                                            <span>{AntLocalization.translateText({ vi: 'Phải trả', en: 'Payables', es: 'Por pagar', id: 'Utang', ja: '買掛金', zh: '应付', ko: '미지급금', hi: 'देय', ar: 'الدائنون', fr: 'Dettes' })}</span>
                                            <span
                                                className="contacts-balance-amount"
                                                style={{ color: viewData.ap_balance > 0 ? '#dc2626' : '#22c55e' }}
                                            >
                                                {formatAmount(viewData.ap_balance)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Recent orders */}
                            {viewData.recent_ar_orders && viewData.recent_ar_orders.length > 0 && (
                                <div className="contacts-orders-section">
                                    <div className="contacts-section-title">
                                        {AntLocalization.translateText({ vi: 'Đơn bán gần nhất', en: 'Recent sales', fr: 'Ventes récentes', es: 'Ventas recientes', id: 'Penjualan terbaru', ja: '最近の販売', zh: '最近销售', ko: '최근 판매', hi: 'हाल की बिक्री', ar: 'أحدث المبيعات' })}
                                    </div>
                                    {viewData.recent_ar_orders.map(function (o) {
                                        return (
                                            <div
                                                key={o.id}
                                                className="contacts-order-item"
                                                onClick={function () {
                                                    contactsNavigate('/ar/AROrder/' + o.code);
                                                }}
                                            >
                                                <span className="contacts-order-code">{o.code}</span>
                                                <span className="contacts-order-status">{o.order_status}</span>
                                                <span className="contacts-order-amount">{formatAmount(o.total_gross_price)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {viewData.recent_ap_orders && viewData.recent_ap_orders.length > 0 && (
                                <div className="contacts-orders-section">
                                    <div className="contacts-section-title">
                                        {AntLocalization.translateText({ vi: 'Đơn mua gần nhất', en: 'Recent purchases', fr: 'Achats récents', es: 'Compras recientes', id: 'Pembelian terbaru', ja: '最近の仕入', zh: '最近采购', ko: '최근 구매', hi: 'हाल की खरीद', ar: 'أحدث المشتريات' })}
                                    </div>
                                    {viewData.recent_ap_orders.map(function (o) {
                                        return (
                                            <div
                                                key={o.id}
                                                className="contacts-order-item"
                                                onClick={function () {
                                                    contactsNavigate('/ap/APOrder/' + o.code);
                                                }}
                                            >
                                                <span className="contacts-order-code">{o.code}</span>
                                                <span className="contacts-order-status">{o.order_status}</span>
                                                <span className="contacts-order-amount">{formatAmount(o.total_gross_price)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Quick actions */}
                            <div className="contacts-actions-section">
                                {ent.phone && (
                                    <a className="contacts-action-btn" href={'tel:' + ent.phone}>
                                        📞 {AntLocalization.translateText({ vi: 'Gọi', en: 'Call', fr: 'Appel', es: 'Llamar', id: 'Telepon', ja: '発信', zh: '拨打', ko: '전화', hi: 'कॉल', ar: 'اتصال' })}
                                    </a>
                                )}
                                {ent.email && (
                                    <a className="contacts-action-btn" href={'mailto:' + ent.email}>
                                        📧 Email
                                    </a>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ═══ LIST VIEW ══════════════════════════════════════════════
    var handleFilterChange = useCallback(
        function (f) {
            setEntityFilter(f);
            doSearch(keyword.trim(), f);
        },
        [keyword, doSearch]
    );

    return (
        <div className="contacts-panel">
            <div className="contacts-search">
                <input
                    className="contacts-search-input"
                    value={keyword}
                    onChange={handleSearchChange}
                    placeholder={AntLocalization.translateText({ vi: 'Tìm tên, SĐT, email...', en: 'Search name, phone, email...', fr: 'Nom, tél., e-mail...', es: 'Nombre, tel., email...', id: 'Cari nama, telp, email...', ja: '名前・電話・メールで検索...', zh: '搜索姓名、电话、邮箱...', ko: '이름, 전화, 이메일 검색...', hi: 'नाम, फ़ोन, ईमेल खोजें...', ar: 'ابحث بالاسم أو الهاتف أو البريد...' })}
                />
            </div>
            <div className="contacts-filter-tabs">
                {[
                    { key: 'all', label: { vi: 'Tất cả', en: 'All', fr: 'Tout', es: 'Todo', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' } },
                    { key: 'Customer', label: { vi: 'Khách', en: 'Customer', fr: 'Client', es: 'Cliente', id: 'Pelanggan', ja: '顧客', zh: '客户', ko: '고객', hi: 'ग्राहक', ar: 'العميل' } },
                    { key: 'Vendor', label: { vi: 'NCC', en: 'Vendor', fr: 'Fourn.', es: 'Prov.', id: 'Pemasok', ja: '仕入先', zh: '供应商', ko: '공급사', hi: 'विक्रेता', ar: 'المورّد' } },
                    { key: 'Employee', label: { vi: 'Nhân viên', en: 'Employee', fr: 'Employé', es: 'Empleado', id: 'Karyawan', ja: '従業員', zh: '员工', ko: '직원', hi: 'कर्मचारी', ar: 'الموظف' } }
                ].map(function (f) {
                    return (
                        <button
                            key={f.key}
                            className={'contacts-filter-chip' + (entityFilter === f.key ? ' active' : '')}
                            onClick={function () {
                                handleFilterChange(f.key);
                            }}
                        >
                            {AntLocalization.translateText(f.label)}
                        </button>
                    );
                })}
            </div>
            <div className="contacts-list">
                {loading && <div className="contacts-loading">{AntLocalization.translateText({ vi: 'Đang tìm...', en: 'Searching...', fr: 'Recherche...', es: 'Buscando...', id: 'Mencari...', ja: '検索中...', zh: '搜索中...', ko: '검색 중...', hi: 'खोज जारी...', ar: 'جارٍ البحث...' })}</div>}

                {!loading && results.length === 0 && (
                    <div className="contacts-empty">{AntLocalization.translateText({ vi: 'Không tìm thấy', en: 'No results', fr: 'Aucun résultat', es: 'Sin resultados', id: 'Tidak ditemukan', ja: '該当なし', zh: '未找到', ko: '결과 없음', hi: 'कोई परिणाम नहीं', ar: 'لا توجد نتائج' })}</div>
                )}

                {results.map(function (r) {
                    var typeDef = ENTITY_TYPE_COLORS[r.type || r.entity_type] || { icon: '⚪', color: '#94a3b8' };
                    return (
                        <div
                            key={r.id}
                            className="contacts-item"
                            onClick={function () {
                                handleViewEntity(r);
                            }}
                        >
                            <span className="contacts-item-icon">{typeDef.icon}</span>
                            <div className="contacts-item-info">
                                <div className="contacts-item-name">{r.entity_name || r.name || r.full_name || r.code}</div>
                                <div className="contacts-item-phone">
                                    {r.entity_contact_phone || r.phone || r.entity_contact_email || r.email || ''}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── REMINDER PANEL ─────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var REMINDER_STATUS_MAP = {
    New: { label: { vi: 'Mới', en: 'New', es: 'Nuevo', id: 'Baru', ja: '新規', zh: '新建', ko: '신규', hi: 'नया', ar: 'جديد', fr: 'Créé' }, color: '#94a3b8' },
    Active: { label: { vi: 'Hoạt động', en: 'Active', fr: 'Actif', es: 'Activo', id: 'Aktif', ja: '有効', zh: '启用', ko: '활성', hi: 'सक्रिय', ar: 'نشط' }, color: '#3b82f6' },
    Completed: { label: { vi: 'Hoàn thành', en: 'Done', fr: 'Terminé', es: 'Completado', id: 'Selesai', ja: '完了', zh: '已完成', ko: '완료', hi: 'पूर्ण', ar: 'مكتمل' }, color: '#22c55e' },
    Cancelled: { label: { vi: 'Huỷ', en: 'Cancelled', fr: 'Annulé', es: 'Cancelado', id: 'Dibatalkan', ja: '取消済', zh: '已取消', ko: '취소됨', hi: 'रद्द', ar: 'ملغى' }, color: '#ef4444' }
};

function reminderFormatDate(ts) {
    if (!ts) return '';
    var ms = ts < 1e12 ? ts * 1000 : ts;
    var sameDay = new Date(ms).toDateString() === new Date().toDateString();
    return sameDay ? sidebarTime(ms) : sidebarDayMonth(ms) + ' ' + sidebarTime(ms);
}

function ReminderPanel() {
    var reminderAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserReminder');
    }, []);

    var [reminderView, setReminderView] = useState('list'); // 'list' | 'detail' | 'settings'
    var [reminders, setReminders] = useState([]);
    var [loading, setLoading] = useState(false);
    var [editingReminder, setEditingReminder] = useState(null);
    var [saving, setSaving] = useState(false);
    var [reminderFilter, setReminderFilter] = useState('all'); // all | mine | shared
    var [showEditorPopup, setShowEditorPopup] = useState(false);
    var [reminderSearchText, setReminderSearchText] = useState('');
    var [isDirty, setIsDirty] = useState(false);

    var currentUserCode = useMemo(function () {
        var session = AntSession ? AntSession() : null;
        if (session && session.user_code) return session.user_code;
        try {
            var keys = Object.keys(localStorage);
            for (var i = 0; i < keys.length; i++) {
                if (keys[i].endsWith('-session-info')) {
                    var info = JSON.parse(localStorage.getItem(keys[i]));
                    if (info && info.user_code) return info.user_code;
                }
            }
        } catch (e) {}
        return null;
    }, []);

    // Phân loại (hooks phải trước early return)
    var myReminders = useMemo(
        function () {
            return reminders.filter(function (r) {
                return r.created_user_code === currentUserCode;
            });
        },
        [reminders, currentUserCode]
    );
    var sharedReminders = useMemo(
        function () {
            return reminders.filter(function (r) {
                return r.created_user_code !== currentUserCode;
            });
        },
        [reminders, currentUserCode]
    );

    // ─── Load ───────────────────────────────────────────────────
    var loadReminders = useCallback(function () {
        setLoading(true);
        reminderAPI.post('/' + reminderAPI.routeURL + '/list', { pageSize: 100, pageIndex: 0 }, function (result) {
            var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
            if (!(rows instanceof Array)) rows = [];
            rows.sort(function (a, b) {
                return (b.updated_at || 0) - (a.updated_at || 0);
            });
            setReminders(rows);
            setLoading(false);
        });
    }, []);

    useEffect(function () {
        loadReminders();
    }, []);

    // ─── Create ─────────────────────────────────────────────────
    var handleCreate = useCallback(
        function () {
            reminderAPI.post(
                '/' + reminderAPI.routeURL + '/create',
                {
                    type: 'personal',
                    title: '',
                    description: '',
                    assign_type: 'self'
                },
                function (result) {
                    var data = (result && result.data) || result;
                    var rid = data && (data.id || data.code);
                    if (rid) {
                        setEditingReminder({
                            id: data.id,
                            code: data.code,
                            title: '',
                            description: '',
                            assign_type: 'self',
                            created_user_code: currentUserCode,
                            _isNew: true
                        });
                        setReminderView('settings');
                        setShowEditorPopup(true);
                    } else {
                        loadReminders();
                    }
                }
            );
        },
        [currentUserCode]
    );

    // ─── Save (chỉ khi user bấm nút Lưu) ─────────────────────
    var doSave = useCallback(
        function (reminder) {
            if (!reminder || !reminder.id) return;
            setSaving(true);
            reminderAPI.post(
                '/' + reminderAPI.routeURL + '/update',
                {
                    where: { id: reminder.id },
                    update: {
                        title: reminder.title,
                        description: reminder.description,
                        assign_type: reminder.assign_type
                    }
                },
                function () {
                    setSaving(false);
                    setIsDirty(false);
                    loadReminders();
                }
            );
        },
        [loadReminders]
    );

    var updateField = useCallback(function (field, value) {
        setEditingReminder(function (prev) {
            if (!prev) return prev;
            var updated = Object.assign({}, prev);
            updated[field] = value;
            return updated;
        });
        setIsDirty(true);
    }, []);

    // ─── Confirm dialog ─────────────────────────────────────────
    var confirm = useConfirm();

    // ─── Delete ─────────────────────────────────────────────────
    var handleDelete = useCallback(
        function (reminder) {
            confirm.show(AntLocalization.translateText({ vi: 'Xóa ghi nhớ này?', en: 'Delete this memo?', fr: 'Supprimer ce pense-bête ?', es: '¿Eliminar esta nota?', id: 'Hapus catatan tempel ini?', ja: 'この付箋を削除しますか？', zh: '删除此便签？', ko: '이 메모를 삭제할까요?', hi: 'यह स्टिकी नोट हटाएँ?', ar: 'حذف هذه الملاحظة اللاصقة؟' }), function () {
                reminderAPI.post('/' + reminderAPI.routeURL + '/delete', { id: reminder.id }, function () {
                    setEditingReminder(null);
                    setReminderView('list');
                    loadReminders();
                });
            });
        },
        [confirm.show]
    );

    // ─── Back to list ───────────────────────────────────────────
    var doBackToList = useCallback(
        function () {
            setEditingReminder(null);
            setReminderView('list');
            setIsDirty(false);
            loadReminders();
        },
        [loadReminders]
    );

    var handleBack = useCallback(
        function () {
            if (isDirty) {
                confirm.show(
                    AntLocalization.translateText({ vi: 'Bạn có thay đổi chưa lưu. Bỏ thay đổi?', en: 'You have unsaved changes. Discard?', fr: 'Modifications non enregistrées. Les abandonner ?', es: 'Hay cambios sin guardar. ¿Descartarlos?', id: 'Ada perubahan yang belum disimpan. Buang?', ja: '未保存の変更があります。破棄しますか？', zh: '有未保存的更改。是否放弃？', ko: '저장하지 않은 변경 사항이 있습니다. 버릴까요?', hi: 'बिना सहेजे बदलाव हैं। छोड़ दें?', ar: 'لديك تغييرات غير محفوظة. تجاهلها؟' }),
                    function () {
                        doBackToList();
                    }
                );
            } else {
                doBackToList();
            }
        },
        [isDirty, confirm, doBackToList]
    );

    // ═══ DETAIL VIEW ════════════════════════════════════════════
    if (editingReminder) {
        var isOwner = !editingReminder.created_user_code || currentUserCode === editingReminder.created_user_code || editingReminder._isNew;
        var backIcon = (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 19l-7-7 7-7" />
            </svg>
        );

        // ── Settings sub-view (edit) ─────────────────────────────
        if (reminderView === 'settings') {
            return (
                <div className="chat-panel">
                    {confirm.el}
                    <div className="chat-subview-header">
                        <button
                            className="chat-back-btn"
                            onClick={function () {
                                if (editingReminder._isNew && !editingReminder.title && !editingReminder.description) {
                                    doBackToList();
                                    return;
                                }
                                if (isDirty) {
                                    confirm.show(
                                        AntLocalization.translateText({ vi: 'Bạn có thay đổi chưa lưu. Bỏ thay đổi?', en: 'You have unsaved changes. Discard?', fr: 'Modifications non enregistrées. Les abandonner ?', es: 'Hay cambios sin guardar. ¿Descartarlos?', id: 'Ada perubahan yang belum disimpan. Buang?', ja: '未保存の変更があります。破棄しますか？', zh: '有未保存的更改。是否放弃？', ko: '저장하지 않은 변경 사항이 있습니다. 버릴까요?', hi: 'बिना सहेजे बदलाव हैं। छोड़ दें?', ar: 'لديك تغييرات غير محفوظة. تجاهلها؟' }),
                                        function () {
                                            if (editingReminder._isNew) {
                                                doBackToList();
                                            } else {
                                                setIsDirty(false);
                                                setReminderView('detail');
                                                loadReminders();
                                            }
                                        }
                                    );
                                } else {
                                    if (editingReminder._isNew) {
                                        doBackToList();
                                    } else {
                                        setReminderView('detail');
                                    }
                                }
                            }}
                        >
                            {backIcon}
                        </button>
                        <span className="chat-subview-title">{AntLocalization.translateText({ vi: 'Cấu hình', en: 'Settings', fr: 'Paramètres', es: 'Configuración', id: 'Pengaturan', ja: '設定', zh: '设置', ko: '설정', hi: 'सेटिंग्स', ar: 'الإعدادات' })}</span>
                        {isDirty && (
                            <button
                                className="chat-settings-save-btn"
                                onClick={function () {
                                    doSave(editingReminder);
                                }}
                                title={AntLocalization.translateText({ vi: 'Lưu', en: 'Save', fr: 'Enreg.', es: 'Guardar', id: 'Simpan', ja: '保存', zh: '保存', ko: '저장', hi: 'सहेजें', ar: 'حفظ' })}
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                                    <polyline points="17 21 17 13 7 13 7 21" />
                                    <polyline points="7 3 7 8 15 8" />
                                </svg>
                            </button>
                        )}
                        <span className="notes-save-indicator">
                            {saving ? AntLocalization.translateText({ vi: 'Đang lưu...', en: 'Saving...', fr: 'Sauvegarde...', es: 'Guardando...', id: 'Menyimpan...', ja: '保存中...', zh: '保存中...', ko: '저장 중...', hi: 'सेव हो रहा...', ar: 'جارٍ الحفظ...' }) : ''}
                        </span>
                    </div>
                    <div className="chat-settings-body">
                        {/* Title */}
                        <div className="chat-settings-section">
                            <span className="chat-settings-label">{AntLocalization.translateText({ vi: 'Tiêu đề', en: 'Title', fr: 'Titre', es: 'Título', id: 'Judul', ja: 'タイトル', zh: '标题', ko: '제목', hi: 'शीर्षक', ar: 'العنوان' })}</span>
                            <input
                                className="chat-settings-name-input"
                                value={editingReminder.title || ''}
                                onChange={function (e) {
                                    updateField('title', e.target.value);
                                }}
                                placeholder={AntLocalization.translateText({ vi: 'Nhập tiêu đề...', en: 'Title...', fr: 'Titre...', es: 'Título...', id: 'Judul...', ja: 'タイトル...', zh: '标题...', ko: '제목...', hi: 'शीर्षक...', ar: 'العنوان...' })}
                            />
                        </div>
                        {/* Content — inline editor (ẩn toolbar) + nút maximize mở popup */}
                        <div className="chat-settings-section notes-editor-section">
                            <div className="chat-settings-label-row">
                                <span className="chat-settings-label">{AntLocalization.translateText({ vi: 'Nội dung', en: 'Content', fr: 'Contenu', es: 'Contenido', id: 'Isi', ja: '内容', zh: '内容', ko: '내용', hi: 'सामग्री', ar: 'المحتوى' })}</span>
                                <button
                                    className="chat-settings-link-btn"
                                    onClick={function () {
                                        setShowEditorPopup(true);
                                    }}
                                >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M15 3h6v6M14 10l6.1-6.1M9 21H3v-6M10 14l-6.1 6.1" />
                                    </svg>
                                    {AntLocalization.translateText({ vi: 'Mở rộng', en: 'Expand', fr: 'Agrandir', es: 'Expandir', id: 'Perluas', ja: '展開', zh: '展开', ko: '펼치기', hi: 'विस्तार करें', ar: 'توسيع' })}
                                </button>
                            </div>
                            <HtmlEditor
                                className="notes-html-editor-inline"
                                defaultValue={editingReminder.description || ''}
                                onValueChanged={function (e) {
                                    updateField('description', e.value);
                                }}
                                placeholder={AntLocalization.translateText({ vi: 'Nội dung ghi nhớ...', en: 'Memo content...', fr: 'Contenu du mémo...', es: 'Contenido de la nota...', id: 'Isi catatan tempel...', ja: '付箋の内容...', zh: '便签内容...', ko: '메모 내용...', hi: 'नोट की सामग्री...', ar: 'محتوى الملاحظة...' })}
                            >
                                <Toolbar multiline={false} />
                            </HtmlEditor>
                        </div>
                        {/* Editor popup */}
                        {showEditorPopup && (
                            <NoteEditorPopup
                                title={editingReminder.title}
                                initialContent={editingReminder.description || ''}
                                onSave={function (html) {
                                    updateField('description', html);
                                    setShowEditorPopup(false);
                                }}
                                onClose={function () {
                                    setShowEditorPopup(false);
                                }}
                            />
                        )}
                        {/* Danger */}
                        <div className="chat-settings-danger">
                            <button
                                className="chat-delete-btn"
                                onClick={function () {
                                    handleDelete(editingReminder);
                                }}
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Xóa ghi nhớ này', en: 'Delete this memo', fr: 'Supprimer ce pense-bête', es: 'Eliminar esta nota', id: 'Hapus catatan ini', ja: 'この付箋を削除', zh: '删除此便签', ko: '이 메모 삭제', hi: 'यह स्टिकी नोट हटाएँ', ar: 'حذف هذه الملاحظة' })}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        // ── DETAIL VIEW (view only, nút gear mở settings) ────────
        return (
            <div className="notes-panel">
                {confirm.el}
                <div className="notes-detail-header">
                    <button className="notes-back-btn" onClick={handleBack}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="notes-header-title">
                        {editingReminder.title || AntLocalization.translateText({ vi: 'Không tiêu đề', en: 'Untitled', fr: 'Sans titre', es: 'Sin título', id: 'Tanpa judul', ja: '無題', zh: '无标题', ko: '제목 없음', hi: 'शीर्षकहीन', ar: 'بدون عنوان' })}
                    </span>
                    {isOwner && (
                        <button
                            className="notes-settings-icon-btn"
                            onClick={function () {
                                setReminderView('settings');
                            }}
                            title={AntLocalization.translateText({ vi: 'Cấu hình', en: 'Settings', fr: 'Paramètres', es: 'Configuración', id: 'Pengaturan', ja: '設定', zh: '设置', ko: '설정', hi: 'सेटिंग्स', ar: 'الإعدادات' })}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                        </button>
                    )}
                </div>
                <div className="notes-detail-body">
                    {editingReminder.description ? (
                        <div className="notes-view-content" dangerouslySetInnerHTML={{ __html: editingReminder.description }} />
                    ) : (
                        <div className="notes-view-content notes-view-empty">
                            {AntLocalization.translateText({ vi: 'Chưa có nội dung', en: 'No content', fr: 'Aucun contenu', es: 'Sin contenido', id: 'Belum ada isi', ja: '内容なし', zh: '暂无内容', ko: '내용 없음', hi: 'कोई सामग्री नहीं', ar: 'لا يوجد محتوى' })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ═══ LIST VIEW ══════════════════════════════════════════════
    var filteredReminders = reminderFilter === 'mine' ? myReminders : reminderFilter === 'shared' ? sharedReminders : reminders;
    if (reminderSearchText.trim()) {
        var rq = reminderSearchText.trim().toLowerCase();
        filteredReminders = filteredReminders.filter(function (r) {
            return (r.title || '').toLowerCase().indexOf(rq) >= 0 || (r.description || '').toLowerCase().indexOf(rq) >= 0;
        });
    }

    var renderReminderItem = function (r) {
        var isOwner = currentUserCode === r.created_user_code;
        var isShared = !isOwner;
        return (
            <div
                key={r.id}
                className={'notes-item' + (isShared ? ' shared' : '')}
                onClick={function () {
                    setEditingReminder(r);
                    setReminderView('detail');
                }}
            >
                <div className="notes-item-header">
                    <span className="notes-item-title">{r.title || AntLocalization.translateText({ vi: 'Không tiêu đề', en: 'Untitled', fr: 'Sans titre', es: 'Sin título', id: 'Tanpa judul', ja: '無題', zh: '无标题', ko: '제목 없음', hi: 'शीर्षकहीन', ar: 'بدون عنوان' })}</span>
                    <span className="notes-item-badges">
                        {isShared ? (
                            <span className="notes-badge shared" title={AntLocalization.translateText({ vi: 'Được chia sẻ', en: 'Shared', fr: 'Partagées', es: 'Compartidas', id: 'Dibagikan', ja: '共有', zh: '共享', ko: '공유됨', hi: 'साझा', ar: 'مشتركة' })}>
                                ↗
                            </span>
                        ) : null}
                    </span>
                </div>
                {isShared && r.created_user_name && (
                    <div className="notes-item-meta">
                        <span className="notes-item-author">{r.created_user_name}</span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="notes-panel">
            <div className="chat-search-bar">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                    className="chat-search-input"
                    value={reminderSearchText}
                    onChange={function (e) {
                        setReminderSearchText(e.target.value);
                    }}
                    placeholder={AntLocalization.translateText({ vi: 'Tìm ghi nhớ...', en: 'Search...', fr: 'Rechercher...', es: 'Buscar...', id: 'Cari...', ja: '検索...', zh: '搜索...', ko: '검색...', hi: 'खोजें...', ar: 'بحث...' })}
                />
                {reminderSearchText && (
                    <button
                        className="chat-search-clear"
                        onClick={function () {
                            setReminderSearchText('');
                        }}
                    >
                        ×
                    </button>
                )}
            </div>

            {sharedReminders.length > 0 && (
                <div className="rs-chips" role="tablist">
                    <button
                        className={'rs-chip' + (reminderFilter === 'all' ? ' active' : '')}
                        onClick={function () {
                            setReminderFilter('all');
                        }}
                    >
                        {AntLocalization.translateText({ vi: 'Tất cả', en: 'All', fr: 'Tous', es: 'Todos', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' })} <span className="rs-chip-count">{reminders.length}</span>
                    </button>
                    <button
                        className={'rs-chip' + (reminderFilter === 'mine' ? ' active' : '')}
                        onClick={function () {
                            setReminderFilter('mine');
                        }}
                    >
                        {AntLocalization.translateText({ vi: 'Của tôi', en: 'Mine', fr: 'Les miennes', es: 'Mías', id: 'Milik saya', ja: '自分', zh: '我的', ko: '내 항목', hi: 'मेरे', ar: 'الخاصة بي' })}{' '}
                        <span className="rs-chip-count">{myReminders.length}</span>
                    </button>
                    <button
                        className={'rs-chip' + (reminderFilter === 'shared' ? ' active' : '')}
                        onClick={function () {
                            setReminderFilter('shared');
                        }}
                    >
                        {AntLocalization.translateText({ vi: 'Được chia sẻ', en: 'Shared', fr: 'Partagées', es: 'Compartidas', id: 'Dibagikan', ja: '共有', zh: '共享', ko: '공유됨', hi: 'साझा', ar: 'مشتركة' })}{' '}
                        <span className="rs-chip-count">{sharedReminders.length}</span>
                    </button>
                </div>
            )}

            <div className="notes-list">
                {loading && <SidebarState type="loading" />}
                {!loading && filteredReminders.length === 0 && (
                    <SidebarState type="empty" icon="reminder" text={{ vi: 'Chưa có ghi nhớ', en: 'No memos yet', fr: 'Aucun pense-bête', es: 'Sin notas rápidas', id: 'Belum ada catatan', ja: '付箋はありません', zh: '暂无便签', ko: '메모가 없습니다', hi: 'कोई नोट नहीं', ar: 'لا توجد ملاحظات' }} />
                )}
                {filteredReminders.map(renderReminderItem)}
            </div>
            <button className="chat-fab" onClick={handleCreate} title={AntLocalization.translateText({ vi: 'Ghi nhớ mới', en: 'New memo', fr: 'Nouveau mémo', es: 'Nueva nota', id: 'Catatan baru', ja: '新しい付箋', zh: '新建便签', ko: '새 메모', hi: 'नया स्टिकी नोट', ar: 'ملاحظة جديدة' })}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                </svg>
            </button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── SHARED FILE LIST ITEM (dùng cho Chat settings + Notes attachments) ──
function FileListItem(props) {
    var f = props.file;
    var name = f.name || f.file_name || 'File';
    var url = f.full_path || f.url || f.file_url || null;
    var isImage = url && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name);
    var ext = name.split('.').pop().toLowerCase();
    var glyph = isImage ? GLYPH.image : ['mp4', 'mov', 'webm'].indexOf(ext) >= 0 ? GLYPH.video : ['zip', 'rar'].indexOf(ext) >= 0 ? GLYPH.archive : ['pdf', 'doc', 'docx', 'xls', 'xlsx'].indexOf(ext) >= 0 ? GLYPH.doc : GLYPH.clip;
    var icon = <SvgIcon d={glyph} size={18} />;
    return (
        <div className="shared-file-item">
            <div className="shared-file-thumb" onClick={props.onPreview}>
                {isImage ? <img src={url} alt="" /> : <span className="shared-file-icon">{icon}</span>}
            </div>
            <div className="shared-file-info">
                <div className="shared-file-name" onClick={props.onPreview} title={name}>
                    {name}
                </div>
                {(f.user_name || f.created_at) && (
                    <div className="shared-file-meta">
                        {f.user_name && <span>{f.user_name}</span>}
                        {f.created_at && <span>{commentFormatTime(f.created_at)}</span>}
                    </div>
                )}
            </div>
            <div className="shared-file-actions">
                {url && (
                    <a
                        className="shared-file-btn"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={AntLocalization.translateText({ vi: 'Tải xuống', en: 'Download', fr: 'Télécharger', es: 'Descargar', id: 'Unduh', ja: 'ダウンロード', zh: '下载', ko: '다운로드', hi: 'डाउनलोड करें', ar: 'تنزيل' })}
                        onClick={function (e) {
                            e.stopPropagation();
                        }}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                        </svg>
                    </a>
                )}
                {props.onDelete && (
                    <button
                        className="shared-file-btn shared-file-del"
                        onClick={function (e) {
                            e.stopPropagation();
                            props.onDelete();
                        }}
                        title={AntLocalization.translateText({ vi: 'Xóa', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएँ', ar: 'حذف' })}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── TASK PANEL (OFTask) ────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

// ─── Task helper maps (muted palette) ───────────────────────────
var GT_STATUS_MAP = {
    New: { label: { vi: 'Mới', en: 'New', es: 'Nuevo', id: 'Baru', ja: '新規', zh: '新建', ko: '신규', hi: 'नया', ar: 'جديد', fr: 'Créé' }, color: '#94a3b8' },
    ToDo: { label: { vi: 'Cần làm', en: 'To Do', fr: 'À faire', es: 'Por hacer', id: 'Tertunda', ja: '未着手', zh: '待办', ko: '할 일', hi: 'करना है', ar: 'للتنفيذ' }, color: '#5b8fd9' },
    WIP: { label: { vi: 'Đang làm', en: 'In Progress', fr: 'En cours', es: 'En curso', id: 'Dikerjakan', ja: '進行中', zh: '进行中', ko: '진행 중', hi: 'प्रगति में', ar: 'قيد التنفيذ' }, color: '#c88a2e' },
    Done: { label: { vi: 'Hoàn thành', en: 'Done', fr: 'Terminé', es: 'Completado', id: 'Selesai', ja: '完了', zh: '已完成', ko: '완료', hi: 'पूर्ण', ar: 'مكتمل' }, color: '#3da06a' },
    Failed: { label: { vi: 'Thất bại', en: 'Failed', fr: 'Échec', es: 'Error', id: 'Gagal', ja: '失敗', zh: '失败', ko: '실패', hi: 'विफल', ar: 'فشل' }, color: '#c75050' },
    Close: { label: { vi: 'Đóng', en: 'Closed', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' }, color: '#64748b' },
    Cancel: { label: { vi: 'Hủy', en: 'Cancelled', fr: 'Annuler', es: 'Cancelar', id: 'Batal', ja: 'キャンセル', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء' }, color: '#94a3b8' }
};

function gtGetStatusColor(status) {
    return GT_STATUS_MAP[status] ? GT_STATUS_MAP[status].color : '#94a3b8';
}
function gtGetStatusLabel(status) {
    return GT_STATUS_MAP[status] ? AntLocalization.translateText(GT_STATUS_MAP[status].label) : status;
}

function gtGetPriorityColor(p) {
    if (p === 'Urgent') return '#b44040';
    if (p === 'High') return '#b8862e';
    if (p === 'Low') return '#94a3b8';
    return null;
}

function gtFormatDate(ts) {
    return sidebarDayMonth(ts);
}

var GT_TYPE_OPTIONS = [
    { code: 'Task', label: { vi: 'Thực hiện', en: 'Execute', fr: 'Exécuter', es: 'Ejecutar', id: 'Kerjakan', ja: '実行', zh: '执行', ko: '수행', hi: 'निष्पादित करें', ar: 'تنفيذ' } },
    { code: 'Feature', label: { vi: 'Phát triển', en: 'Develop', fr: 'Développer', es: 'Desarrollar', id: 'Kembangkan', ja: '開発', zh: '开发', ko: '개발', hi: 'विकास', ar: 'تطوير' } },
    { code: 'Bug', label: { vi: 'Xử lý lỗi', en: 'Fix Bug', fr: 'Corriger bug', es: 'Corregir error', id: 'Perbaiki bug', ja: 'バグ修正', zh: '修复缺陷', ko: '버그 수정', hi: 'बग ठीक करें', ar: 'إصلاح خطأ' } },
    { code: 'Improvement', label: { vi: 'Cải tiến', en: 'Improve', fr: 'Améliorer', es: 'Mejorar', id: 'Tingkatkan', ja: '改善', zh: '改进', ko: '개선', hi: 'सुधार', ar: 'تحسين' } },
    { code: 'Issue', label: { vi: 'Giải quyết sự cố', en: 'Resolve Issue', fr: 'Résoudre un incident', es: 'Resolver incidencia', id: 'Selesaikan masalah', ja: '問題解決', zh: '解决问题', ko: '이슈 해결', hi: 'समस्या हल करें', ar: 'حل مشكلة' } },
    { code: 'Inspection', label: { vi: 'Kiểm tra/QC', en: 'Inspect/QC', fr: 'Inspection/QC', es: 'Inspección/QC', id: 'Inspeksi/QC', ja: '検査/QC', zh: '检验/QC', ko: '검사/QC', hi: 'निरीक्षण/QC', ar: 'فحص/QC' } },
    { code: 'Milestone', label: { vi: 'Cột mốc', en: 'Milestone', fr: 'Jalon', es: 'Hito', id: 'Tonggak', ja: 'マイルストーン', zh: '里程碑', ko: '마일스톤', hi: 'माइलस्टोन', ar: 'مرحلة رئيسية' } },
    { code: 'ChangeRequest', label: { vi: 'Yêu cầu thay đổi', en: 'Change Request', fr: 'Changement', es: 'Solicitud de cambio', id: 'Permintaan perubahan', ja: '変更要求', zh: '变更请求', ko: '변경 요청', hi: 'परिवर्तन अनुरोध', ar: 'طلب تغيير' } }
];
var GT_PRIORITY_OPTIONS = [
    { code: 'Low', label: { vi: 'Thấp', en: 'Low', fr: 'Basse', es: 'Baja', id: 'Rendah', ja: '低', zh: '低', ko: '낮음', hi: 'कम', ar: 'منخفضة' } },
    { code: 'Normal', label: { vi: 'Bình thường', en: 'Normal', fr: 'Normale', es: 'Normal', id: 'Normal', ja: '通常', zh: '普通', ko: '보통', hi: 'सामान्य', ar: 'عادية' } },
    { code: 'High', label: { vi: 'Cao', en: 'High', fr: 'Haute', es: 'Alta', id: 'Tinggi', ja: '高', zh: '高', ko: '높음', hi: 'उच्च', ar: 'عالية' } },
    { code: 'Urgent', label: { vi: 'Khẩn cấp', en: 'Urgent', fr: 'Urgente', es: 'Urgente', id: 'Mendesak', ja: '緊急', zh: '紧急', ko: '긴급', hi: 'अत्यावश्यक', ar: 'عاجلة' } }
];
var GT_SEVERITY_OPTIONS = [
    { code: 'Trivial', label: { vi: 'Không đáng kể', en: 'Trivial', fr: 'Négligeable', es: 'Trivial', id: 'Sepele', ja: '軽微', zh: '轻微', ko: '사소함', hi: 'नगण्य', ar: 'طفيفة' } },
    { code: 'Minor', label: { vi: 'Nhỏ', en: 'Minor', fr: 'Mineure', es: 'Menor', id: 'Minor', ja: '小', zh: '次要', ko: '경미', hi: 'मामूली', ar: 'ثانوية' } },
    { code: 'Normal', label: { vi: 'Bình thường', en: 'Normal', fr: 'Normale', es: 'Normal', id: 'Normal', ja: '通常', zh: '普通', ko: '보통', hi: 'सामान्य', ar: 'عادية' } },
    { code: 'Major', label: { vi: 'Quan trọng', en: 'Major', fr: 'Majeure', es: 'Mayor', id: 'Mayor', ja: '重要', zh: '重要', ko: '중요', hi: 'प्रमुख', ar: 'رئيسية' } },
    { code: 'Critical', label: { vi: 'Nghiêm trọng', en: 'Critical', fr: 'Critique', es: 'Crítica', id: 'Kritis', ja: '重大', zh: '严重', ko: '심각', hi: 'गंभीर', ar: 'حرجة' } },
    { code: 'Blocker', label: { vi: 'Chặn', en: 'Blocker', fr: 'Bloquante', es: 'Bloqueante', id: 'Pemblokir', ja: 'ブロッカー', zh: '阻断', ko: '차단', hi: 'अवरोधक', ar: 'معيقة' } }
];
var GT_DELIVERY_OPTIONS = [
    { code: 'None', label: { vi: 'Không yêu cầu', en: 'None', fr: 'Aucun', es: 'Ninguno', id: 'Tidak ada', ja: 'なし', zh: '无', ko: '없음', hi: 'कोई नहीं', ar: 'لا شيء' } },
    { code: 'Report', label: { vi: 'Báo cáo', en: 'Report', fr: 'Rapport', es: 'Informe', id: 'Laporan', ja: 'レポート', zh: '报告', ko: '보고서', hi: 'रिपोर्ट', ar: 'تقرير' } },
    { code: 'Attachment', label: { vi: 'Đính kèm file', en: 'Attachment', fr: 'Pièce jointe', es: 'Adjunto', id: 'Lampiran', ja: '添付ファイル', zh: '附件', ko: '첨부 파일', hi: 'संलग्नक', ar: 'مرفق' } },
    { code: 'Approval', label: { vi: 'Cần duyệt', en: 'Approval', fr: 'Approbation', es: 'Aprobación', id: 'Persetujuan', ja: '承認', zh: '审批', ko: '승인', hi: 'अनुमोदन', ar: 'الموافقة' } }
];

function gtGetTaskTypeLabel(taskType) {
    var opt = GT_TYPE_OPTIONS.find(function (o) {
        return o.code === taskType;
    });
    return opt ? AntLocalization.translateText(opt.label) : taskType;
}

// ─── Reusable TaskDropdown (right sidebar copy) ─────────────────
function GTaskDropdown(props) {
    var options = props.options;
    var value = props.value;
    var onChange = props.onChange;
    var className = props.className || '';
    var [open, setOpen] = useState(false);
    var ref = useRef(null);

    var selected = options.find(function (o) {
        return o.code === value;
    });
    var label = selected ? AntLocalization.translateText(selected.label) : value || '';

    useEffect(
        function () {
            if (!open) return;
            var handleClickOutside = function (e) {
                if (ref.current && !ref.current.contains(e.target)) setOpen(false);
            };
            document.addEventListener('mousedown', handleClickOutside);
            return function () {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        },
        [open]
    );

    return (
        <div className={'task-dropdown' + (className ? ' ' + className : '') + (open ? ' open' : '')} ref={ref}>
            <button
                className="task-dropdown-trigger"
                onClick={function () {
                    setOpen(!open);
                }}
                type="button"
            >
                <span className="task-dropdown-label">{label}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            {open && (
                <div className="task-dropdown-menu">
                    {options.map(function (o) {
                        var isActive = o.code === value;
                        return (
                            <div
                                key={o.code}
                                className={'task-dropdown-item' + (isActive ? ' active' : '')}
                                onClick={function () {
                                    onChange(o.code);
                                    setOpen(false);
                                }}
                            >
                                <span>{AntLocalization.translateText(o.label)}</span>
                                {isActive && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Reusable TaskUserField (right sidebar copy) ────────────────
function GTaskUserField(props) {
    var userCode = props.userCode;
    var userName = props.userName;
    var onSelect = props.onSelect;
    var onClear = props.onClear;

    var [localSearch, setLocalSearch] = useState('');
    var [localResults, setLocalResults] = useState([]);
    var [localLoading, setLocalLoading] = useState(false);
    var [showList, setShowList] = useState(false);
    var localTimer = useRef(null);
    var wrapRef = useRef(null);
    var userAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserAccount');
    }, []);

    var doSearch = useCallback(
        function (text) {
            setLocalLoading(true);
            var params = { pageSize: 30, pageIndex: 0 };
            if (text && text.length > 0) params.keyword = text;
            userAPI.post('/' + userAPI.routeURL + '/list', params, function (result) {
                var rows =
                    (result && result.data && result.data.rows) ||
                    (result && result.rows) ||
                    (result && result.data instanceof Array ? result.data : []);
                setLocalResults(rows instanceof Array ? rows : []);
                setLocalLoading(false);
            });
        },
        [userAPI]
    );

    var handleSearchChange = useCallback(
        function (e) {
            var val = e.target.value;
            setLocalSearch(val);
            if (localTimer.current) clearTimeout(localTimer.current);
            localTimer.current = setTimeout(function () {
                doSearch(val.trim());
            }, 300);
        },
        [doSearch]
    );

    var handleFocus = useCallback(
        function () {
            setShowList(true);
            if (localResults.length === 0) doSearch('');
        },
        [localResults, doSearch]
    );

    useEffect(
        function () {
            if (!showList) return;
            var handleClickOutside = function (e) {
                if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowList(false);
            };
            document.addEventListener('mousedown', handleClickOutside);
            return function () {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        },
        [showList]
    );

    var getInitial = function (name) {
        return name ? name.charAt(0).toUpperCase() : '?';
    };

    if (userCode) {
        return (
            <div className="task-assignee-selected">
                <span className="task-assignee-avatar">{getInitial(userName || userCode)}</span>
                <span className="task-assignee-name">{userName || userCode}</span>
                <button
                    className="task-assignee-clear"
                    onClick={function () {
                        onClear();
                        setLocalSearch('');
                        setShowList(false);
                    }}
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>
        );
    }

    return (
        <div ref={wrapRef}>
            <div className="task-user-search-wrap">
                <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ flexShrink: 0, color: '#8da4c8' }}
                >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                    className="task-user-search-input"
                    value={localSearch}
                    onChange={handleSearchChange}
                    onFocus={handleFocus}
                    placeholder={AntLocalization.translateText({ vi: 'Tìm theo tên, mã...', en: 'Search by name or code...', fr: 'Rechercher par nom ou code...', es: 'Buscar por nombre o código...', id: 'Cari nama atau kode...', ja: '名前またはコードで検索...', zh: '按名称或编码搜索...', ko: '이름 또는 코드로 검색...', hi: 'नाम या कोड से खोजें...', ar: 'البحث بالاسم أو الرمز...' })}
                />
            </div>
            {showList && (
                <div className="task-user-list">
                    {localLoading && (
                        <div className="task-user-list-msg">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा है...', ar: 'جارٍ التحميل...' })}</div>
                    )}
                    {!localLoading && localResults.length === 0 && (
                        <div className="task-user-list-msg">{AntLocalization.translateText({ vi: 'Không tìm thấy', en: 'Not found', fr: 'Aucun résultat', es: 'No encontrado', id: 'Tidak ditemukan', ja: '見つかりません', zh: '未找到', ko: '찾을 수 없음', hi: 'नहीं मिला', ar: 'لم يتم العثور' })}</div>
                    )}
                    {localResults.map(function (u) {
                        var uName = u.user_name || u.full_name || u.name || u.user_code || u.code || '';
                        var uCode = u.user_code || u.code || '';
                        return (
                            <div
                                key={u.id || uCode}
                                className="task-user-item"
                                onClick={function () {
                                    onSelect(uCode, uName);
                                    setShowList(false);
                                    setLocalSearch('');
                                }}
                            >
                                <span className="task-user-avatar">{getInitial(uName)}</span>
                                <div className="task-user-info">
                                    <div className="task-user-name">{uName}</div>
                                    {uCode && <div className="task-user-code">{uCode}</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── GlobalTaskPanel (rewritten with quality matching left sidebar) ──
function GlobalTaskPanel(props) {
    var onNavigate = props.onNavigate;
    var panelNav = useNavigate();
    var taskAPI = useMemo(function () {
        return new AntAPI('of', 'OFTask');
    }, []);
    var confirm = useConfirm();

    var [tasks, setTasks] = useState([]);
    var [loading, setLoading] = useState(false);
    var [taskSearchText, setTaskSearchText] = useState('');
    var [taskFilter, setTaskFilter] = useState('assigned'); // assigned | created | all
    var [taskView, setTaskView] = useState('list'); // list | create | detail | settings
    var [activeTask, setActiveTask] = useState(null);
    var [creating, setCreating] = useState(false);
    var [saving, setSaving] = useState(false);
    var [isDirty, setIsDirty] = useState(false);
    var [showComment, setShowComment] = useState(false);
    var [childTasks, setChildTasks] = useState([]);
    var [taskComments, setTaskComments] = useState([]);
    var rtCommentAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserComment');
    }, []);

    // Create form state
    var [newTitle, setNewTitle] = useState('');
    var [newDesc, setNewDesc] = useState('');
    var [newTaskType, setNewTaskType] = useState('Task');
    var [newPriority, setNewPriority] = useState('Normal');
    var [newUserCode, setNewUserCode] = useState('');
    var [newUserName, setNewUserName] = useState('');
    var [newSupportCode, setNewSupportCode] = useState('');
    var [newSupportName, setNewSupportName] = useState('');

    // Edit state (settings view)
    var [editData, setEditData] = useState({});

    var currentUserCode = useMemo(function () {
        var session = AntSession ? AntSession() : null;
        return session ? session.user_code : null;
    }, []);

    // ─── Load (correct API per filter) ──────────────────────────
    var loadTasks = useCallback(
        function () {
            setLoading(true);
            var url = taskFilter === 'assigned' ? '/mytask/list' : taskFilter === 'created' ? '/assigned/list' : '/visible/list';
            taskAPI.post('/' + taskAPI.routeURL + url, { pageSize: 50 }, function (result) {
                var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                setTasks(rows instanceof Array ? rows : []);
                setLoading(false);
            });
        },
        [taskFilter, taskAPI]
    );

    useEffect(
        function () {
            loadTasks();
        },
        [taskFilter]
    );

    // ─── Open create view ───────────────────────────────────────
    var openCreateView = useCallback(function () {
        setNewTitle('');
        setNewDesc('');
        setNewTaskType('Task');
        setNewPriority('Normal');
        setNewUserCode('');
        setNewUserName('');
        setNewSupportCode('');
        setNewSupportName('');
        setTaskView('create');
    }, []);

    // ─── Create ─────────────────────────────────────────────────
    var handleCreate = useCallback(
        function () {
            var title = newTitle.trim();
            if (!title) return;
            setCreating(true);
            var data = {
                type: 'FlyTask',
                title: title,
                task_type: newTaskType || 'Task',
                priority: newPriority || 'Normal'
            };
            if (newDesc.trim()) data.description = newDesc.trim();
            if (newUserCode) data.user_code = newUserCode;
            if (newSupportCode) data.support1_user_code = newSupportCode;
            taskAPI.post('/' + taskAPI.routeURL + '/create', data, function () {
                setCreating(false);
                setNewTitle('');
                setNewDesc('');
                setNewTaskType('Task');
                setNewPriority('Normal');
                setNewUserCode('');
                setNewUserName('');
                setNewSupportCode('');
                setNewSupportName('');
                setTaskView('list');
                loadTasks();
            });
        },
        [newTitle, newDesc, newTaskType, newPriority, newUserCode, newSupportCode, taskAPI, loadTasks]
    );

    var handleCreateKeyDown = useCallback(
        function (e) {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleCreate();
            }
            if (e.key === 'Escape') {
                setTaskView('list');
            }
        },
        [handleCreate]
    );

    // ─── Open detail ────────────────────────────────────────────
    var openTask = useCallback(
        function (task) {
            setActiveTask(task);
            setEditData({
                title: task.title || '',
                description: task.description || '',
                task_type: task.task_type || 'Task',
                priority: task.priority || 'Normal',
                severity: task.severity || 'Normal',
                due_date: task.due_date || '',
                delivery_type: task.delivery_type || 'None',
                delivery_description: task.delivery_description || '',
                user_code: task.user_code || '',
                user_name: task.user_name || '',
                manage_user_code: task.manage_user_code || '',
                support1_user_code: task.support1_user_code || '',
                support1_user_name: task.support1_user_name || ''
            });
            setIsDirty(false);
            setShowComment(false);
            setTaskView('detail');
            // Load child tasks
            taskAPI.post(
                '/' + taskAPI.routeURL + '/list',
                {
                    parent_task_id: task.id,
                    pageSize: 20,
                    ordering: [{ name: 'id', type: 'desc' }]
                },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || [];
                    setChildTasks(rows instanceof Array ? rows : []);
                }
            );
            // Load comments
            rtCommentAPI.post(
                '/' + rtCommentAPI.routeURL + '/list',
                {
                    voucher_type: 'OFTask',
                    voucher_id: task.id,
                    voucher_code: task.code,
                    pageSize: 10,
                    pageIndex: 0,
                    ordering: [{ name: 'id', type: 'desc' }]
                },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || [];
                    setTaskComments((rows instanceof Array ? rows : []).slice().reverse());
                }
            );
        },
        [taskAPI, rtCommentAPI]
    );

    var backToList = useCallback(
        function () {
            setActiveTask(null);
            setTaskView('list');
            setIsDirty(false);
            setShowComment(false);
            loadTasks();
        },
        [loadTasks]
    );

    // ─── Save (settings) ────────────────────────────────────────
    var handleSave = useCallback(
        function () {
            if (!activeTask || !isDirty) return;
            setSaving(true);
            var update = {};
            var ed = editData;
            if (ed.title !== undefined) update.title = (ed.title || '').trim();
            if (ed.description !== undefined) update.description = (ed.description || '').trim();
            if (ed.task_type) update.task_type = ed.task_type;
            if (ed.priority) update.priority = ed.priority;
            if (ed.severity) update.severity = ed.severity;
            if (ed.due_date !== undefined) update.due_date = ed.due_date || null;
            if (ed.delivery_type) update.delivery_type = ed.delivery_type;
            if (ed.delivery_description !== undefined) update.delivery_description = ed.delivery_description;
            if (ed.user_code !== undefined) update.user_code = ed.user_code || null;
            if (ed.manage_user_code !== undefined) update.manage_user_code = ed.manage_user_code || null;
            if (ed.support1_user_code !== undefined) update.support1_user_code = ed.support1_user_code || null;

            taskAPI.post(
                '/' + taskAPI.routeURL + '/update',
                {
                    where: { id: activeTask.id },
                    update: update
                },
                function () {
                    setSaving(false);
                    setIsDirty(false);
                    setActiveTask(function (prev) {
                        return prev ? Object.assign({}, prev, update) : prev;
                    });
                    loadTasks();
                }
            );
        },
        [activeTask, editData, isDirty, taskAPI, loadTasks]
    );

    var updateEditField = useCallback(function (field, value) {
        setEditData(function (prev) {
            var n = Object.assign({}, prev);
            n[field] = value;
            return n;
        });
        setIsDirty(true);
    }, []);

    // ─── Quick actions (status change) ──────────────────────────
    var handleQuickAction = useCallback(
        function (actionName) {
            if (!activeTask) return;
            taskAPI.post('/' + taskAPI.routeURL + '/' + actionName, { id: activeTask.id }, function (result) {
                var data = (result && result.data) || result;
                if (data && data.error) {
                    if (typeof window !== 'undefined' && window.alert) window.alert(data.message || data.errorMessage || 'Action failed');
                    return;
                }
                if (data) {
                    setActiveTask(function (prev) {
                        if (!prev) return prev;
                        var updated = Object.assign({}, prev);
                        if (data.task_status) updated.task_status = data.task_status;
                        if (data.started_at) updated.started_at = data.started_at;
                        if (data.completed_at) updated.completed_at = data.completed_at;
                        if (data.delivery_status) updated.delivery_status = data.delivery_status;
                        if (data.progress_pct != null) updated.progress_pct = data.progress_pct;
                        return updated;
                    });
                    loadTasks();
                }
            });
        },
        [activeTask, taskAPI, loadTasks]
    );

    // ─── Delete ─────────────────────────────────────────────────
    var handleDelete = useCallback(
        function () {
            if (!activeTask) return;
            confirm.show(AntLocalization.translateText({ vi: 'Xoá công việc này?', en: 'Delete this task?', fr: 'Supprimer cette tâche ?', es: '¿Eliminar esta tarea?', id: 'Hapus tugas ini?', ja: 'このタスクを削除しますか？', zh: '删除此任务？', ko: '이 작업을 삭제할까요?', hi: 'यह कार्य हटाएँ?', ar: 'حذف هذه المهمة؟' }), function () {
                taskAPI.post('/' + taskAPI.routeURL + '/delete', { id: activeTask.id }, function () {
                    backToList();
                });
            });
        },
        [activeTask, confirm, taskAPI, backToList]
    );

    // ─── Navigate to voucher ────────────────────────────────────
    var handleOpenVoucher = useCallback(
        function () {
            if (!activeTask || !activeTask.voucher_type || !activeTask.voucher_code) return;
            var urlMap = {
                AROrder: '/ar/AROrder/',
                APOrder: '/ap/APOrder/',
                EXPOrder: '/exp/EXPOrder/',
                PMProject: '/pm/PMProject/',
                PMFeature: '/pm/PMFeature/'
            };
            var prefix = urlMap[activeTask.voucher_type];
            if (prefix) panelNav(prefix + activeTask.voucher_code);
        },
        [activeTask, panelNav]
    );

    // ─── Create sub-task ────────────────────────────────────────
    var handleCreateSubTask = useCallback(
        function () {
            if (!activeTask) return;
            var subTitle = window.prompt(AntLocalization.translateText({ vi: 'Tiêu đề task con:', en: 'Sub-task title:', fr: 'Titre sous-tâche :', es: 'Título de la subtarea:', id: 'Judul subtugas:', ja: 'サブタスクのタイトル:', zh: '子任务标题：', ko: '하위 작업 제목:', hi: 'उप-कार्य शीर्षक:', ar: 'عنوان المهمة الفرعية:' }));
            if (!subTitle || !subTitle.trim()) return;
            taskAPI.post(
                '/' + taskAPI.routeURL + '/create',
                {
                    type: 'FlyTask',
                    title: subTitle.trim(),
                    parent_task_id: activeTask.id,
                    parent_task_code: activeTask.code
                },
                function () {
                    openTask(activeTask);
                    loadTasks();
                }
            );
        },
        [activeTask, taskAPI, openTask, loadTasks]
    );

    var canManage =
        activeTask &&
        (activeTask.manage_user_code === currentUserCode ||
            activeTask.request_user_code === currentUserCode ||
            (!activeTask.manage_user_code && activeTask.user_code === currentUserCode));
    var isAssignee = activeTask && activeTask.user_code === currentUserCode;

    // ═══ FLOATING COMMENT (dùng FloatingCommentPanel reusable) ════
    var commentContext =
        showComment && activeTask
            ? { voucherType: 'OFTask', voucherId: activeTask.id, voucherCode: activeTask.code, voucherTypeLabel: activeTask.title }
            : null;

    // ═══ CREATE VIEW ════════════════════════════════════════════
    if (taskView === 'create') {
        return (
            <div className="chat-panel">
                <div className="task-subview-header">
                    <button
                        className="task-back-btn"
                        onClick={function () {
                            setTaskView('list');
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="task-subview-title">{AntLocalization.translateText({ vi: 'Tạo công việc', en: 'New Task', fr: 'Nouvelle tâche', es: 'Nueva tarea', id: 'Tugas baru', ja: '新規タスク', zh: '新建任务', ko: '새 작업', hi: 'नया कार्य', ar: 'مهمة جديدة' })}</span>
                </div>
                <div className="task-create-body">
                    <textarea
                        className="task-create-title-input"
                        value={newTitle}
                        onChange={function (e) {
                            setNewTitle(e.target.value);
                        }}
                        onKeyDown={handleCreateKeyDown}
                        rows={1}
                        placeholder={AntLocalization.translateText({ vi: 'Tiêu đề công việc...', en: 'Task title...', fr: 'Titre de la tâche...', es: 'Título de tarea...', id: 'Judul tugas...', ja: 'タスクのタイトル...', zh: '任务标题...', ko: '작업 제목...', hi: 'कार्य शीर्षक...', ar: 'عنوان المهمة...' })}
                        autoFocus
                        disabled={creating}
                    />
                    <textarea
                        className="task-create-desc-input"
                        value={newDesc}
                        onChange={function (e) {
                            setNewDesc(e.target.value);
                        }}
                        onKeyDown={handleCreateKeyDown}
                        rows={2}
                        placeholder={AntLocalization.translateText({ vi: 'Mô tả (tùy chọn)...', en: 'Description (optional)...', fr: 'Description (facultatif)...', es: 'Descripción (opcional)...', id: 'Deskripsi (opsional)...', ja: '説明（任意）...', zh: '描述（可选）...', ko: '설명(선택)...', hi: 'विवरण (वैकल्पिक)...', ar: 'الوصف (اختياري)...' })}
                        disabled={creating}
                    />
                    <div className="task-create-divider" />
                    <div className="task-create-row">
                        <div className="task-create-field">
                            <span className="task-create-field-label">{AntLocalization.translateText({ vi: 'Loại', en: 'Type', fr: 'Type', es: 'Tipo', id: 'Jenis', ja: '種類', zh: '类型', ko: '유형', hi: 'प्रकार', ar: 'النوع' })}</span>
                            <GTaskDropdown
                                options={GT_TYPE_OPTIONS.slice(0, 5)}
                                value={newTaskType}
                                onChange={function (v) {
                                    setNewTaskType(v);
                                }}
                            />
                        </div>
                        <div className="task-create-field">
                            <span className="task-create-field-label">{AntLocalization.translateText({ vi: 'Ưu tiên', en: 'Priority', fr: 'Priorité', es: 'Prioridad', id: 'Prioritas', ja: '優先度', zh: '优先级', ko: '우선순위', hi: 'प्राथमिकता', ar: 'الأولوية' })}</span>
                            <GTaskDropdown
                                options={GT_PRIORITY_OPTIONS}
                                value={newPriority}
                                onChange={function (v) {
                                    setNewPriority(v);
                                }}
                            />
                        </div>
                    </div>
                    <div className="task-create-divider" />
                    <div className="task-create-field">
                        <span className="task-create-field-label">{AntLocalization.translateText({ vi: 'Người được giao', en: 'Assignee', fr: 'Responsable', es: 'Responsable', id: 'Penerima tugas', ja: '担当者', zh: '负责人', ko: '담당자', hi: 'कार्यकर्ता', ar: 'المكلَّف' })}</span>
                        <GTaskUserField
                            userCode={newUserCode}
                            userName={newUserName}
                            onSelect={function (code, name) {
                                setNewUserCode(code);
                                setNewUserName(name);
                            }}
                            onClear={function () {
                                setNewUserCode('');
                                setNewUserName('');
                            }}
                        />
                    </div>
                    <div className="task-create-field">
                        <span className="task-create-field-label">{AntLocalization.translateText({ vi: 'Người hỗ trợ', en: 'Support', fr: 'Soutien', es: 'Apoyo', id: 'Pendukung', ja: 'サポート担当', zh: '协助人', ko: '지원자', hi: 'सहयोगी', ar: 'الداعم' })}</span>
                        <GTaskUserField
                            userCode={newSupportCode}
                            userName={newSupportName}
                            onSelect={function (code, name) {
                                setNewSupportCode(code);
                                setNewSupportName(name);
                            }}
                            onClear={function () {
                                setNewSupportCode('');
                                setNewSupportName('');
                            }}
                        />
                    </div>
                </div>
                <div className="task-create-footer">
                    <button
                        className="task-create-cancel"
                        onClick={function () {
                            setTaskView('list');
                        }}
                    >
                        {AntLocalization.translateText({ vi: 'Hủy', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', ja: 'キャンセル', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء' })}
                    </button>
                    <button className="task-create-submit" onClick={handleCreate} disabled={!newTitle.trim() || creating}>
                        {creating
                            ? AntLocalization.translateText({ vi: 'Đang tạo...', en: 'Creating...', fr: 'Création...', es: 'Creando...', id: 'Membuat...', ja: '作成中...', zh: '创建中...', ko: '만드는 중...', hi: 'बनाया जा रहा है...', ar: 'جارٍ الإنشاء...' })
                            : AntLocalization.translateText({ vi: 'Tạo công việc', en: 'Create Task', fr: 'Créer la tâche', es: 'Crear tarea', id: 'Buat tugas', ja: 'タスクを作成', zh: '创建任务', ko: '작업 만들기', hi: 'कार्य बनाएँ', ar: 'إنشاء مهمة' })}
                    </button>
                </div>
            </div>
        );
    }

    // ═══ SETTINGS VIEW ══════════════════════════════════════════
    if (taskView === 'settings' && activeTask) {
        var ed = editData;
        var isBugEdit = ed.task_type === 'Bug' || ed.task_type === 'Issue';
        return (
            <div className="chat-panel">
                {confirm.el}
                <div className="task-subview-header">
                    <button
                        className="task-back-btn"
                        onClick={function () {
                            if (isDirty) {
                                handleSave();
                            }
                            setTaskView('detail');
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="task-subview-title">{AntLocalization.translateText({ vi: 'Chỉnh sửa', en: 'Edit', fr: 'Modifier', es: 'Editar', id: 'Ubah', ja: '編集', zh: '编辑', ko: '편집', hi: 'संपादित करें', ar: 'تعديل' })}</span>
                    {isDirty && (
                        <button className="task-save-btn" onClick={handleSave} disabled={saving}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M5 13l4 4L19 7" />
                            </svg>
                        </button>
                    )}
                </div>
                <div className="task-settings-body">
                    <div className="task-settings-section">
                        <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Tiêu đề', en: 'Title', fr: 'Titre', es: 'Título', id: 'Judul', ja: 'タイトル', zh: '标题', ko: '제목', hi: 'शीर्षक', ar: 'العنوان' })}</span>
                        <input
                            className="task-settings-input"
                            value={ed.title || ''}
                            onChange={function (e) {
                                updateEditField('title', e.target.value);
                            }}
                        />
                    </div>
                    <div className="task-settings-section">
                        <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Mô tả', en: 'Description', fr: 'Description', es: 'Descripción', id: 'Deskripsi', ja: '説明', zh: '描述', ko: '설명', hi: 'विवरण', ar: 'الوصف' })}</span>
                        <textarea
                            className="task-settings-input"
                            value={ed.description || ''}
                            rows={3}
                            onChange={function (e) {
                                updateEditField('description', e.target.value);
                            }}
                        />
                    </div>
                    <div className="task-create-row">
                        <div className="task-settings-section">
                            <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Loại', en: 'Type', fr: 'Type', es: 'Tipo', id: 'Jenis', ja: '種類', zh: '类型', ko: '유형', hi: 'प्रकार', ar: 'النوع' })}</span>
                            <GTaskDropdown
                                options={GT_TYPE_OPTIONS}
                                value={ed.task_type}
                                onChange={function (v) {
                                    updateEditField('task_type', v);
                                }}
                            />
                        </div>
                        <div className="task-settings-section">
                            <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Ưu tiên', en: 'Priority', fr: 'Priorité', es: 'Prioridad', id: 'Prioritas', ja: '優先度', zh: '优先级', ko: '우선순위', hi: 'प्राथमिकता', ar: 'الأولوية' })}</span>
                            <GTaskDropdown
                                options={GT_PRIORITY_OPTIONS}
                                value={ed.priority}
                                onChange={function (v) {
                                    updateEditField('priority', v);
                                }}
                            />
                        </div>
                    </div>
                    {isBugEdit && (
                        <div className="task-settings-section">
                            <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Mức độ', en: 'Severity', fr: 'Gravité', es: 'Gravedad', id: 'Keparahan', ja: '重大度', zh: '严重程度', ko: '심각도', hi: 'गंभीरता', ar: 'الخطورة' })}</span>
                            <GTaskDropdown
                                options={GT_SEVERITY_OPTIONS}
                                value={ed.severity}
                                onChange={function (v) {
                                    updateEditField('severity', v);
                                }}
                            />
                        </div>
                    )}
                    <div className="task-settings-section">
                        <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Kết quả cần giao', en: 'Delivery', fr: 'Livrable', es: 'Entregable', id: 'Hasil kerja', ja: '成果物', zh: '交付成果', ko: '산출물', hi: 'डिलिवरेबल', ar: 'المخرجات' })}</span>
                        <GTaskDropdown
                            options={GT_DELIVERY_OPTIONS}
                            value={ed.delivery_type}
                            onChange={function (v) {
                                updateEditField('delivery_type', v);
                            }}
                        />
                        {ed.delivery_type && ed.delivery_type !== 'None' && (
                            <textarea
                                className="task-settings-input"
                                style={{ marginTop: 6 }}
                                value={ed.delivery_description || ''}
                                rows={2}
                                onChange={function (e) {
                                    updateEditField('delivery_description', e.target.value);
                                }}
                                placeholder={AntLocalization.translateText({ vi: 'Mô tả kết quả cần nộp...', en: 'Describe deliverables...', fr: 'Décrire les livrables...', es: 'Describa los entregables...', id: 'Jelaskan hasil kerja...', ja: '成果物を記述...', zh: '描述交付成果...', ko: '산출물 설명...', hi: 'डिलिवरेबल का वर्णन करें...', ar: 'صف المخرجات...' })}
                            />
                        )}
                    </div>
                    <div className="task-settings-section">
                        <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Người được giao', en: 'Assignee', fr: 'Responsable', es: 'Responsable', id: 'Penerima tugas', ja: '担当者', zh: '负责人', ko: '담당자', hi: 'कार्यकर्ता', ar: 'المكلَّف' })}</span>
                        <GTaskUserField
                            userCode={ed.user_code}
                            userName={ed.user_name || (activeTask && activeTask.user_name) || ''}
                            onSelect={function (code, name) {
                                updateEditField('user_code', code);
                                updateEditField('user_name', name);
                            }}
                            onClear={function () {
                                updateEditField('user_code', '');
                                updateEditField('user_name', '');
                            }}
                        />
                    </div>
                    <div className="task-settings-section">
                        <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Người hỗ trợ', en: 'Support', fr: 'Soutien', es: 'Apoyo', id: 'Pendukung', ja: 'サポート担当', zh: '协助人', ko: '지원자', hi: 'सहयोगी', ar: 'الداعم' })}</span>
                        <GTaskUserField
                            userCode={ed.support1_user_code}
                            userName={ed.support1_user_name || (activeTask && activeTask.support1_user_name) || ''}
                            onSelect={function (code, name) {
                                updateEditField('support1_user_code', code);
                                updateEditField('support1_user_name', name);
                            }}
                            onClear={function () {
                                updateEditField('support1_user_code', '');
                                updateEditField('support1_user_name', '');
                            }}
                        />
                    </div>
                    {canManage && (
                        <div className="task-settings-danger">
                            <button className="task-settings-delete" onClick={handleDelete}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Xoá công việc', en: 'Delete task', fr: 'Supprimer la tâche', es: 'Eliminar tarea', id: 'Hapus tugas', ja: 'タスクを削除', zh: '删除任务', ko: '작업 삭제', hi: 'कार्य हटाएँ', ar: 'حذف المهمة' })}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ═══ DETAIL VIEW ════════════════════════════════════════════
    if (taskView === 'detail' && activeTask) {
        var sc = gtGetStatusColor(activeTask.task_status);
        var canEdit2 = canManage;
        var isAssignee2 = isAssignee;
        var tt2 = activeTask.task_type || 'Task';
        var isOverdue2 = activeTask.due_date && ['Close', 'Done', 'Cancel'].indexOf(activeTask.task_status) < 0;
        var priColor2 = gtGetPriorityColor(activeTask.priority);

        // Main action
        var mainAction = null;
        if (activeTask.task_status === 'New' && canEdit2)
            mainAction = { name: 'actionRelease', label: AntLocalization.translateText({ vi: 'Giao việc', en: 'Assign', fr: 'Attribuer', es: 'Asignar', id: 'Tugaskan', ja: '割り当て', zh: '分配', ko: '배정', hi: 'सौंपें', ar: 'تعيين' }), color: '#5b8fd9' };
        if (activeTask.task_status === 'ToDo' && (isAssignee2 || canEdit2))
            mainAction = {
                name: 'actionStart',
                label: AntLocalization.translateText({ vi: 'Bắt đầu thực hiện', en: 'Start Working', fr: 'Commencer', es: 'Comenzar', id: 'Mulai kerjakan', ja: '作業開始', zh: '开始执行', ko: '작업 시작', hi: 'काम शुरू करें', ar: 'بدء العمل' }),
                color: '#5b8fd9'
            };
        if (activeTask.task_status === 'WIP' && (isAssignee2 || canEdit2))
            mainAction = { name: 'actionDone', label: AntLocalization.translateText({ vi: 'Hoàn tất', en: 'Mark as Done', fr: 'Terminer', es: 'Completar', id: 'Tandai selesai', ja: '完了にする', zh: '标记为完成', ko: '완료로 표시', hi: 'पूर्ण करें', ar: 'تعليم كمكتمل' }), color: '#3da06a' };
        if ((activeTask.task_status === 'Done' || activeTask.task_status === 'Failed') && canEdit2)
            mainAction = { name: 'actionClose', label: AntLocalization.translateText({ vi: 'Đóng công việc', en: 'Close Task', fr: 'Clôturer la tâche', es: 'Cerrar tarea', id: 'Tutup tugas', ja: 'タスクを閉じる', zh: '关闭任务', ko: '작업 종료', hi: 'कार्य बंद करें', ar: 'إغلاق المهمة' }), color: '#64748b' };

        // Info rows
        var infoRows = [];
        if (activeTask.user_name)
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Thực hiện', en: 'Assignee', fr: 'Responsable', es: 'Responsable', id: 'Pelaksana', ja: '担当者', zh: '执行人', ko: '담당자', hi: 'कार्यकर्ता', ar: 'المنفذ' }),
                value: activeTask.entity_full_name || activeTask.entity_name || activeTask.user_name
            });
        if (activeTask.manage_user_name)
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Quản lý', en: 'Manager', fr: 'Responsable', es: 'Responsable', id: 'Manajer', ja: '管理者', zh: '负责人', ko: '관리자', hi: 'प्रबंधक', ar: 'المدير' }),
                value: activeTask.manage_entity_full_name || activeTask.manage_entity_name || activeTask.manage_user_name
            });
        if (activeTask.support1_user_name)
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Hỗ trợ', en: 'Support', fr: 'Soutien', es: 'Apoyo', id: 'Pendukung', ja: 'サポート', zh: '协助', ko: '지원', hi: 'सहयोग', ar: 'الدعم' }),
                value: activeTask.support1_entity_full_name || activeTask.support1_entity_name || activeTask.support1_user_name
            });
        if (activeTask.user_group_name)
            infoRows.push({ label: AntLocalization.translateText({ vi: 'Phòng ban', en: 'Department', fr: 'Service', es: 'Departamento', id: 'Departemen', ja: '部署', zh: '部门', ko: '부서', hi: 'विभाग', ar: 'القسم' }), value: activeTask.user_group_name });
        if (activeTask.due_date)
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Hạn', en: 'Due', fr: 'Échéance', es: 'Vencimiento', id: 'Tenggat', ja: '期限', zh: '截止', ko: '마감', hi: 'नियत तिथि', ar: 'الاستحقاق' }),
                value: commentFormatTime(activeTask.due_date),
                color: isOverdue2 ? '#dc2626' : null
            });
        if (activeTask.severity && activeTask.severity !== 'Normal' && (tt2 === 'Bug' || tt2 === 'Issue'))
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Mức độ', en: 'Severity', fr: 'Gravité', es: 'Gravedad', id: 'Keparahan', ja: '重大度', zh: '严重程度', ko: '심각도', hi: 'गंभीरता', ar: 'الخطورة' }),
                value: AntLocalization.translateText(
                    (
                        GT_SEVERITY_OPTIONS.find(function (o) {
                            return o.code === activeTask.severity;
                        }) || {}
                    ).label || { vi: activeTask.severity, en: activeTask.severity }
                ),
                color: activeTask.severity === 'Critical' || activeTask.severity === 'Blocker' ? '#dc2626' : '#d97706'
            });
        if (activeTask.ref_entity_name)
            infoRows.push({ label: AntLocalization.translateText({ vi: 'Liên quan', en: 'Related', fr: 'Lié', es: 'Relacionado', id: 'Terkait', ja: '関連', zh: '相关', ko: '관련', hi: 'संबंधित', ar: 'ذو صلة' }), value: activeTask.ref_entity_name });
        if (activeTask.store_name)
            infoRows.push({ label: AntLocalization.translateText({ vi: 'Địa điểm', en: 'Location', fr: 'Lieu', es: 'Ubicación', id: 'Lokasi', ja: '場所', zh: '地点', ko: '장소', hi: 'स्थान', ar: 'الموقع' }), value: activeTask.store_name });
        if (activeTask.request_user_name)
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Tạo bởi', en: 'Created by', fr: 'Créé par', es: 'Creado por', id: 'Dibuat oleh', ja: '作成者', zh: '创建人', ko: '작성자', hi: 'द्वारा बनाया', ar: 'أنشأه' }),
                value: activeTask.request_entity_full_name || activeTask.request_entity_name || activeTask.request_user_name
            });

        return (
            <div className="chat-panel">
                {confirm.el}
                <FloatingCommentPanel
                    visible={showComment}
                    onClose={function () {
                        setShowComment(false);
                    }}
                    context={commentContext}
                    title={activeTask ? activeTask.title : ''}
                />

                {/* Header */}
                <div className="task-subview-header">
                    <button className="task-back-btn" onClick={backToList}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="task-subview-title">{activeTask.title}</span>
                    {canEdit2 && (
                        <button
                            className="task-gear-btn"
                            onClick={function () {
                                setTaskView('settings');
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                        </button>
                    )}
                </div>

                <div className="task-detail-body">
                    {/* Zone 1: Status + Type + Priority badges */}
                    <div className="task-detail-zone zone-status">
                        <span className="task-detail-status-badge" style={{ color: sc, background: sc + '14', border: '1px solid ' + sc + '30' }}>
                            {gtGetStatusLabel(activeTask.task_status)}
                        </span>
                        {tt2 !== 'Task' && <span className="task-detail-type-badge">{gtGetTaskTypeLabel(tt2)}</span>}
                        {activeTask.priority && activeTask.priority !== 'Normal' && (
                            <span
                                className="task-detail-priority-badge"
                                style={{ color: priColor2 || '#64748b', background: (priColor2 || '#64748b') + '14' }}
                            >
                                {AntLocalization.translateText(
                                    (
                                        GT_PRIORITY_OPTIONS.find(function (o) {
                                            return o.code === activeTask.priority;
                                        }) || {}
                                    ).label || { vi: activeTask.priority, en: activeTask.priority }
                                )}
                            </span>
                        )}
                    </div>

                    {/* Zone 2: Info rows */}
                    {infoRows.length > 0 && (
                        <div className="task-detail-zone">
                            <div className="task-info-grid">
                                {infoRows.map(function (row, ri) {
                                    return (
                                        <div key={ri} className="task-info-row">
                                            <span className="task-info-label">{row.label}</span>
                                            <span className="task-info-value" style={row.color ? { color: row.color } : null}>
                                                {row.value}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Zone 3: Progress */}
                    {activeTask.progress_pct > 0 && (
                        <div className="task-detail-zone">
                            <div className="task-progress-label">
                                <span>{AntLocalization.translateText({ vi: 'Tiến độ', en: 'Progress', fr: 'Avancement', es: 'Progreso', id: 'Progres', ja: '進捗', zh: '进度', ko: '진행률', hi: 'प्रगति', ar: 'التقدم' })}</span>
                                <span>{Math.round(activeTask.progress_pct)}%</span>
                            </div>
                            <div className="task-progress-bar">
                                <div
                                    className="task-progress-fill"
                                    style={{
                                        width: activeTask.progress_pct + '%',
                                        background: activeTask.progress_pct >= 100 ? '#22c55e' : '#3b82f6'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Zone 4: Description */}
                    {activeTask.description && (
                        <div className="task-detail-zone">
                            <span className="task-zone-title">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 6h16M4 12h10M4 18h14" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Mô tả', en: 'Description', fr: 'Description', es: 'Descripción', id: 'Deskripsi', ja: '説明', zh: '描述', ko: '설명', hi: 'विवरण', ar: 'الوصف' })}
                            </span>
                            <div className="task-detail-desc">{activeTask.description}</div>
                        </div>
                    )}

                    {/* Zone 5: Source voucher link */}
                    {activeTask.voucher_type && activeTask.voucher_code && (
                        <div className="task-detail-zone">
                            <span className="task-zone-title">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Phiếu gốc', en: 'Source', fr: 'Origine', es: 'Origen', id: 'Sumber', ja: '元伝票', zh: '源单据', ko: '원본 문서', hi: 'स्रोत', ar: 'المصدر' })}
                            </span>
                            <button className="task-settings-link" onClick={handleOpenVoucher}>
                                {activeTask.voucher_type} — {activeTask.voucher_code}
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {/* Zone 6: Main action */}
                    {mainAction && (
                        <div className="task-detail-zone task-action-row">
                            <button
                                className="task-main-action"
                                style={{ background: mainAction.color }}
                                onClick={function () {
                                    handleQuickAction(mainAction.name);
                                }}
                            >
                                {mainAction.label}
                            </button>
                            {activeTask.task_status === 'WIP' && (isAssignee2 || canEdit2) && (
                                <button
                                    className="task-secondary-action"
                                    style={{ color: '#9e5050', borderColor: '#d4b0b0' }}
                                    onClick={function () {
                                        handleQuickAction('actionFailed');
                                    }}
                                >
                                    {AntLocalization.translateText({ vi: 'Báo thất bại', en: 'Report Failed', fr: 'Signaler un échec', es: 'Reportar fallo', id: 'Laporkan gagal', ja: '失敗を報告', zh: '报告失败', ko: '실패 보고', hi: 'विफलता दर्ज करें', ar: 'الإبلاغ عن فشل' })}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Zone: Bình luận (read-only flat) */}
                    {taskComments.length > 0 && (
                        <div className="task-detail-zone">
                            <div
                                className="task-zone-title"
                                style={{ cursor: 'pointer' }}
                                onClick={function () {
                                    setShowComment(true);
                                }}
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Bình luận', en: 'Comments', fr: 'Commentaires', es: 'Comentarios', id: 'Komentar', ja: 'コメント', zh: '评论', ko: '댓글', hi: 'टिप्पणियाँ', ar: 'التعليقات' })}
                                <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 2 }}>({taskComments.length})</span>
                            </div>
                            {taskComments.map(function (cm) {
                                var cmName = cm.entity_full_name || cm.entity_name || cm.user_name || '';
                                return (
                                    <div key={cm.id} className="task-inline-comment">
                                        <div className="task-inline-comment-avatar">
                                            {cmName ? cmName.charAt(cmName.trim().lastIndexOf(' ') + 1 || 0).toUpperCase() : '?'}
                                        </div>
                                        <div className="task-inline-comment-body">
                                            <div className="task-inline-comment-header">
                                                <span className="task-inline-comment-author">{cmName}</span>
                                                <span className="task-inline-comment-time">{commentFormatTime(cm.created_at)}</span>
                                            </div>
                                            {cm.description && <div className="task-inline-comment-text">{cm.description}</div>}
                                            {cm.meddoc_name && <div className="task-inline-comment-file"><SvgIcon d={GLYPH.clip} size={12} /> {cm.meddoc_name}</div>}
                                        </div>
                                    </div>
                                );
                            })}
                            <button
                                className="task-inline-comment-more"
                                onClick={function () {
                                    setShowComment(true);
                                }}
                            >
                                {AntLocalization.translateText({ vi: 'Xem tất cả & trả lời...', en: 'View all & reply...', fr: 'Tout voir et répondre...', es: 'Ver todo y responder...', id: 'Lihat semua & balas...', ja: 'すべて表示して返信...', zh: '查看全部并回复...', ko: '모두 보기 및 답글...', hi: 'सभी देखें और उत्तर दें...', ar: 'عرض الكل والرد...' })}
                            </button>
                        </div>
                    )}

                    {/* Zone 7: Checklist */}
                    {activeTask.checklist && activeTask.checklist instanceof Array && activeTask.checklist.length > 0 && (
                        <div className="task-detail-zone">
                            <span className="task-zone-title">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Danh sách kiểm tra', en: 'Checklist', fr: 'Check-list', es: 'Checklist', id: 'Daftar periksa', ja: 'チェックリスト', zh: '检查清单', ko: '체크리스트', hi: 'चेकलिस्ट', ar: 'قائمة التحقق' })}
                            </span>
                            {activeTask.checklist.map(function (cl, ci) {
                                return (
                                    <div key={ci} className="task-checklist-item">
                                        <span className={'task-checklist-check' + (cl.checked ? ' checked' : '')}>
                                            {cl.checked && (
                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                    <path d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </span>
                                        <span>{cl.text}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Zone 8: Parent + children */}
                    {(activeTask.parent_task_code || childTasks.length > 0) && (
                        <div className="task-detail-zone">
                            {activeTask.parent_task_code && (
                                <div className="task-info-row">
                                    <span className="task-info-label">{AntLocalization.translateText({ vi: 'Thuộc', en: 'Parent', fr: 'Parent', es: 'Principal', id: 'Induk', ja: '親タスク', zh: '父任务', ko: '상위', hi: 'मूल', ar: 'الأصل' })}</span>
                                    <span className="task-info-value" style={{ color: 'var(--AnteBaseColor1, #4f7cff)', cursor: 'pointer' }}>
                                        {activeTask.parent_task_title || activeTask.parent_task_code}
                                    </span>
                                </div>
                            )}
                            {childTasks.length > 0 && (
                                <Fragment>
                                    <span className="task-zone-title" style={{ marginTop: activeTask.parent_task_code ? 8 : 0 }}>
                                        {AntLocalization.translateText({ vi: 'Công việc con', en: 'Sub-tasks', fr: 'Sous-tâches', es: 'Subtareas', id: 'Subtugas', ja: 'サブタスク', zh: '子任务', ko: '하위 작업', hi: 'उप-कार्य', ar: 'المهام الفرعية' })} ({childTasks.length})
                                    </span>
                                    {childTasks.map(function (ct) {
                                        var csc = gtGetStatusColor(ct.task_status);
                                        var ctAssignee = ct.entity_name || ct.user_name || '';
                                        return (
                                            <div
                                                key={ct.id}
                                                className="task-child-item"
                                                onClick={function () {
                                                    openTask(ct);
                                                }}
                                            >
                                                <span className="task-child-dot" style={{ background: csc }} />
                                                <div className="task-child-content">
                                                    <span className="task-child-title">{ct.title}</span>
                                                    <span className="task-child-meta">
                                                        <span style={{ color: csc }}>{gtGetStatusLabel(ct.task_status)}</span>
                                                        {ctAssignee && <span className="task-child-assignee">{ctAssignee}</span>}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Fragment>
                            )}
                        </div>
                    )}
                </div>

                {/* Bottom toolbar */}
                <div className="task-detail-toolbar">
                    {canEdit2 && (
                        <button className="task-flat-btn" onClick={handleCreateSubTask}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            <span>{AntLocalization.translateText({ vi: 'Thêm việc con', en: 'Sub-task', fr: 'Sous-tâche', es: 'Subtarea', id: 'Subtugas', ja: 'サブタスク', zh: '子任务', ko: '하위 작업', hi: 'उप-कार्य', ar: 'مهمة فرعية' })}</span>
                        </button>
                    )}
                    <button
                        className="task-flat-btn"
                        onClick={function () {
                            setShowComment(true);
                        }}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                        </svg>
                        <span>{AntLocalization.translateText({ vi: 'Bình luận', en: 'Comment', fr: 'Commenter', es: 'Comentar', id: 'Komentar', ja: 'コメント', zh: '评论', ko: '댓글', hi: 'टिप्पणी', ar: 'تعليق' })}</span>
                    </button>
                </div>
            </div>
        );
    }

    // ═══ LIST VIEW ══════════════════════════════════════════════
    var filteredTasks = tasks;
    if (taskSearchText.trim()) {
        var tq = taskSearchText.trim().toLowerCase();
        filteredTasks = tasks.filter(function (t) {
            return (t.title || '').toLowerCase().indexOf(tq) >= 0 || (t.description || '').toLowerCase().indexOf(tq) >= 0;
        });
    }

    return (
        <div className="chat-panel">
            <div className="chat-search-bar">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                    className="chat-search-input"
                    value={taskSearchText}
                    onChange={function (e) {
                        setTaskSearchText(e.target.value);
                    }}
                    placeholder={AntLocalization.translateText({ vi: 'Tìm công việc...', en: 'Search tasks...', fr: 'Chercher tâches...', es: 'Buscar tareas...', id: 'Cari tugas...', ja: 'タスクを検索...', zh: '搜索任务...', ko: '작업 검색...', hi: 'कार्य खोजें...', ar: 'البحث في المهام...' })}
                />
                {taskSearchText && (
                    <button
                        className="chat-search-clear"
                        onClick={function () {
                            setTaskSearchText('');
                        }}
                    >
                        ×
                    </button>
                )}
            </div>
            <div className="rs-chips" role="tablist">
                {[
                    { key: 'assigned', label: { vi: 'Được giao', en: 'Assigned', fr: 'Assignées', es: 'Asignadas', id: 'Ditugaskan', ja: '担当', zh: '分配给我', ko: '배정됨', hi: 'सौंपे गए', ar: 'المسندة إليّ' } },
                    { key: 'created', label: { vi: 'Đã giao', en: 'Created', fr: 'Créées', es: 'Creadas', id: 'Dibuat', ja: '依頼済み', zh: '我分配的', ko: '배정함', hi: 'सौंपे', ar: 'التي أسندتها' } },
                    { key: 'all', label: { vi: 'Tất cả', en: 'All', fr: 'Tous', es: 'Todos', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' } }
                ].map(function (f) {
                    return (
                        <button
                            key={f.key}
                            type="button"
                            role="tab"
                            aria-selected={taskFilter === f.key}
                            className={'rs-chip' + (taskFilter === f.key ? ' active' : '')}
                            onClick={function () {
                                setTaskFilter(f.key);
                            }}
                        >
                            {AntLocalization.translateText(f.label)}
                        </button>
                    );
                })}
            </div>
            <div className="chat-list gt-task-list">
                {loading && <SidebarState type="loading" />}
                {!loading && filteredTasks.length === 0 && (
                    <SidebarState type="empty" icon="task" text={{ vi: 'Chưa có công việc', en: 'No tasks yet', fr: 'Aucune tâche', es: 'Sin tareas', id: 'Belum ada tugas', ja: 'タスクはありません', zh: '暂无任务', ko: '작업이 없습니다', hi: 'अभी कोई कार्य नहीं', ar: 'لا توجد مهام' }} />
                )}
                {filteredTasks.map(function (task) {
                    var statusColor = gtGetStatusColor(task.task_status);
                    var isOverdue =
                        task.due_date &&
                        ['Close', 'Done', 'Cancel'].indexOf(task.task_status) < 0 &&
                        (task.due_date < Date.now() / 1000 || (task.due_date > 1e12 && task.due_date < Date.now()));
                    return (
                        <div
                            key={task.id}
                            className="task-item"
                            onClick={function () {
                                openTask(task);
                            }}
                        >
                            <div className="task-status-dot" style={{ backgroundColor: statusColor, color: statusColor }} />
                            <div className="task-content">
                                <div className="task-title" style={isOverdue ? { color: '#dc2626' } : null}>
                                    {task.title}
                                </div>
                                <div className="task-meta">
                                    <span className="task-status-label" style={{ color: statusColor }}>
                                        {gtGetStatusLabel(task.task_status)}
                                    </span>
                                    {task.entity_name && taskFilter !== 'assigned' && <span className="task-assignee">{task.entity_name}</span>}
                                    {task.progress_pct > 0 && task.progress_pct < 100 && (
                                        <span style={{ color: '#64748b' }}>{Math.round(task.progress_pct)}%</span>
                                    )}
                                    {task.due_date && <span style={{ color: isOverdue ? '#dc2626' : '#94a3b8' }}>{gtFormatDate(task.due_date)}</span>}
                                </div>
                            </div>
                            <span className="task-item-arrow">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 5l7 7-7 7" />
                                </svg>
                            </span>
                        </div>
                    );
                })}
            </div>
            <button className="task-fab" onClick={openCreateView} title={AntLocalization.translateText({ vi: 'Tạo công việc', en: 'New task', fr: 'Nouvelle tâche', es: 'Nueva tarea', id: 'Tugas baru', ja: '新規タスク', zh: '新建任务', ko: '새 작업', hi: 'नया कार्य', ar: 'مهمة جديدة' })}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 5v14M5 12h14" />
                </svg>
            </button>

            {/* FloatingWorkPanel handled by parent AntRightSidebar */}
        </div>
    );
}

// Approval tab: panels/ApprovalPanel.js (same organization as ante-mobile-user).

// ═══════════════════════════════════════════════════════════════════
// ─── DOCUMENTS PANEL ────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var DOCUMENT_FILE_TONES = { pdf: 'pdf', doc: 'word', docx: 'word', xls: 'sheet', xlsx: 'sheet', csv: 'sheet', ppt: 'slide', pptx: 'slide' };

function DocumentsPanel(props) {
    var docAPI = useMemo(function () {
        return new AntAPI('of', 'OFDocument');
    }, []);
    var folderAPI = useMemo(function () {
        return new AntAPI('of', 'OFDocFolder');
    }, []);

    var [documents, setDocuments] = useState([]);
    var [folders, setFolders] = useState([]);
    var [loading, setLoading] = useState(false);
    var [error, setError] = useState('');
    var [activeFolder, setActiveFolder] = useState(null);
    var [searchText, setSearchText] = useState('');
    var [previewDoc, setPreviewDoc] = useState(null);

    var loadFolders = useCallback(function () {
        folderAPI.post('/' + folderAPI.routeURL + '/list', { pageSize: 50, is_active: 1 }, function (result) {
            var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
            setFolders(rows instanceof Array ? rows : []);
        });
    }, []);

    var loadDocuments = useCallback(
        function (folderId, text) {
            setLoading(true);
            setError('');
            var params = { pageSize: 30, pageIndex: 0 };
            if (folderId) params.folder_code = folderId;
            var query = text === undefined ? searchText : text;
            if (query.trim()) params.searchText = query.trim();
            docAPI.post('/' + docAPI.routeURL + '/list', params, function (result) {
                setLoading(false);
                if (!result || result.error) {
                    var failure = result && result.error;
                    setError(
                        (typeof failure === 'string' && failure) ||
                            (failure && (failure.content || failure.message)) ||
                            AntLocalization.translateText({ vi: 'Chưa tải được danh sách', en: 'List could not be loaded', fr: 'Impossible de charger la liste', es: 'No se pudo cargar la lista', id: 'Daftar tidak dapat dimuat', ja: '一覧を読み込めませんでした', zh: '无法加载列表', ko: '목록을 불러오지 못했습니다', hi: 'सूची लोड नहीं हो सकी', ar: 'تعذر تحميل القائمة' })
                    );
                    return;
                }
                var rows = (result.data && result.data.rows) || result.rows || [];
                setDocuments(rows instanceof Array ? rows : []);
            });
        },
        [searchText]
    );

    useEffect(function () {
        loadFolders();
    }, []);
    useEffect(
        function () {
            loadDocuments(activeFolder ? activeFolder.code : null);
        },
        [activeFolder]
    );

    var handleSearch = useCallback(
        function (e) {
            if (e.key === 'Enter') loadDocuments(activeFolder ? activeFolder.code : null);
        },
        [activeFolder, searchText]
    );

    var handleOpenDoc = useCallback(
        function (doc) {
            // Mở preview modal — KHÔNG navigate. User chỉ xem; muốn quản lý thì maximize.
            setPreviewDoc(doc);
        },
        []
    );

    return (
        <div className="documents-panel">
            <div className="chat-search-bar">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                    className="chat-search-input"
                    value={searchText}
                    onChange={function (e) {
                        setSearchText(e.target.value);
                    }}
                    onKeyDown={handleSearch}
                    placeholder={AntLocalization.translateText({ vi: 'Tìm tài liệu...', en: 'Search documents...', fr: 'Chercher documents...', es: 'Buscar documentos...', id: 'Cari dokumen...', ja: '文書を検索...', zh: '搜索文档...', ko: '문서 검색...', hi: 'दस्तावेज़ खोजें...', ar: 'البحث في المستندات...' })}
                />
                {searchText && (
                    <button
                        type="button"
                        className="chat-search-clear"
                        onClick={function () {
                            setSearchText('');
                            loadDocuments(activeFolder ? activeFolder.code : null, '');
                        }}
                    >
                        ×
                    </button>
                )}
            </div>

            {/* Folders */}
            {folders.length > 0 && (
                <div className="rs-chips" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={!activeFolder}
                        className={'rs-chip' + (!activeFolder ? ' active' : '')}
                        onClick={function () {
                            setActiveFolder(null);
                        }}
                    >
                        {AntLocalization.translateText({ vi: 'Tất cả', en: 'All', fr: 'Tous', es: 'Todos', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' })}
                    </button>
                    {folders.map(function (f) {
                        var active = activeFolder && activeFolder.id === f.id;
                        return (
                            <button
                                key={f.id}
                                type="button"
                                role="tab"
                                aria-selected={!!active}
                                className={'rs-chip' + (active ? ' active' : '')}
                                onClick={function () {
                                    setActiveFolder(f);
                                }}
                            >
                                {f.name}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Document list */}
            <div className="docs-list">
                {error && (
                    <SidebarState
                        type="error"
                        text={error}
                        onRetry={function () {
                            loadDocuments(activeFolder ? activeFolder.code : null);
                        }}
                    />
                )}
                {loading && <SidebarState type="loading" />}
                {!loading && !error && documents.length === 0 && (
                    <SidebarState type="empty" icon="document" text={{ vi: 'Chưa có tài liệu', en: 'No documents', fr: 'Aucun document', es: 'Sin documentos', id: 'Belum ada dokumen', ja: '文書はありません', zh: '暂无文档', ko: '문서가 없습니다', hi: 'कोई दस्तावेज़ नहीं', ar: 'لا توجد مستندات' }} />
                )}
                {!error &&
                    documents.map(function (doc) {
                        var ext = String(doc.file_type || (doc.file_name ? doc.file_name.split('.').pop() : '')).toLowerCase();
                        var tone = DOCUMENT_FILE_TONES[ext] || 'file';
                        return (
                            <button
                                key={doc.id}
                                type="button"
                                className="docs-item"
                                onClick={function () {
                                    handleOpenDoc(doc);
                                }}
                            >
                                <span className={'docs-item-icon docs-item-icon--' + tone}>
                                    <SvgIcon d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" size={18} />
                                </span>
                                <span className="docs-item-info">
                                    <span className="docs-item-name">{doc.title || doc.file_name}</span>
                                    <span className="docs-item-meta">
                                        {doc.created_user_name && <span>{doc.created_user_name}</span>}
                                        {doc.created_at && <span> · {commentFormatTime(doc.created_at)}</span>}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
            </div>
            {previewDoc && (
                <OFDocumentPreview
                    visible
                    doc={previewDoc}
                    code={previewDoc.code}
                    onClose={function () { setPreviewDoc(null); }}
                />
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── CALENDAR PANEL ─────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var DAY_LABELS = { vi: ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'], en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] };
var MONTH_LABELS = {
    vi: ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};

function getMonthDays(year, month) {
    var firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    var startOffset = firstDay === 0 ? 6 : firstDay - 1; // Mon=0
    var daysInMonth = new Date(year, month + 1, 0).getDate();
    var cells = [];
    for (var i = 0; i < startOffset; i++) cells.push(null);
    for (var d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
}

function CalendarPanel() {
    var calNav = useNavigate();
    var now = new Date();
    var [viewYear, setViewYear] = useState(now.getFullYear());
    var [viewMonth, setViewMonth] = useState(now.getMonth());
    var [selectedDate, setSelectedDate] = useState(now.getDate());
    var [events, setEvents] = useState([]);
    var [loading, setLoading] = useState(false);
    var [showQuickAdd, setShowQuickAdd] = useState(false);
    var [quickTitle, setQuickTitle] = useState('');
    var [quickTime, setQuickTime] = useState('');

    var reminderAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserReminder');
    }, []);
    var appointmentAPI = useMemo(function () {
        return new AntAPI('ar', 'ARAppointment');
    }, []);
    var meetingAPI = useMemo(function () {
        return new AntAPI('of', 'OFMeeting');
    }, []);
    var taskAPI = useMemo(function () {
        return new AntAPI('of', 'OFTask');
    }, []);

    // Load events từ nhiều nguồn: Reminder + Appointment + Meeting + Task
    var loadEvents = useCallback(function () {
        setLoading(true);
        var allEvents = [];
        var pending = 4;
        var done = function () {
            pending--;
            if (pending <= 0) {
                setEvents(allEvents);
                setLoading(false);
            }
        };

        // 1. Reminders
        reminderAPI.post('/' + reminderAPI.routeURL + '/list', { pageSize: 100, pageIndex: 0 }, function (result) {
            var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
            if (rows instanceof Array)
                rows.forEach(function (r) {
                    allEvents.push({
                        _source: 'reminder',
                        _color: '#8b5cf6',
                        id: 'r-' + r.id,
                        title: r.title,
                        remind_at: r.remind_at,
                        repeat_type: r.repeat_type,
                        is_active: r.is_active,
                        code: r.code
                    });
                });
            done();
        });

        // 2. ARAppointment (serve/support entity = current user)
        appointmentAPI.post(
            '/' + appointmentAPI.routeURL + '/list',
            { pageSize: 50, pageIndex: 0, appointment_status: ['Release', 'Open'] },
            function (result) {
                var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                if (rows instanceof Array)
                    rows.forEach(function (a) {
                        allEvents.push({
                            _source: 'appointment',
                            _color: '#3b82f6',
                            id: 'a-' + a.id,
                            title: a.entity_name || a.description || a.code,
                            remind_at: a.appointment_date,
                            code: a.code
                        });
                    });
                done();
            }
        );

        // 3. OFMeeting
        meetingAPI.post(
            '/' + meetingAPI.routeURL + '/list',
            { pageSize: 50, pageIndex: 0, meeting_status: ['Scheduled', 'InProgress'] },
            function (result) {
                var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                if (rows instanceof Array)
                    rows.forEach(function (m) {
                        allEvents.push({
                            _source: 'meeting',
                            _color: '#d97706',
                            id: 'm-' + m.id,
                            title: m.title || m.name,
                            remind_at: m.start_time || m.meeting_date,
                            code: m.code
                        });
                    });
                done();
            }
        );

        // 4. OFTask (có due_date)
        taskAPI.post('/' + taskAPI.routeURL + '/list', { pageSize: 50, pageIndex: 0, task_status: ['New', 'InProgress'] }, function (result) {
            var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
            if (rows instanceof Array)
                rows.forEach(function (t) {
                    if (t.due_date || t.start_date)
                        allEvents.push({
                            _source: 'task',
                            _color: '#22c55e',
                            id: 't-' + t.id,
                            title: t.title || t.name,
                            remind_at: t.start_date || t.due_date,
                            code: t.code
                        });
                });
            done();
        });
    }, []);

    useEffect(
        function () {
            loadEvents();
        },
        [viewYear, viewMonth]
    );

    // Events cho ngày được chọn
    var selectedDateStr = viewYear + '-' + String(viewMonth + 1).padStart(2, '0') + '-' + String(selectedDate).padStart(2, '0');
    var todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');

    var dayEvents = useMemo(
        function () {
            return events.filter(function (e) {
                if (e.remind_at) {
                    var ts = e.remind_at < 1e12 ? e.remind_at * 1000 : e.remind_at;
                    var d = new Date(ts);
                    var ds = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
                    return ds === selectedDateStr;
                }
                // Daily reminders
                if (e.repeat_type === 'daily' && e.is_active) return true;
                return false;
            });
        },
        [events, selectedDateStr]
    );

    // Prev/next month
    var goPrev = useCallback(
        function () {
            if (viewMonth === 0) {
                setViewYear(viewYear - 1);
                setViewMonth(11);
            } else setViewMonth(viewMonth - 1);
        },
        [viewYear, viewMonth]
    );

    var goNext = useCallback(
        function () {
            if (viewMonth === 11) {
                setViewYear(viewYear + 1);
                setViewMonth(0);
            } else setViewMonth(viewMonth + 1);
        },
        [viewYear, viewMonth]
    );

    var goToday = useCallback(function () {
        setViewYear(now.getFullYear());
        setViewMonth(now.getMonth());
        setSelectedDate(now.getDate());
    }, []);

    var cells = getMonthDays(viewYear, viewMonth);
    var lang = AntLocalization.getLanguage ? AntLocalization.getLanguage() : 'vi';
    var dayLabels = DAY_LABELS[lang] || DAY_LABELS.vi;
    var monthLabel = (MONTH_LABELS[lang] || MONTH_LABELS.vi)[viewMonth] + ' ' + viewYear;

    return (
        <div className="calendar-panel">
            {/* Month header */}
            <div className="cal-month-header">
                <button className="cal-nav-btn" onClick={goPrev}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <span className="cal-month-label">{monthLabel}</span>
                <button className="cal-nav-btn" onClick={goNext}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 5l7 7-7 7" />
                    </svg>
                </button>
                <button className="cal-today-btn" onClick={goToday}>
                    {AntLocalization.translateText({ vi: 'Nay', en: 'Today', fr: 'Auj.', es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم' })}
                </button>
            </div>

            {/* Day headers */}
            <div className="cal-grid cal-day-headers">
                {dayLabels.map(function (d) {
                    return (
                        <span key={d} className="cal-day-label">
                            {d}
                        </span>
                    );
                })}
            </div>

            {/* Date cells */}
            <div className="cal-grid">
                {cells.map(function (day, idx) {
                    if (day === null) return <span key={'e' + idx} className="cal-cell empty" />;
                    var isToday = viewYear === now.getFullYear() && viewMonth === now.getMonth() && day === now.getDate();
                    var isSelected = day === selectedDate;
                    return (
                        <button
                            key={day}
                            className={'cal-cell' + (isToday ? ' today' : '') + (isSelected ? ' selected' : '')}
                            onClick={function () {
                                setSelectedDate(day);
                            }}
                        >
                            {day}
                        </button>
                    );
                })}
            </div>

            {/* Events for selected date */}
            <div className="cal-events">
                <div className="cal-events-header">
                    <span>
                        {selectedDateStr === todayStr
                            ? AntLocalization.translateText({ vi: 'Hôm nay', en: 'Today', es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم', fr: 'Ce jour' })
                            : selectedDate + '/' + (viewMonth + 1)}
                    </span>
                    <button
                        className="cal-add-btn"
                        onClick={function () {
                            setShowQuickAdd(!showQuickAdd);
                        }}
                        title={AntLocalization.translateText({ vi: 'Thêm sự kiện', en: 'Add event', fr: 'Événement', es: 'Añadir evento', id: 'Tambah acara', ja: '予定を追加', zh: '添加事件', ko: '일정 추가', hi: 'इवेंट जोड़ें', ar: 'إضافة حدث' })}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 5v14M5 12h14" />
                        </svg>
                    </button>
                </div>

                {/* Quick add event */}
                {showQuickAdd && (
                    <div className="cal-quick-add">
                        <input
                            className="cal-quick-input"
                            value={quickTitle}
                            onChange={function (e) {
                                setQuickTitle(e.target.value);
                            }}
                            onKeyDown={function (e) {
                                if (e.key === 'Enter' && quickTitle.trim()) {
                                    var remindAt = selectedDateStr + 'T' + (quickTime || '09:00') + ':00';
                                    reminderAPI.post(
                                        '/' + reminderAPI.routeURL + '/create',
                                        {
                                            type: 'personal',
                                            title: quickTitle.trim(),
                                            remind_at: remindAt,
                                            repeat_type: 'none'
                                        },
                                        function (result) {
                                            if (result && result.error) return;
                                            setQuickTitle('');
                                            setQuickTime('');
                                            setShowQuickAdd(false);
                                            loadEvents();
                                        }
                                    );
                                }
                                if (e.key === 'Escape') {
                                    setShowQuickAdd(false);
                                    setQuickTitle('');
                                }
                            }}
                            placeholder={AntLocalization.translateText({ vi: 'S\u1EF1 ki\u1EC7n m\u1EDBi...', en: 'New event...', fr: 'Événement...', es: 'Nuevo evento...', id: 'Acara baru...', ja: '新しい予定...', zh: '新事件...', ko: '새 일정...', hi: 'नया इवेंट...', ar: 'حدث جديد...' })}
                            autoFocus
                        />
                        <input
                            className="cal-quick-time"
                            type="time"
                            value={quickTime}
                            onChange={function (e) {
                                setQuickTime(e.target.value);
                            }}
                        />
                        <div className="cal-quick-actions">
                            <button
                                className="cal-quick-create-btn"
                                disabled={!quickTitle.trim()}
                                onClick={function () {
                                    if (!quickTitle.trim()) return;
                                    var remindAt = selectedDateStr + 'T' + (quickTime || '09:00') + ':00';
                                    reminderAPI.post(
                                        '/' + reminderAPI.routeURL + '/create',
                                        {
                                            type: 'personal',
                                            title: quickTitle.trim(),
                                            remind_at: remindAt,
                                            repeat_type: 'none'
                                        },
                                        function (result) {
                                            if (result && result.error) return;
                                            setQuickTitle('');
                                            setQuickTime('');
                                            setShowQuickAdd(false);
                                            loadEvents();
                                        }
                                    );
                                }}
                            >
                                {AntLocalization.translateText({ vi: 'T\u1EA1o', en: 'Create', fr: 'Créer', es: 'Crear', id: 'Buat', ja: '作成', zh: '创建', ko: '생성', hi: 'बनाएँ', ar: 'إنشاء' })}
                            </button>
                            <button
                                className="cal-quick-cancel-btn"
                                onClick={function () {
                                    setShowQuickAdd(false);
                                    setQuickTitle('');
                                    setQuickTime('');
                                }}
                            >
                                {AntLocalization.translateText({ vi: 'Hu\u1EF7', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء', ja: '取消' })}
                            </button>
                        </div>
                    </div>
                )}

                <div className="cal-events-list">
                    {loading && <div className="cal-events-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読込中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोडिंग...', ar: 'تحميل...' })}</div>}
                    {!loading && dayEvents.length === 0 && !showQuickAdd && (
                        <div className="cal-events-empty">{AntLocalization.translateText({ vi: 'Không có sự kiện', en: 'No events', fr: 'Aucun événement', es: 'Sin eventos', id: 'Tidak ada acara', ja: '予定なし', zh: '暂无事件', ko: '일정 없음', hi: 'कोई इवेंट नहीं', ar: 'لا توجد أحداث' })}</div>
                    )}
                    {dayEvents.map(function (ev) {
                        var SOURCE_LABELS = { reminder: '📝', appointment: '📅', meeting: '🤝', task: '✅' };
                        return (
                            <div
                                key={ev.id}
                                className="cal-event-item"
                                onClick={function () {
                                    if (ev._source === 'appointment' && ev.code) calNav('/ar/ARAppointment/' + ev.code);
                                    else if (ev._source === 'meeting' && ev.code) calNav('/of/OFMeeting/' + ev.code);
                                    else if (ev._source === 'task' && ev.code) calNav('/of/OFTask/' + ev.code);
                                }}
                                style={{ cursor: ev._source !== 'reminder' ? 'pointer' : 'default' }}
                            >
                                <span className="cal-event-dot" style={{ background: ev._color || '#3b82f6' }} />
                                <div className="cal-event-info">
                                    <div className="cal-event-title">
                                        {SOURCE_LABELS[ev._source] || ''} {ev.title}
                                    </div>
                                    {ev.remind_at && <div className="cal-event-time">{reminderFormatDate(ev.remind_at)}</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── MAIN COMPONENT ─────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
export default function AntRightSidebar() {
    // ─── Context ───────────────────────────────────────────────────
    var sidebarContext = AntSidebar();
    var pageContext = (sidebarContext && sidebarContext.context) || null;
    var hasVoucherContext = pageContext && pageContext.voucherType;
    var session = AntSession ? AntSession() : null;
    var navigate = useNavigate();

    // ─── Global panels + pin/unpin ─────────────────────────────────
    var panels = GLOBAL_PANELS.filter(function (p) {
        var isValid = AntCondition.isVisible({}, p);
        if (!isValid) return false;
        return true;
    });
    // OFDocument manage permission — gate hiển thị nút maximize tab "documents".
    // Preview ai cũng xem được, nhưng manage (CRUD) chỉ user có quyền can_manage.
    var canManageOFDocument = AntCondition.isVisible({}, {
        visible: { when: { whenUser: { OFDocument_can_manage: 1 } } }
    });
    var defaultPanel = 'notification';
    var MAX_VISIBLE_ICONS = 6;
    var PIN_STORAGE_KEY = 'ant_rs_pinned';

    var [pinnedKeys, setPinnedKeys] = useState(function () {
        try {
            var v = JSON.parse(localStorage.getItem(PIN_STORAGE_KEY));
            if (v instanceof Array) return v;
        } catch (e) {
            /* */
        }
        return null; // null = use default first 6
    });
    var [showMoreMenu, setShowMoreMenu] = useState(false);
    var moreMenuRef = useRef(null);

    // Click outside to close more menu
    useEffect(
        function () {
            if (!showMoreMenu) return;
            var handler = function (e) {
                if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setShowMoreMenu(false);
            };
            document.addEventListener('mousedown', handler);
            return function () {
                document.removeEventListener('mousedown', handler);
            };
        },
        [showMoreMenu]
    );

    var visiblePanels = useMemo(
        function () {
            if (pinnedKeys && pinnedKeys.length > 0) {
                var filtered = panels.filter(function (p) {
                    return pinnedKeys.indexOf(p.key) >= 0;
                });
                if (filtered.length > 0) return filtered.slice(0, MAX_VISIBLE_ICONS);
            }
            // Default: hiện tất cả (11 icons như cũ) — không giới hạn khi chưa pin
            return panels;
        },
        [panels, pinnedKeys]
    );

    var hiddenPanels = useMemo(
        function () {
            var visKeys = {};
            visiblePanels.forEach(function (p) {
                visKeys[p.key] = true;
            });
            return panels.filter(function (p) {
                return !visKeys[p.key];
            });
        },
        [panels, visiblePanels]
    );

    var handleTogglePin = useCallback(
        function (panelKey) {
            var current =
                pinnedKeys ||
                panels.slice(0, MAX_VISIBLE_ICONS).map(function (p) {
                    return p.key;
                });
            var idx = current.indexOf(panelKey);
            var newPinned;
            if (idx >= 0) {
                // Unpin
                newPinned = current.filter(function (k) {
                    return k !== panelKey;
                });
            } else {
                // Pin — add, remove oldest if over limit
                newPinned = current.concat([panelKey]);
                if (newPinned.length > MAX_VISIBLE_ICONS) newPinned = newPinned.slice(newPinned.length - MAX_VISIBLE_ICONS);
            }
            setPinnedKeys(newPinned);
            try {
                localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(newPinned));
            } catch (e) {
                /* */
            }
            setShowMoreMenu(false);
        },
        [pinnedKeys, panels]
    );

    // ─── State ─────────────────────────────────────────────────────
    var saved = useMemo(loadState, []);
    var [isOpen, setIsOpen] = useState(function () {
        if (saved && saved.isOpen !== undefined) return saved.isOpen;
        return window.innerWidth >= WIDE_SCREEN_BREAKPOINT;
    });
    // pinned: panel ghim cố định — không tự ẩn khi click ngoài + chiếm chỗ trong
    // sidebar root (bên trái icon-strip) thay vì overlay tuyệt đối. Persist localStorage.
    var [pinned, setPinned] = useState(function () {
        try { return localStorage.getItem('ant_rs_panel_pinned') === '1'; } catch (e) { return false; }
    });
    useEffect(function () {
        try { localStorage.setItem('ant_rs_panel_pinned', pinned ? '1' : '0'); } catch (e) { /* ignore */ }
    }, [pinned]);
    var [activePanel, setActivePanel] = useState(function () {
        var key = (saved && saved.activePanel) || defaultPanel;
        // Panel không còn định nghĩa (vd AI Jobs đang tạm ẩn) → về mặc định, tránh mở panel đã gỡ tab
        if (!GLOBAL_PANELS.some(function (p) { return p.key === key; })) return defaultPanel;
        return key;
    });
    var [width, setWidth] = useState(function () {
        return (saved && saved.width) || DEFAULT_WIDTH;
    });
    var [isResizing, setIsResizing] = useState(false);
    var [isCompactScreen, setIsCompactScreen] = useState(function () {
        return window.innerWidth <= COMPACT_SCREEN_BREAKPOINT;
    });
    // Màn hình nhỏ luôn dùng overlay. Nếu desktop đang ghim panel thì không để
    // trạng thái đã lưu làm co hoặc che cố định vùng nội dung khi đổi viewport.
    var effectivePinned = pinned && !isCompactScreen;

    // ─── Unread / badge state ──────────────────────────────────────
    // tabBadges: { notification: true, chat: true, task: true, ... } — thống nhất cho mọi tab
    var [tabBadges, setTabBadges] = useState({});
    var [unreadChatIds, setUnreadChatIds] = useState({});
    var [chatToasts, setChatToasts] = useState([]);
    var [notifyToasts, setNotifyToasts] = useState([]);
    var activeChatIdRef = useRef(null);
    var [pendingOpenChatId, setPendingOpenChatId] = useState(null);
    var [notifyRefreshKey, setNotifyRefreshKey] = useState(0);
    var [panelRefreshKey, setPanelRefreshKey] = useState(0);
    var [expandedPanel, setExpandedPanel] = useState(null); // panel key đang mở floating

    // Helper: bật badge cho 1 tab (chỉ khi tab đó không đang mở)
    var markTabBadge = useCallback(
        function (tabKey) {
            if (isOpen && activePanel === tabKey) return;
            setTabBadges(function (prev) {
                if (prev[tabKey]) return prev;
                var next = Object.assign({}, prev);
                next[tabKey] = true;
                return next;
            });
        },
        [isOpen, activePanel]
    );

    // ─── Auto-check badges: dùng AntBadgeStore (gom 1 API) ─────────
    var checkBadges = useCallback(function () {
        AntBadgeStore.invalidate();
    }, []);

    useEffect(
        function () {
            var unsubscribe = AntBadgeStore.subscribe(function (snapshot) {
                var chatUnread = (snapshot && snapshot.chat_unread) || 0;
                var notifUnread = (snapshot && snapshot.notification_unread) || 0;
                setTabBadges(function (prev) {
                    var next = prev;
                    if (chatUnread > 0 && !prev.chat) {
                        next = Object.assign({}, next, { chat: true });
                    }
                    if (notifUnread > 0 && !prev.notification) {
                        next = Object.assign({}, next, { notification: true });
                    }
                    return next;
                });
            });
            var onFocus = function () {
                AntBadgeStore.invalidate();
            };
            window.addEventListener('focus', onFocus);
            return function () {
                unsubscribe();
                window.removeEventListener('focus', onFocus);
            };
        },
        []
    );

    // ─── Auto-switch panel when context changes ────────────────────
    useEffect(
        function () {
            var validKeys = panels.map(function (p) {
                return p.key;
            });
            if (validKeys.indexOf(activePanel) < 0) {
                setActivePanel(defaultPanel);
            }
        },
        [hasVoucherContext]
    );

    // ─── Persist state ─────────────────────────────────────────────
    useEffect(
        function () {
            saveState({ isOpen: isOpen, activePanel: activePanel, width: width });
        },
        [isOpen, activePanel, width]
    );

    // ─── Badge socket handler ──────────────────────────────────────
    // Mapping: socket event → tab key (mở rộng thêm event mới ở đây)
    useEffect(
        function () {
            if (!session || !session.registerSocketListener) return;
            var handler = function (data) {
                if (!data) return false;

                // ── Chat ────────────────────────────────────────────
                if (data.event === 'NewChatMessage') {
                    var chatIdStr = String(data.chat_id);
                    var isActiveChatOpen = isOpen && activePanel === 'chat' && activeChatIdRef.current === chatIdStr;
                    if (!isActiveChatOpen) {
                        setUnreadChatIds(function (prev) {
                            var next = Object.assign({}, prev);
                            next[chatIdStr] = { user_name: data.user_name, content: data.content, content_type: data.content_type };
                            return next;
                        });
                        markTabBadge('chat');
                        if (data.user_name || data.content) {
                            setChatToasts(function (prev) {
                                var toast = {
                                    id: Date.now() + Math.random(),
                                    chat_id: chatIdStr,
                                    user_name: data.user_name,
                                    content: data.content,
                                    content_type: data.content_type,
                                    chat_name: data.chat_name,
                                    chat_type: data.chat_type
                                };
                                return prev.slice(-2).concat([toast]);
                            });
                        }
                    }
                    return false;
                }
                // Opened on another device or removed: clear its unread mark and refresh notifications.
                if (data.event === 'ChatRead' || data.event === 'ChatDeleted') {
                    setUnreadChatIds(function (prev) {
                        if (!prev[String(data.chat_id)]) return prev;
                        var next = Object.assign({}, prev);
                        delete next[String(data.chat_id)];
                        return next;
                    });
                    if (data.event === 'ChatRead') setNotifyRefreshKey(function (k) { return k + 1; });
                    return false;
                }

                // ── Notification / Comment ──────────────────────────
                // toast_enable=false: bản sao BE gửi về đúng nơi vừa thao tác (chỉ để vẽ dữ liệu mới) — không phải thông báo cho tab này.
                if (data.event === 'notification' && data.toast_enable === false) return false;
                if (data.event === 'notification' || data.event === 'NewComment') {
                    markTabBadge('notification');
                    // Huy hiệu thực đơn (vd Phiếu kế toán sau việc ghi sổ nền) tải lại theo thông báo.
                    if (data.event === 'notification') AntBadgeStore.invalidate();
                    setNotifyRefreshKey(function (k) {
                        return k + 1;
                    });
                    // Toast cho user
                    var nTitle, nContent;
                    if (data.event === 'NewComment') {
                        var cmtUser = data.user_name || '?';
                        var cmtCode = data.voucher_code || '';
                        var vTypeLabels = {
                            AROrder: { vi: 'Đơn hàng', en: 'Order', fr: 'Commande', es: 'Pedido', id: 'Pesanan', ja: '注文', zh: '订单', ko: '주문', hi: 'ऑर्डर', ar: 'طلب' },
                            APOrder: { vi: 'Đơn mua', en: 'Purchase', fr: 'Achat', es: 'Compra', id: 'Pembelian', ja: '購買', zh: '采购', ko: '구매', hi: 'खरीद', ar: 'شراء' },
                            ARCredit: { vi: 'Công nợ thu', en: 'Receivable', fr: 'Créance', es: 'Por cobrar', id: 'Piutang', ja: '売掛金', zh: '应收款', ko: '미수금', hi: 'प्राप्य', ar: 'ذمم مدينة' },
                            APCredit: { vi: 'Công nợ trả', en: 'Payable', fr: 'Dette', es: 'Por pagar', id: 'Utang', ja: '買掛金', zh: '应付款', ko: '미지급금', hi: 'देय', ar: 'ذمم دائنة' },
                            ARIssue: { vi: 'Phiếu giao', en: 'Shipment', fr: 'Expédition', es: 'Envío', id: 'Pengiriman', ja: '出荷', zh: '发货单', ko: '출고', hi: 'शिपमेंट', ar: 'شحنة' },
                            ARReturn: { vi: 'Phiếu trả', en: 'Return', fr: 'Retour', es: 'Devolución', id: 'Retur', ja: '返品', zh: '退货单', ko: '반품', hi: 'वापसी', ar: 'مرتجع' },
                            ARInvoice: { vi: 'Hóa đơn', en: 'Invoice', fr: 'Facture', es: 'Factura', id: 'Faktur', ja: '請求書', zh: '发票', ko: '송장', hi: 'चालान', ar: 'فاتورة' },
                            APInvoice: { vi: 'Hóa đơn mua', en: 'Invoice', fr: 'Facture achat', es: 'Factura compra', id: 'Faktur beli', ja: '仕入請求書', zh: '采购发票', ko: '매입 송장', hi: 'खरीद चालान', ar: 'فاتورة شراء' },
                            CARequest: { vi: 'Thanh toán', en: 'Payment', fr: 'Paiement', es: 'Pago', id: 'Pembayaran', ja: '支払', zh: '付款', ko: '결제', hi: 'भुगतान', ar: 'دفعة' },
                            ICReceipt: { vi: 'Phiếu nhập', en: 'Receipt', fr: 'Réception', es: 'Recepción', id: 'Penerimaan', ja: '入庫', zh: '入库单', ko: '입고', hi: 'प्राप्ति', ar: 'استلام' },
                            ICIssue: { vi: 'Phiếu xuất', en: 'Issue', fr: 'Sortie', es: 'Salida', id: 'Pengeluaran', ja: '出庫', zh: '出库单', ko: '출고', hi: 'निर्गम', ar: 'صرف' },
                            GLJournal: { vi: 'Bút toán', en: 'Journal', fr: 'Écriture', es: 'Asiento', id: 'Jurnal', ja: '仕訳', zh: '凭证', ko: '분개', hi: 'जर्नल', ar: 'قيد يومية' },
                            OFTask: { vi: 'Công việc', en: 'Task', fr: 'Tâche', es: 'Tarea', id: 'Tugas', ja: 'タスク', zh: '任务', ko: '작업', hi: 'कार्य', ar: 'مهمة' },
                            OFApproval: { vi: 'Phê duyệt', en: 'Approval', fr: 'Approbation', es: 'Aprobación', id: 'Persetujuan', ja: '承認', zh: '审批', ko: '승인', hi: 'अनुमोदन', ar: 'الموافقة' },
                            EXPOrder: { vi: 'Chi phí', en: 'Expense', fr: 'Dépense', es: 'Gasto', id: 'Biaya', ja: '経費', zh: '费用', ko: '비용', hi: 'व्यय', ar: 'مصروف' },
                            HRSalary: { vi: 'Bảng lương', en: 'Salary', fr: 'Paie', es: 'Nómina', id: 'Penggajian', ja: '給与', zh: '工资表', ko: '급여', hi: 'वेतन', ar: 'الرواتب' }
                        };
                        var vLabel = vTypeLabels[data.voucher_type]
                            ? AntLocalization.translateText(vTypeLabels[data.voucher_type])
                            : data.voucher_type || '';
                        nTitle = cmtUser + ' (' + vLabel + ' #' + cmtCode + ')';
                        nContent = data.content || '';
                    } else {
                        nTitle = data.title || AntLocalization.translateText({ vi: 'Thông báo', en: 'Notification', fr: 'Notification', es: 'Notificación', id: 'Notifikasi', ja: '通知', zh: '通知', ko: '알림', hi: 'सूचना', ar: 'إشعار' });
                        nContent = data.message || '';
                    }
                    setNotifyToasts(function (prev) {
                        return prev.slice(-2).concat([
                            {
                                id: Date.now() + Math.random(),
                                event: data.event,
                                title: nTitle,
                                content: nContent,
                                voucher_type: data.voucher_type || (data.data && data.data.object_type) || null,
                                voucher_code: data.voucher_code || (data.data && data.data.object_value) || null
                            }
                        ]);
                    });
                    return false;
                }

                // ── Task ────────────────────────────────────────────
                if (data.event === 'NewTask' || data.event === 'TaskAssigned' || data.source === 'OFTask') {
                    markTabBadge('task');
                    return false;
                }

                // ── Approval ────────────────────────────────────────
                if (
                    data.event === 'NewApproval' ||
                    data.event === 'ApprovalRequested' ||
                    data.source === 'OFApproval' ||
                    data.source === 'WFApprovalPolicy'
                ) {
                    markTabBadge('approval');
                    return false;
                }

                // ── Calendar ────────────────────────────────────────
                if (
                    data.event === 'NewAppointment' ||
                    data.event === 'NewMeeting' ||
                    data.source === 'ARAppointment' ||
                    data.source === 'ACMeeting'
                ) {
                    markTabBadge('calendar');
                    return false;
                }

                return false;
            };
            session.registerSocketListener(handler, 'AntRightSidebarBadge');
            return function () {
                session.clearSocketListener('AntRightSidebarBadge');
            };
        },
        [session, activePanel, isOpen, markTabBadge]
    );

    // ─── Clear badge khi mở tab ─────────────────────────────────────
    useEffect(
        function () {
            if (isOpen && activePanel) {
                setTabBadges(function (prev) {
                    if (!prev[activePanel]) return prev;
                    var next = Object.assign({}, prev);
                    delete next[activePanel];
                    return next;
                });
            }
        },
        [activePanel, isOpen]
    );

    // ─── Toast / unread callbacks ──────────────────────────────────
    var handleToastDismiss = useCallback(function (toastId) {
        setChatToasts(function (prev) {
            return prev.filter(function (t) {
                return t.id !== toastId;
            });
        });
    }, []);

    var handleToastChatClick = useCallback(
        function (chatId) {
            // Dismiss toast cho chat này
            if (chatId) {
                setChatToasts(function (prev) {
                    return prev.filter(function (t) {
                        return t.chat_id !== chatId;
                    });
                });
            }
            setActivePanel('chat');
            if (!isOpen) setIsOpen(true);
            setPanelRefreshKey(function (k) {
                return k + 1;
            });
            // Yêu cầu ChatPanel mở chat cụ thể
            if (chatId) setPendingOpenChatId(chatId);
        },
        [isOpen]
    );

    var handleNotifyToastDismiss = useCallback(function (toastId) {
        setNotifyToasts(function (prev) {
            return prev.filter(function (t) {
                return t.id !== toastId;
            });
        });
    }, []);

    var handleNotifyToastClick = useCallback(
        function (toast) {
            setNotifyToasts(function (prev) {
                return prev.filter(function (t) {
                    return t.id !== toast.id;
                });
            });
            if (toast.voucher_type && toast.voucher_code) {
                var moduleMap = {
                    AROrder: '/ar/AROrder/',
                    HRLeave: '/hrm/HRLeave/',
                    HROvertimeRequest: '/timesheet/HROvertimeRequest/',
                    APOrder: '/ap/APOrder/',
                    EXPOrder: '/exp/EXPOrder/',
                    ARCredit: '/ar/ARCredit/',
                    APCredit: '/ap/APCredit/',
                    EXPCredit: '/exp/EXPCredit/',
                    ARIssue: '/ar/ARIssue/',
                    ARReturn: '/ar/ARReturn/',
                    ARInvoice: '/ar/ARInvoice/',
                    APInvoice: '/ap/APInvoice/',
                    APReceive: '/ap/APReceive/',
                    APReturn: '/ap/APReturn/',
                    CARequest: '/ca/CARequest/',
                    CAReceive: '/ca/CAReceive/',
                    CAIssue: '/ca/CAIssue/',
                    CATransfer: '/ca/CATransfer/',
                    ICReceipt: '/inv/ICReceipt/',
                    ICIssue: '/inv/ICIssue/',
                    GLJournal: '/gl/GLJournal/',
                    GLJournalAutoPost: '/gl/GLJournals?group=',
                    GLJournalRecheck: '/gl/GLJournals?group=',
                    GLJournalNightPost: '/gl/GLJournals?group=',
                    GLJournalSavePost: '/gl/GLJournals?group=',
                    OFTask: '/of/OFTask/',
                    OFApproval: '/of/OFApproval/'
                };
                var basePath = moduleMap[toast.voucher_type];
                if (basePath) {
                    navigate(basePath + toast.voucher_code);
                    // Nếu là comment → yêu cầu mở left sidebar tab comment + focus input
                    if (toast.event === 'NewComment' && sidebarContext && sidebarContext.openLeftPanel) {
                        setTimeout(function () {
                            sidebarContext.openLeftPanel('comment', { focus: true });
                        }, 300);
                    }
                }
            }
        },
        [navigate, sidebarContext]
    );

    var handleMarkChatRead = useCallback(function (chatId) {
        setUnreadChatIds(function (prev) {
            var next = Object.assign({}, prev);
            delete next[String(chatId)];
            return next;
        });
    }, []);

    // Sync tabBadges.chat khi unreadChatIds thay đổi
    useEffect(
        function () {
            var hasUnread = Object.keys(unreadChatIds).length > 0;
            setTabBadges(function (prev) {
                if (hasUnread && !prev.chat) return Object.assign({}, prev, { chat: true });
                if (!hasUnread && prev.chat) {
                    var n = Object.assign({}, prev);
                    delete n.chat;
                    return n;
                }
                return prev;
            });
        },
        [unreadChatIds]
    );

    // Đồng bộ unreadChatIds từ server (sau loadChats, DB-based)
    // allChatIds = tất cả chat IDs trong response (để phân biệt "server nói đã đọc" vs "server không trả về")
    var handleUpdateUnread = useCallback(function (serverUnreadMap, allChatIds) {
        setUnreadChatIds(function (prev) {
            var next = Object.assign({}, prev);
            var activeChatId = activeChatIdRef.current;
            // Chỉ xoá entry nếu server TRẢ VỀ chat đó với has_unread = false
            // (không xoá nếu server không trả về — giữ local từ socket)
            if (allChatIds && allChatIds.length > 0) {
                for (var i = 0; i < allChatIds.length; i++) {
                    var cid = allChatIds[i];
                    if (!serverUnreadMap[cid] && next[cid]) delete next[cid];
                }
            }
            // Bổ sung từ server (trừ chat đang mở)
            for (var k in serverUnreadMap) {
                if (k === activeChatId) continue;
                if (!next[k]) next[k] = true;
            }
            return next;
        });
    }, []);

    // ─── Auto-open on wide screen ──────────────────────────────────
    useEffect(function () {
        function handleResize() {
            setIsCompactScreen(window.innerWidth <= COMPACT_SCREEN_BREAKPOINT);
            if (window.innerWidth >= WIDE_SCREEN_BREAKPOINT && !isOpen) {
                var s = loadState();
                if (!s || s.isOpen === undefined) setIsOpen(true);
            }
        }
        window.addEventListener('resize', handleResize);
        return function () {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // ─── Mutual exclusion ──────────────────────────────────────────
    var openRight = useCallback(
        function () {
            setIsOpen(true);
            if (sidebarContext && sidebarContext.setRightSidebarOpen) sidebarContext.setRightSidebarOpen(true);
        },
        [sidebarContext]
    );

    var closeRight = useCallback(
        function () {
            setIsOpen(false);
            if (sidebarContext && sidebarContext.setRightSidebarOpen) sidebarContext.setRightSidebarOpen(false);
        },
        [sidebarContext]
    );

    // Lắng nghe left sidebar mở → đóng right
    useEffect(
        function () {
            if (sidebarContext && sidebarContext.leftSidebarOpen && isOpen) {
                setIsOpen(false);
                if (sidebarContext.setRightSidebarOpen) sidebarContext.setRightSidebarOpen(false);
            }
        },
        [sidebarContext ? sidebarContext.leftSidebarOpen : null]
    );

    // ─── Toggle ────────────────────────────────────────────────────
    var handleToggle = useCallback(
        function () {
            if (isOpen) closeRight();
            else openRight();
        },
        [isOpen, openRight, closeRight]
    );

    // ─── Panel click ───────────────────────────────────────────────
    var handlePanelClick = useCallback(
        function (panelKey) {
            // 'assistant' panel: route thẳng tới /ai/AIWorkspace, không mở drawer
            if (panelKey === 'assistant') {
                if (navigate) navigate('/ai/AIWorkspace');
                else if (typeof window !== 'undefined') window.location.hash = '#/ai/AIWorkspace';
                if (isOpen) closeRight();
                return;
            }
            if (activePanel === panelKey && isOpen) {
                closeRight();
            } else {
                setActivePanel(panelKey);
                if (!isOpen) openRight();
                // Trigger reload nội dung panel khi switch tab
                setPanelRefreshKey(function (k) {
                    return k + 1;
                });
            }
        },
        [activePanel, isOpen, openRight, closeRight, navigate]
    );

    // ─── Resize ────────────────────────────────────────────────────
    var resizeRef = useRef({ startX: 0, startWidth: 0 });

    var handleResizeStart = useCallback(
        function (e) {
            e.preventDefault();
            setIsResizing(true);
            resizeRef.current.startX = e.clientX;
            resizeRef.current.startWidth = width;

            function onMove(ev) {
                var diff = resizeRef.current.startX - ev.clientX;
                var newW = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeRef.current.startWidth + diff));
                setWidth(newW);
            }
            function onUp() {
                setIsResizing(false);
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            }
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        [width]
    );

    // ─── Navigate handler for InsightPanel ─────────────────────────
    var handleInsightNavigate = useCallback(
        function (path) {
            if (path) navigate(path);
        },
        [navigate]
    );

    // ─── Navigate handler for NotificationPanel ────────────────────
    var handleNotifyNavigate = useCallback(
        function (objectType, objectValue, notifyType) {
            if (!objectType || !objectValue) return;
            var moduleMap = {
                AROrder: '/ar/AROrder/',
                HRLeave: '/hrm/HRLeave/',
                HROvertimeRequest: '/timesheet/HROvertimeRequest/',
                APOrder: '/ap/APOrder/',
                APRequest: '/ap/APRequest/',
                EXPOrder: '/exp/EXPOrder/',
                ARCredit: '/ar/ARCredit/',
                APCredit: '/ap/APCredit/',
                EXPCredit: '/exp/EXPCredit/',
                ARIssue: '/ar/ARIssue/',
                ARReturn: '/ar/ARReturn/',
                ARInvoice: '/ar/ARInvoice/',
                APInvoice: '/ap/APInvoice/',
                ARAppointment: '/ar/ARAppointment/',
                APReceive: '/ap/APReceive/',
                APReturn: '/ap/APReturn/',
                CARequest: '/ca/CARequest/',
                CAReceive: '/ca/CAReceive/',
                CAIssue: '/ca/CAIssue/',
                CATransfer: '/ca/CATransfer/',
                CABankStatement: '/ca/CABankStatement/',
                ICReceipt: '/inv/ICReceipt/',
                ICIssue: '/inv/ICIssue/',
                ICTransfer: '/inv/ICTransfer/',
                ICAdjust: '/inv/ICAdjust/',
                GLJournal: '/gl/GLJournal/',
                // Kết quả việc ghi sổ nền: object_value là nhóm lọc (Pending | Posted | Error) của danh sách Phiếu kế toán.
                GLJournalAutoPost: '/gl/GLJournals?group=',
                GLJournalRecheck: '/gl/GLJournals?group=',
                GLJournalNightPost: '/gl/GLJournals?group=',
                GLJournalSavePost: '/gl/GLJournals?group=',
                GLFXRevaluation: '/gl/GLFXRevaluation/',
                HRSalary: '/hrm/HRSalary/',
                OFTask: '/of/OFTask/',
                OFApproval: '/of/OFApproval/',
                OFMeeting: '/of/OFMeeting/',
                OFDocument: '/of/OFDocument/',
                FAAssetRegister: '/fa/FAAssetRegister/',
                ARServiceOrder: '/ar/ARServiceOrder/',
                ARWarrantyClaim: '/ar/ARWarrantyClaim/',
                WFApprovalPolicy: '/setting/WFApprovalPolicy/'
            };
            var basePath = moduleMap[objectType];
            if (basePath) {
                navigate(basePath + objectValue);
                // Nếu notification loại Comment → mở left sidebar tab bình luận + focus input
                if (notifyType === 'Comment' && sidebarContext && sidebarContext.openLeftPanel) {
                    setTimeout(function () {
                        sidebarContext.openLeftPanel('comment', { focus: true });
                    }, 300);
                }
            }
        },
        [navigate, sidebarContext]
    );

    // ─── Render panel content ──────────────────────────────────────
    // Switch panel handler (e.g. from comment attachment click → attachment panel)
    var handleSwitchPanel = useCallback(
        function (panelKey, selectId) {
            setActivePanel(panelKey);
            if (!isOpen) setIsOpen(true);
        },
        [isOpen]
    );

    var renderPanel = function () {
        // ─── Global panels (GLOBAL_PANELS) ──────────────────────
        if (activePanel === 'notification') return <NotificationPanel onNavigate={handleNotifyNavigate} refreshKey={notifyRefreshKey} />;
        if (activePanel === 'chat')
            return (
                <ChatPanel
                    mode="sidebar"
                    unreadChatIds={unreadChatIds}
                    onMarkRead={handleMarkChatRead}
                    onUpdateUnread={handleUpdateUnread}
                    activeChatIdRef={activeChatIdRef}
                    pendingOpenChatId={pendingOpenChatId}
                    onClearPendingChat={function () {
                        setPendingOpenChatId(null);
                    }}
                />
            );
        if (activePanel === 'notes') return <NotesPanel />;
        if (activePanel === 'reminder') return <ReminderPanel />;
        if (activePanel === 'calendar') return <CalendarPanel />;
        if (activePanel === 'contacts') return <ContactsPanel />;
        if (activePanel === 'system') return <InsightPanel mockMode={MOCK_MODE} onNavigate={handleInsightNavigate} />;

        // ─── AI Assistant panel (global mode — không có voucher context) ─
        if (activePanel === 'assistant') return <AIPanel mockMode={MOCK_MODE} />;

        if (activePanel === 'documents') return <DocumentsPanel onNavigate={handleInsightNavigate} />;
        if (activePanel === 'file_explorer') return <MEDExplorerPanel session={session} navigate={navigate} />;
        if (activePanel === 'task') return <GlobalTaskPanel onNavigate={handleNotifyNavigate} />;
        if (activePanel === 'approval') return <GlobalApprovalPanel onNavigate={handleNotifyNavigate} />;
        if (activePanel === 'aijobs') return <AIJobsPanel />;

        return null;
    };

    var activePanelDef =
        panels.find(function (p) {
            return p.key === activePanel;
        }) || panels[0];

    // ─── Chặn wheel event không lan ra DevExtreme ScrollView ────
    var handleWheel = useCallback(function (e) {
        e.stopPropagation();
    }, []);

    // ─── Auto-close khi click ra ngoài ────────────────────────────
    // Khi sidebar đang mở, click chuột ngoài (sidebar + bất kỳ overlay DevExtreme
    // nào — popup/dropdown/tooltip render qua portal lên body) → đóng sidebar.
    var sidebarRootRef = useRef(null);
    useEffect(function () {
        if (!isOpen || effectivePinned) return undefined;
        var handler = function (e) {
            var target = e.target;
            if (!target || !target.nodeType) return;
            // Trong sidebar?
            if (sidebarRootRef.current && sidebarRootRef.current.contains(target)) return;
            // Trong DevExtreme overlay (AntStandardPopup, AntDropDownGrid, tooltip...)?
            if (target.closest && target.closest('.dx-overlay-wrapper')) return;
            // Trong floating panel preview/viewer khác (OFDocumentPreview render qua AntStandardPopup
            // đã có .dx-overlay-wrapper; AntFileViewer cũng portal lên body với class riêng).
            if (target.closest && target.closest('.ant-file-viewer, .ant-standard-popup')) return;
            closeRight();
        };
        // Dùng mousedown capture phase để bắt trước React click handler
        document.addEventListener('mousedown', handler, true);
        return function () {
            document.removeEventListener('mousedown', handler, true);
        };
    }, [isOpen, effectivePinned, closeRight]);

    // ─── Render ────────────────────────────────────────────────────
    return (
        <div
            ref={sidebarRootRef}
            className={'ant-right-sidebar'
                + (isOpen ? ' open' : ' collapsed')
                + (isResizing ? ' resizing' : '')
                + (effectivePinned && isOpen ? ' pinned' : '')}
            style={{ width: (effectivePinned && isOpen) ? (COLLAPSED_WIDTH + width) : COLLAPSED_WIDTH }}
            onWheel={handleWheel}
        >
            {isOpen && (
                <div className="sidebar-content" style={{ width: width }}>
                    <div className="sidebar-resize-handle" onMouseDown={handleResizeStart} />
                    <div className="sidebar-panel-header">
                        {(activePanel !== 'documents' || canManageOFDocument) && (
                            <button
                                className="panel-expand-btn"
                                onClick={function () {
                                    if (activePanel === 'assistant') {
                                        if (navigate) navigate('/ai/AIWorkspace');
                                        else if (typeof window !== 'undefined') window.location.hash = '#/ai/AIWorkspace';
                                        closeRight();
                                        return;
                                    }
                                    if (activePanel === 'chat') {
                                        var chatId = activeChatIdRef && activeChatIdRef.current;
                                        var chatUrl = '/user/ChatWorkspace' + (chatId ? ('?chat_id=' + encodeURIComponent(chatId)) : '');
                                        if (navigate) navigate(chatUrl);
                                        else if (typeof window !== 'undefined') window.location.hash = '#' + chatUrl;
                                        closeRight();
                                        return;
                                    }
                                    if (activePanel === 'file_explorer') {
                                        if (navigate) navigate('/media/MEDExplorer');
                                        else if (typeof window !== 'undefined') window.location.hash = '#/media/MEDExplorer';
                                        closeRight();
                                        return;
                                    }
                                    if (activePanel === 'documents') {
                                        if (navigate) navigate('/of/OFDocuments');
                                        else if (typeof window !== 'undefined') window.location.hash = '#/of/OFDocuments';
                                        closeRight();
                                        return;
                                    }
                                    // Approval center has both lists (Đề xuất / Chứng từ) as full pages.
                                    if (activePanel === 'approval') {
                                        if (navigate) navigate('/wf/WFApprovalPendings');
                                        else if (typeof window !== 'undefined') window.location.hash = '#/wf/WFApprovalPendings';
                                        closeRight();
                                        return;
                                    }
                                    setExpandedPanel(activePanel);
                                }}
                                title={AntLocalization.translateText({ vi: 'Mở rộng', en: 'Expand', fr: 'Agrandir', es: 'Expandir', id: 'Perluas', ja: '展開', zh: '展开', ko: '펼치기', hi: 'विस्तार करें', ar: 'توسيع' })}
                            >
                                <SvgIcon d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" size={12} />
                            </button>
                        )}
                        <span className="panel-title">{activePanelDef ? AntLocalization.translateText(activePanelDef.label) : ''}</span>
                        <button
                            className={'panel-pin-btn' + (pinned ? ' panel-pin-btn--on' : '')}
                            onClick={function () { setPinned(function (p) { return !p; }); }}
                            title={AntLocalization.translateText(pinned
                                ? { vi: 'Bỏ ghim panel', en: 'Unpin panel', fr: 'Détacher', es: 'Desfijar', id: 'Lepas sematan', ja: 'ピン解除', zh: '取消固定', ko: '고정 해제', hi: 'अनपिन', ar: 'إلغاء التثبيت' }
                                : { vi: 'Ghim panel cố định', en: 'Pin panel', fr: 'Épingler', es: 'Fijar', id: 'Sematkan', ja: 'ピン留め', zh: '固定面板', ko: '고정', hi: 'पिन', ar: 'تثبيت' })}
                        >
                            {pinned
                                ? <SvgIcon d="M16 9V4l1 0c.55 0 1-.45 1-1V2H6v1c0 .55.45 1 1 1l1 0v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z" size={12} />
                                : <SvgIcon d="M14 4v5c0 1.12.37 2.16 1 3H9c.65-.86 1-1.9 1-3V4h4m3-2H7v2h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3V4h1V2z" size={12} />
                            }
                        </button>
                        <button className="panel-close-btn" onClick={handleToggle}>
                            <SvgIcon d={chevronRightPath} size={14} />
                        </button>
                    </div>
                    <div className="sidebar-panel-body" key={activePanel + '-' + panelRefreshKey}>
                        {renderPanel()}
                    </div>
                </div>
            )}

            <div className="sidebar-icon-strip">
                {visiblePanels.map(function (panel) {
                    var isActive = activePanel === panel.key && isOpen;
                    var hasBadge = !!tabBadges[panel.key];
                    return (
                        <TooltipBtn
                            key={panel.key}
                            className={'sidebar-icon-btn' + (isActive ? ' active' : '')}
                            onClick={function () {
                                handlePanelClick(panel.key);
                            }}
                            tooltip={AntLocalization.translateText(panel.label)}
                            side="left"
                        >
                            <div className="sidebar-icon-wrap">
                                <SvgIcon d={panel.iconPath} size={18} color={isActive ? 'var(--base-accent, #4f7cff)' : '#edf1f7'} />
                                {hasBadge && <span className="sb-badge-dot" />}
                            </div>
                        </TooltipBtn>
                    );
                })}
                {hiddenPanels.length > 0 && (
                    <div style={{ position: 'relative' }} ref={moreMenuRef}>
                        <TooltipBtn
                            className={'sidebar-icon-btn' + (showMoreMenu ? ' active' : '')}
                            onClick={function () {
                                setShowMoreMenu(!showMoreMenu);
                            }}
                            tooltip={AntLocalization.translateText({ vi: 'Thêm', en: 'More', fr: 'Plus', es: 'Más', id: 'Lainnya', ja: 'その他', zh: '更多', ko: '더 보기', hi: 'अधिक', ar: 'المزيد' })}
                            side="left"
                        >
                            <SvgIcon d="M5 12h.01M12 12h.01M19 12h.01" size={18} color={showMoreMenu ? 'var(--base-accent, #4f7cff)' : '#edf1f7'} />
                        </TooltipBtn>
                        {showMoreMenu &&
                            ReactDOM.createPortal(
                                <div
                                    className="sidebar-more-menu"
                                    ref={moreMenuRef}
                                    style={{ top: (moreMenuRef.current ? moreMenuRef.current.getBoundingClientRect().top : 100) + 'px' }}
                                >
                                    {hiddenPanels.map(function (panel) {
                                        var hasBadge = !!tabBadges[panel.key];
                                        return (
                                            <div
                                                key={panel.key}
                                                className="sidebar-more-item"
                                                onClick={function () {
                                                    handlePanelClick(panel.key);
                                                    setShowMoreMenu(false);
                                                }}
                                            >
                                                <SvgIcon d={panel.iconPath} size={16} color="#edf1f7" />
                                                <span className="sidebar-more-label">{AntLocalization.translateText(panel.label)}</span>
                                                {hasBadge && <span className="sb-badge-dot" style={{ position: 'static', marginLeft: 'auto' }} />}
                                            </div>
                                        );
                                    })}
                                    <div className="sidebar-more-divider" />
                                    <div className="sidebar-more-item sidebar-more-pin-header">
                                        <span
                                            className="sidebar-more-label"
                                            style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}
                                        >
                                            {AntLocalization.translateText({ vi: 'Tuỳ chỉnh', en: 'Customize', fr: 'Personnaliser', es: 'Personalizar', id: 'Sesuaikan', ja: 'カスタマイズ', zh: '自定义', ko: '사용자 지정', hi: 'अनुकूलित करें', ar: 'تخصيص' })}
                                        </span>
                                    </div>
                                    {panels.map(function (panel) {
                                        var isPinned = visiblePanels.some(function (p) {
                                            return p.key === panel.key;
                                        });
                                        return (
                                            <div
                                                key={'pin-' + panel.key}
                                                className={'sidebar-more-item sidebar-more-pin' + (isPinned ? ' pinned' : '')}
                                                onClick={function () {
                                                    handleTogglePin(panel.key);
                                                }}
                                            >
                                                <SvgIcon d={panel.iconPath} size={14} color={isPinned ? 'var(--base-accent, #4f7cff)' : '#edf1f7'} />
                                                <span className="sidebar-more-label">{AntLocalization.translateText(panel.label)}</span>
                                                <span className="sidebar-more-pin-icon">
                                                    {isPinned ? <SvgIcon d="M5 13l4 4L19 7" size={12} color="var(--base-accent, #4f7cff)" /> : null}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>,
                                document.body
                            )}
                    </div>
                )}
                <div className="sidebar-icon-separator" />
                <TooltipBtn
                    className="sidebar-icon-btn toggle-btn"
                    onClick={handleToggle}
                    tooltip={
                        isOpen
                            ? AntLocalization.translateText({ vi: 'Thu gọn', en: 'Collapse', fr: 'Réduire', es: 'Contraer', id: 'Ciutkan', ja: '折りたたむ', zh: '收起', ko: '접기', hi: 'संक्षिप्त करें', ar: 'طي' })
                            : AntLocalization.translateText({ vi: 'Mở rộng', en: 'Expand', fr: 'Agrandir', es: 'Expandir', id: 'Perluas', ja: '展開', zh: '展开', ko: '펼치기', hi: 'विस्तार करें', ar: 'توسيع' })
                    }
                    side="left"
                >
                    <SvgIcon d={isOpen ? chevronRightPath : chevronLeftPath} size={16} />
                </TooltipBtn>
            </div>
            {/* ── FloatingWorkPanel (mở rộng bất kỳ tab nào) ── */}
            {(function () {
                if (!expandedPanel) return null;
                var epDef = panels.find(function (p) {
                    return p.key === expandedPanel;
                });
                var epTitle = epDef ? AntLocalization.translateText(epDef.label) : '';
                var epIcon = epDef ? epDef.iconPath : null;

                // Panels có layout master-detail riêng khi floating
                // Còn lại: render sidebar panel content full width
                var hasCustomFloating = expandedPanel === 'chat' || expandedPanel === 'assistant';

                if (hasCustomFloating) {
                    if (expandedPanel === 'chat') {
                        return (
                            <ChatPanel
                                mode="floating"
                                visible={true}
                                unreadChatIds={unreadChatIds}
                                onMarkRead={handleMarkChatRead}
                                onClose={function () {
                                    setExpandedPanel(null);
                                }}
                            />
                        );
                    }
                    if (expandedPanel === 'assistant') {
                        return (
                            <AIFloating
                                visible={true}
                                onClose={function () {
                                    setExpandedPanel(null);
                                }}
                            />
                        );
                    }
                }

                // Default: full-width detail (sidebar panel content)
                return (
                    <FloatingWorkPanel
                        visible={true}
                        onClose={function () {
                            setExpandedPanel(null);
                        }}
                        title={epTitle}
                        icon={epIcon}
                        renderDetail={function () {
                            return <div style={{ height: '100%', overflow: 'auto' }}>{renderPanel()}</div>;
                        }}
                    />
                );
            })()}

            <ChatToastContainer toasts={chatToasts} onDismiss={handleToastDismiss} onChatClick={handleToastChatClick} />
            <NotifyToastContainer toasts={notifyToasts} onDismiss={handleNotifyToastDismiss} onClick={handleNotifyToastClick} />
        </div>
    );
}
